
//gemini code

import React, { useEffect, useState, useRef } from "react";

import QRCode from "react-qr-code";
import "./styles.css";
import AIHostTTSPlayer from "./ai/AIHostTTSPlayer";
import HostAgent from "./ai/HostAgent";
import CharacterPromptManager from "./ai/CharacterPromptManager";
// Quizmaster.js — at top with other firestore imports
import RoundScores from "./Roundscores";
import TeamRoundChoiceGate from "./TeamRoundChoiceGate";
import {
  scheduleTeamRoundAfterEndRound,
  getTeamRoundChoiceSettingsFromLocalStorage,
  teamRoundChoiceSettingsForGameDoc,
  findNextTeamChoiceStartIndex,
  findRoundStartQuestionIndex,
} from "./roundTagChoice";
import { isSimulatorTestTeam } from "./TestDataGenerator";
import {
  openAnswersGate,
  getQuestionDurationSeconds,
  timerActiveOnQuestionAdvance,
  isJigsawOn,
} from "./quizHelpers";

// Quizscreen.js — add with your other firestore imports
import { db } from "./firebase";
import { useHostVenues } from "./venues";
import { doc, updateDoc, getDoc, serverTimestamp, Timestamp, onSnapshot } from "firebase/firestore";
// add these to your existing firestore imports
import { collection, getDocs, deleteDoc } from "firebase/firestore";


// --- One-time kill-switch: stop all videos on manual navigation ---
if (typeof window !== "undefined" && !window.__videoKillInstalled) {
  window.__videoKillInstalled = true;
  window.addEventListener("quiz:advance", () => {
    try {
      // Pause any <video> left mid-play and drop the global busy flag
      document.querySelectorAll("video").forEach(v => {
        try { v.pause(); } catch {}
      });
    } catch {}
    try { window.__videoPlaying = false; } catch {}
  });
}

// --- 10s cue library: each entry plays ONCE per quiz, in order ---
// Add more videos/lines later; the first item is your requested video.


// Fallback line once the library is exhausted (used for the rest of the quiz)
const TEN_SECOND_DEFAULT_TTS = "Ten seconds remaining.";

// ———————————————————————————————————————————————
// OptionsPanel — Start Time stored per game (opt_start_time__<gameCode>)
// Shows separate Hour + Minute dropdowns. Disabled when autoplay = "No".

// === Options Panel (initial slide) ===
// Quizscreen.js -> OptionsPanel component (Full Code)

// Quizscreen.js -> OptionsPanel component (Full Code with specified options removed)

/**
 * Speed Test mode (localStorage opt_speed_test = "On", set by the automated E2E tests):
 * shortens the host's between-slide pauses so test runs finish quickly. Never on for real quizzes.
 */
function isSpeedTestMode() {
  try {
    return localStorage.getItem("opt_speed_test") === "On";
  } catch {
    return false;
  }
}

function OptionsPanel({ onClose, gameCode, joinCode }) {
  const pad2 = (n) => String(n).padStart(2, "0");

  // per-game localStorage key for start time
  const startKey = React.useMemo(
    () => `opt_start_time__${String(gameCode || "default")}`,
    [gameCode]
  );

  // per-game localStorage key for music bed volume
  const bedVolumeKey = React.useMemo(
    () => `opt_bed_volume__${String(gameCode || "default")}`,
    [gameCode]
  );

  // Read persisted start time (if any) without creating a default.
  const readOrInitStart = React.useCallback(() => {
    const stored = localStorage.getItem(startKey);
    if (stored && /^\d{2}:\d{2}$/.test(stored)) {
      const [h, m] = stored.split(":");
      return { hh: h, mm: m };
    }
    // No valid stored time for this game yet.
    return null;
  }, [startKey]);

  // panel state + persistence
  const [autoplay, setAutoplay] = React.useState(
    localStorage.getItem("opt_autoplay") || "Yes" // Default to "Yes" if not set
  );
  const [committedStart, setCommittedStart] = React.useState(() => readOrInitStart());
  const [hour, setHour] = React.useState(() => {
    const initial = readOrInitStart();
    return initial?.hh ?? "";
  });
  const [minute, setMinute] = React.useState(() => {
    const initial = readOrInitStart();
    return initial?.mm ?? "";
  });

  // Venue State - Default to empty string (-- Select a Venue --) if not set
  const [venue, setVenue] = React.useState(
    localStorage.getItem("opt_venue") || ""
  );

  const sanitizeTestUserCount = React.useCallback((value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return 0;
    return Math.min(40, Math.max(0, Math.floor(n)));
  }, []);

  const [testUserCount, setTestUserCount] = React.useState(() => {
    const stored = localStorage.getItem("opt_test_user_count");
    return sanitizeTestUserCount(stored ?? 0);
  });

  // Test Mode State
  const [testMode, setTestMode] = React.useState(
    localStorage.getItem("opt_test_mode") || "Off"
  );

  // TTS State
  const [ttsEnabled, setTtsEnabled] = React.useState(
    localStorage.getItem("opt_tts") || "On"
  );

  // TTS Volume State (0-100 for display, stored as 0-1.0 in localStorage)
  const [ttsVolume, setTtsVolume] = React.useState(() => {
    const stored = localStorage.getItem("opt_tts_volume");
    if (stored !== null) {
      // Convert from 0-1.0 to 0-100 for display
      return Math.round(parseFloat(stored) * 100);
    }
    return 100; // Default to 100%
  });

  // Music Bed State
  const [bedEnabled, setBedEnabled] = React.useState(
    localStorage.getItem("opt_bed") || "On"
  );

  // Music Bed Volume State (0-100 for display, stored as 0-1.0 in localStorage)
  const [bedVolume, setBedVolume] = React.useState(() => {
    const key = `opt_bed_volume__${String(gameCode || "default")}`;
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      // Convert from 0-1.0 to 0-100 for display
      return Math.round(parseFloat(stored) * 100);
    }
    return 25; // Default to 25%
  });

  // convenience localStorage wrappers
  const ls = {
    get: (k, def) => localStorage.getItem(k) || def,
    set: (k, v) => localStorage.setItem(k, v),
  };

  // useEffect for autoplay
  React.useEffect(() => {
    localStorage.setItem("opt_autoplay", autoplay);
  }, [autoplay]);

  React.useEffect(() => {
    const next = readOrInitStart();
    setCommittedStart(next);
    if (next) {
      setHour(next.hh);
      setMinute(next.mm);
    } else {
      setHour("");
      setMinute("");
    }
  }, [readOrInitStart]);

  // Venue useEffect
  React.useEffect(() => {
    if (venue) {
      localStorage.setItem("opt_venue", venue);
    } else {
      localStorage.removeItem("opt_venue");
    }
  }, [venue]);

  React.useEffect(() => {
    const sanitized = sanitizeTestUserCount(testUserCount);
    if (sanitized !== testUserCount) {
      setTestUserCount(sanitized);
      return;
    }
    localStorage.setItem("opt_test_user_count", String(sanitized));
    localStorage.setItem("opt_add_test_users", sanitized > 0 ? "On" : "Off");
  }, [sanitizeTestUserCount, testUserCount]);

  // Test Mode useEffect
  React.useEffect(() => {
    localStorage.setItem("opt_test_mode", testMode);
  }, [testMode]);

  const [teamRoundChoice, setTeamRoundChoice] = React.useState(
    () => localStorage.getItem("opt_team_round_choice") || "Off"
  );
  const [teamRoundBehavior, setTeamRoundBehavior] = React.useState(
    () => localStorage.getItem("opt_team_round_behavior") || "fairShare"
  );
  const [teamRoundTimeoutSec, setTeamRoundTimeoutSec] = React.useState(
    () => localStorage.getItem("opt_team_round_timeout_sec") || "15"
  );
  const [teamRoundRevealSec, setTeamRoundRevealSec] = React.useState(
    () => localStorage.getItem("opt_team_round_reveal_pause_sec") || "7"
  );
  const [teamMinQuestionsPerTag, setTeamMinQuestionsPerTag] = React.useState(
    () => localStorage.getItem("opt_team_min_questions_per_tag") || "5"
  );

  React.useEffect(() => {
    localStorage.setItem("opt_team_round_choice", teamRoundChoice);
  }, [teamRoundChoice]);
  React.useEffect(() => {
    localStorage.setItem("opt_team_round_behavior", teamRoundBehavior);
  }, [teamRoundBehavior]);
  React.useEffect(() => {
    localStorage.setItem("opt_team_round_timeout_sec", teamRoundTimeoutSec);
  }, [teamRoundTimeoutSec]);
  React.useEffect(() => {
    localStorage.setItem("opt_team_round_reveal_pause_sec", teamRoundRevealSec);
  }, [teamRoundRevealSec]);
  React.useEffect(() => {
    localStorage.setItem("opt_team_min_questions_per_tag", teamMinQuestionsPerTag);
  }, [teamMinQuestionsPerTag]);

  // TTS Enabled useEffect
  React.useEffect(() => {
    localStorage.setItem("opt_tts", ttsEnabled);
  }, [ttsEnabled]);

  // TTS Volume useEffect
  React.useEffect(() => {
    // Store as 0-1.0 in localStorage
    const volumeValue = ttsVolume / 100;
    localStorage.setItem("opt_tts_volume", String(volumeValue));
  }, [ttsVolume]);

  // Music Bed Enabled useEffect
  React.useEffect(() => {
    localStorage.setItem("opt_bed", bedEnabled);
  }, [bedEnabled]);

  // Music Bed Volume useEffect
  React.useEffect(() => {
    // Store as 0-1.0 in localStorage
    const volumeValue = bedVolume / 100;
    localStorage.setItem(bedVolumeKey, String(volumeValue));
  }, [bedVolume, bedVolumeKey]);

  // “glassy” inputs style
  const selectStyle = {
    padding: 10,
    borderRadius: 12,
    background: "rgba(18, 24, 42, 0.55)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.12)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
  };

  const hours = React.useMemo(
    () => Array.from({ length: 24 }, (_, i) => pad2(i)),
    []
  );
  const minutes = React.useMemo(
    () => Array.from({ length: 60 }, (_, i) => pad2(i)),
    []
  );
  const hasPendingStartChange = React.useMemo(() => {
    if (committedStart == null) {
      // No start time set yet – require both hour and minute before enabling Set.
      return !!(hour && minute);
    }
    return hour !== committedStart.hh || minute !== committedStart.mm;
  }, [hour, minute, committedStart]);

  const handleSetStart = () => {
    if (!hour || !minute) return;
    const hh = pad2(parseInt(hour || "0", 10));
    const mm = pad2(parseInt(minute || "0", 10));
    const next = { hh, mm };
    localStorage.setItem(startKey, `${hh}:${mm}`);
    setCommittedStart(next);
    setHour(next.hh);
    setMinute(next.mm);
  };

  // Venue Options
  // The host's own venues (Account → Your venues) plus Test Venue for rehearsals.
  const { venues: hostVenues } = useHostVenues();
  const venueOptions = [...hostVenues, ...(venue && venue !== "Test Venue" && !hostVenues.includes(venue) ? [venue] : []), "Test Venue"];

  // Check if we should hide QR code and join URL (test mode enabled in production)
  const shouldHideQRAndJoin = testMode === "On" && window.location.hostname !== 'localhost';

  return (
    <>
      {/* QR Code at top left - outside overlay */}
      {!shouldHideQRAndJoin && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "20px",
            zIndex: 200010,
            background: "#fff",
            borderRadius: 12,
            padding: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <QRCode
            value={`${window.location.origin}/play`}
            style={{
              width: '150px',
              height: '150px',
              background: "#fff",
            }}
          />
          {joinCode && (
            <div
              style={{
                marginTop: "8px",
                fontFamily: "monospace, Menlo, 'Courier New', Courier",
                fontWeight: 700,
                fontSize: "1.2rem",
                letterSpacing: "0.1em",
                color: "#294b88",
                textAlign: "center",
              }}
            >
              Join code: {joinCode}
            </div>
          )}
        </div>
      )}
      {/* Play URL - positioned at top center (localhost only) */}
      {gameCode && window.location.hostname === 'localhost' && (
        <div
          style={{
            position: "fixed",
            top: "5px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200010,
            background: "#f7f7ff",
            color: "#294b88",
            borderRadius: 7,
            fontFamily: "monospace, Menlo, 'Courier New', Courier",
            fontWeight: 700,
            fontSize: "1.1rem",
            letterSpacing: "0.04em",
            padding: '0.5em 0.8em',
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            whiteSpace: "nowrap",
          }}
        >
          {`${window.location.origin}/play/${gameCode}`}
        </div>
      )}

      {/* Options Panel Overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 200000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.45)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
        }}
      >
      <div
        style={{
          width: "min(900px, 92vw)",
          maxHeight: "90vh",
          overflow: "auto",
          borderRadius: 18,
          background: "rgba(11, 15, 26, 0.58)",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow:
            "0 18px 60px rgba(0,0,0,.45), inset 0 1px 1px rgba(255,255,255,.08)",
          backdropFilter: "blur(10px) saturate(120%)",
          WebkitBackdropFilter: "blur(10px) saturate(120%)",
          padding: "28px 28px 22px",
          color: "#fff",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", flexDirection: "column", marginBottom: 18, gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: 0.3 }}>
              Quiz Options
            </div>
            <div style={{ opacity: 0.8, fontSize: 14 }}>
              These apply to this session only.
            </div>
          </div>
          {gameCode && (
            <div style={{ fontSize: 15, fontWeight: 700, opacity: 0.9 }}>
              Game Number:{" "}
              <span style={{ fontFamily: "monospace", letterSpacing: "0.06em" }}>
                {gameCode}
              </span>
            </div>
          )}
        </div>

        {/* Grid */}
        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}
        >
          {/* Play Quiz Automatically */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Play Quiz Automatically</span>
            <select
              value={autoplay}
              style={selectStyle}
              onChange={(e) => setAutoplay(e.target.value)}
            >
              <option>Yes</option>
              <option>No</option>
            </select>
          </label>

          {/* Start Time (HH:MM) */}
          <div style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Start Time (auto-play only HH:MM)</span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ width: 88 }}>
                <select
                  value={hour}
                  style={{ ...selectStyle, width: "100%" }}
                  onChange={(e) => setHour(e.target.value)}
                >
                  {hours.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
              <div style={{ width: 88 }}>
                <select
                  value={minute}
                  style={{ ...selectStyle, width: "100%" }}
                  onChange={(e) => setMinute(e.target.value)}
                >
                  {minutes.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                onClick={handleSetStart}
                disabled={!hasPendingStartChange}
                style={{
                  padding: "10px 18px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.25)",
                  background: !hasPendingStartChange
                    ? "rgba(255,255,255,0.18)"
                    : "rgba(64,132,255,0.85)",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: !hasPendingStartChange ? "not-allowed" : "pointer",
                  opacity: !hasPendingStartChange ? 0.6 : 1,
                  transition: "background 0.2s ease, opacity 0.2s ease",
                  minWidth: 80
                }}
              >
                Set
              </button>
            </div>
            <div style={{ fontSize: 12, opacity: 0.75 }}>
              {committedStart
                ? `Current start time: ${committedStart.hh}:${committedStart.mm}`
                : "Current start time: Not set"}
            </div>
          </div>

          {/* Venue Dropdown */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>
              Select Venue <span style={{ color: '#ff6b6b' }}>*</span> {/* Red asterisk for required */}
            </span>
            <select
              value={venue}
              style={selectStyle}
              onChange={(e) => setVenue(e.target.value)}
            >
              <option value="">-- Select a Venue --</option> {/* Default unselected option */}
              {venueOptions.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </label>

          {/* Quiz Host */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Quiz Host</span>
            <select
              defaultValue={ls.get("opt_host", "Ollie")}
              style={selectStyle}
              onChange={(e) => ls.set("opt_host", e.target.value)}
            >
              <option>None</option>
              <option>Ollie</option>
            </select>
          </label>

          {/* Test Users Slider */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Number of test users</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="range"
                min={0}
                max={40}
                step={1}
                value={testUserCount}
                onChange={(e) => setTestUserCount(sanitizeTestUserCount(e.target.value))}
                style={{ flex: 1 }}
              />
              <div
                style={{
                  minWidth: 44,
                  textAlign: "center",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                {testUserCount}
              </div>
            </div>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              Test users auto-join during hosting. Set to 0 to disable.
            </div>
          </label>

          {/* Test Mode */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Test Mode</span>
            <select
              value={testMode}
              style={selectStyle}
              onChange={(e) => setTestMode(e.target.value)}
            >
              <option>Off</option>
              <option>On</option>
            </select>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              Questions won't be recorded against venue.
            </div>
          </label>

          {/* Enable TTS */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Enable TTS</span>
            <select
              value={ttsEnabled}
              style={selectStyle}
              onChange={(e) => setTtsEnabled(e.target.value)}
            >
              <option>On</option>
              <option>Off</option>
            </select>
          </label>

          {/* TTS Volume Slider */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>TTS Volume</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={ttsVolume}
                onChange={(e) => setTtsVolume(Number(e.target.value))}
                disabled={ttsEnabled === "Off"}
                style={{ 
                  flex: 1,
                  opacity: ttsEnabled === "Off" ? 0.5 : 1,
                  cursor: ttsEnabled === "Off" ? "not-allowed" : "pointer"
                }}
              />
              <div
                style={{
                  minWidth: 50,
                  textAlign: "center",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 16,
                  fontWeight: 600,
                  opacity: ttsEnabled === "Off" ? 0.5 : 1,
                }}
              >
                {ttsVolume}%
              </div>
            </div>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              {ttsEnabled === "Off" ? "Enable TTS to adjust volume" : "Adjust the volume of text-to-speech audio"}
            </div>
          </label>

          {/* Music Bed */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Music Bed</span>
            <select
              value={bedEnabled}
              style={selectStyle}
              onChange={(e) => setBedEnabled(e.target.value)}
            >
              <option>Off</option>
              <option>On</option>
            </select>
          </label>

          {/* Music Bed Volume Slider */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Music Bed Volume</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <input
                type="range"
                min={0}
                max={100}
                step={1}
                value={bedVolume}
                onChange={(e) => setBedVolume(Number(e.target.value))}
                disabled={bedEnabled === "Off"}
                style={{ 
                  flex: 1,
                  opacity: bedEnabled === "Off" ? 0.5 : 1,
                  cursor: bedEnabled === "Off" ? "not-allowed" : "pointer"
                }}
              />
              <div
                style={{
                  minWidth: 50,
                  textAlign: "center",
                  fontVariantNumeric: "tabular-nums",
                  fontSize: 16,
                  fontWeight: 600,
                  opacity: bedEnabled === "Off" ? 0.5 : 1,
                }}
              >
                {bedVolume}%
              </div>
            </div>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              {bedEnabled === "Off" ? "Enable Music Bed to adjust volume" : "Adjust the volume of background music"}
            </div>
          </label>

          <div style={{ gridColumn: "1 / -1", fontWeight: 800, marginTop: 6, opacity: 0.95 }}>
            Round selection (team tag choice)
          </div>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Team round tag choice</span>
            <select
              value={teamRoundChoice}
              style={selectStyle}
              onChange={(e) => setTeamRoundChoice(e.target.value)}
            >
              <option value="Off">Off</option>
              <option value="On">On</option>
            </select>
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              Losing team picks a hashtag for the next round (see #TeamChoice on STARTROUND).
            </div>
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Chooser tie-break</span>
            <select
              value={teamRoundBehavior}
              style={selectStyle}
              onChange={(e) => setTeamRoundBehavior(e.target.value)}
              disabled={teamRoundChoice === "Off"}
            >
              <option value="fairShare">Fair share (avoid repeat chooser)</option>
              <option value="random">Pure random</option>
            </select>
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Pick timeout (seconds)</span>
            <input
              type="number"
              min={5}
              max={120}
              value={teamRoundTimeoutSec}
              onChange={(e) => setTeamRoundTimeoutSec(e.target.value)}
              disabled={teamRoundChoice === "Off"}
              style={{ ...selectStyle, width: 120 }}
            />
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Reveal pause (seconds)</span>
            <input
              type="number"
              min={1}
              max={30}
              value={teamRoundRevealSec}
              onChange={(e) => setTeamRoundRevealSec(e.target.value)}
              disabled={teamRoundChoice === "Off"}
              style={{ ...selectStyle, width: 120 }}
            />
          </label>

          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Min questions per tag (library)</span>
            <input
              type="number"
              min={1}
              max={50}
              value={teamMinQuestionsPerTag}
              onChange={(e) => setTeamMinQuestionsPerTag(e.target.value)}
              disabled={teamRoundChoice === "Off"}
              style={{ ...selectStyle, width: 120 }}
            />
          </label>

          {/* Remove teams after X non-answers */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontWeight: 700 }}>Remove teams after X non-answers</span>
            <input
              type="number"
              min={0}
              step={1}
              defaultValue={Number(ls.get("opt_remove_after_x", "0"))}
              onChange={(e) =>
                ls.set(
                  "opt_remove_after_x",
                  String(Math.max(0, parseInt(e.target.value || "0", 10)))
                )
              }
              style={{ ...selectStyle, width: 120 }}
            />
            <div style={{ opacity: 0.8, fontSize: 12 }}>
              0 disables automatic removal
            </div>
          </label>

          {/* --- OPTIONS REMOVED --- */}
          {/* Show Team Join QR */}
          {/* Show 10-second cue */}
          {/* Countdown Style */}
          {/* Reveal Correct Answer */}
          {/* Leaderboard Animations */}
          {/* Start on INTRODUCTION */}
          {/* --- END REMOVED --- */}

        </div> {/* End Grid */}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
          <button
            style={{
              padding: "10px 16px",
              borderRadius: 12,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "rgba(21,29,51,0.65)",
              color: "#fff",
              fontWeight: 700,
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
            }}
            onClick={() => {
              if (gameCode) {
                getDoc(doc(db, "games", gameCode))
                  .then((snap) => {
                    const g = snap.data() || {};
                    return updateDoc(doc(db, "games", gameCode), {
                      settings: {
                        ...(g.settings || {}),
                        ...teamRoundChoiceSettingsForGameDoc(),
                      },
                    });
                  })
                  .catch(() => {});
              }
              onClose();
            }}
          >
            Close Options
          </button>
        </div>

        {/* Keyboard Shortcuts Display */}
        <div style={{
          marginTop: 18,
          paddingTop: 16,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
          fontSize: 13,
          color: "rgba(255,255,255,0.7)",
          flexWrap: "wrap"
        }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <kbd style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "monospace",
              minWidth: 20,
              textAlign: "center"
            }}>O</kbd>
            <span>Open/close options</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <kbd style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "monospace",
              minWidth: 20,
              textAlign: "center"
            }}>P</kbd>
            <span>Pause/unpause</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <kbd style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "monospace",
              minWidth: 20,
              textAlign: "center"
            }}>R</kbd>
            <span>Return to lobby</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <kbd style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "monospace",
              minWidth: 20,
              textAlign: "center"
            }}>Q</kbd>
            <span>Questions tab</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <kbd style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "monospace",
              minWidth: 20,
              textAlign: "center"
            }}>S</kbd>
            <span>Scores tab</span>
          </span>
        </div>
      </div> {/* End Content Div */}
    </div> {/* End Overlay Div */}
    </>
  );
  
}

// --- DEPRECATED: legacy lobby timer badge ---
// This stub intentionally renders nothing so we don't get a duplicate timer.
// The Start Time countdown + auto-advance are now handled by useSlideTiming().
function AutoStartCountdown() {
  return null;
}





function fmtHMS(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;

  // --- ADDED CHECK ---
  // If hours are 0, only show MM:SS
  if (hh === 0) {
    return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }
  // --- END ADDED CHECK ---

  // Otherwise, show HH:MM:SS (original behavior)
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
}


// === Ollie speech queue ===
// Ollie lines started or queued and not yet finished. Slides don't move on while he's mid-sentence
// or has more to say; moving to another slide drops everything he had queued.
function ollieQueue() {
  if (!window.__ollieQueue) window.__ollieQueue = { pending: 0, timers: new Set() };
  return window.__ollieQueue;
}

/** Says a line after `delayMs`, after whatever is already being said (the TTS player queues). */
function sayOllieLine(text, delayMs = 0) {
  const line = String(text || "").trim();
  if (!line) return;
  const queue = ollieQueue();
  queue.pending += 1;
  const id = setTimeout(() => {
    queue.timers.delete(id);
    queue.pending = Math.max(0, queue.pending - 1);
    try { window.aiSpeak?.(line); } catch {}
  }, Math.max(0, delayMs));
  queue.timers.add(id);
}

function ollieSpeechBusy() {
  if (ollieQueue().pending > 0) return true;
  try {
    if (typeof window.aiPending === "function" && window.aiPending()) return true;
  } catch {}
  return false;
}

/** Stops Ollie and drops everything he had queued (moving to another slide). */
function cancelOllieSpeech() {
  const queue = ollieQueue();
  queue.timers.forEach((id) => clearTimeout(id));
  queue.timers.clear();
  queue.pending = 0;
  try { window.aiStop?.(); } catch {}
}

/** Waits (up to `capMs`) until Ollie has finished everything he has started or queued. */
async function waitForOllieSpeech(capMs = 30000, isCancelled = () => false) {
  const until = Date.now() + capMs;
  while (ollieSpeechBusy() && Date.now() < until && !isCancelled()) {
    await new Promise((r) => setTimeout(r, 200));
  }
}

function olliePointsLabel(p) {
  const n = Number(p) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Teams ranked by points, best first (ties by name), like HostEngine.rank. */
function rankTeamsByPoints(totals) {
  return Object.entries(totals)
    .map(([team, points]) => ({ team, points }))
    .sort((a, b) => (a.points !== b.points ? b.points - a.points : a.team.localeCompare(b.team)));
}

// Ollie's question-type intros, said the first time a type comes up in a round.
const OLLIE_TYPE_INTRO_EVENTS = {
  MULTICHOICE: "mcq_intro",
  "TRUE/FALSE": "truefalse_intro",
  VANISHINGCHOICE: "vanishing_intro",
  NEARESTANSWER: "nearest_intro",
  TYPE: "type_intro",
  WHEEL: "wheel_intro",
  BREADCRUMBS: "breadcrumbs_intro",
  OUTOFORDER: "outoforder_intro",
  BETANSWER: "bet_intro",
};

// Points scale with speed on these types; on the rest speed only breaks ties.
const SPEED_BONUS_TYPES = new Set(["VANISHINGCHOICE", "TYPE", "WHEEL", "BREADCRUMBS"]);

async function speakAndWait(
  line,
  {
    delayMs = 500,
    minHoldMs = 1100,
    maxMs = 15000,
    noDetectGraceMs = 1200,
    approxMinMs = 2000,
    approxPerWordMs = 400,
    postEndHoldMs = 0,
    silenceHoldMs = 300,
    pollStepMs = 120,
    // If false, do NOT watch generic <audio> elements or use heuristic timers;
    // rely primarily on window.aiSpeak's onend callback (plus maxMs fallback).
    useGenericAudioDetection = true,
  } = {}
) {
  return new Promise((resolve) => {
    let done = false;
    const startAtRef = { t: 0 };

    let pollId = 0, approxId = 0, hardCapId = 0, noDetectId = 0, observer = null;

    const cleanup = () => {
      try { if (pollId)     clearInterval(pollId); } catch {}
      try { if (approxId)   clearTimeout(approxId); } catch {}
      try { if (hardCapId)  clearTimeout(hardCapId); } catch {}
      try { if (noDetectId) clearTimeout(noDetectId); } catch {}
      try { if (observer)   observer.disconnect(); } catch {}
      pollId = approxId = hardCapId = noDetectId = 0; observer = null;
    };

    const finish = (reason = "unknown") => {
      if (done) return;
      done = true;
      cleanup();
      window.__mediaBusy = false; // <<< not busy anymore
      const elapsed = performance.now() - (startAtRef.t || performance.now());
      const left = Math.max(0, minHoldMs - elapsed);
      try {
        console.info("[TTS] speakAndWait finished", {
          reason,
          elapsedMs: Math.round(elapsed),
          line: String(line).slice(0, 120),
        });
      } catch {}
      window.setTimeout(resolve, left + Math.max(0, postEndHoldMs));
    };

    const start = () => {
      startAtRef.t = performance.now();
      window.__mediaBusy = true; // <<< mark busy during narration

      // Deduplicate the same line in ~2s (StrictMode-safe).
      // IMPORTANT: do NOT call finish() here; just avoid starting a second TTS.
      try {
        const now = performance.now();
        const last = window.__lastSpoken || { text: "", t: 0 };
        if (String(last.text) === String(line) && (now - last.t) < 2000) {
          // Skip starting another TTS for the same line; let the existing one run
          // and invoke finish() via its onend callback.
          return;
        }
        window.__lastSpoken = { text: String(line), t: now };
      } catch {}

      try { if (typeof window.aiStop === "function") window.aiStop(); } catch {}

      try {
        if (typeof window.aiSpeak === "function") {
          try { window.aiSpeak(line, { onend: () => finish("aiSpeak_onend") }); }
          catch { window.aiSpeak(line); }
        }
      } catch {}

      if (useGenericAudioDetection) {
        let sawSpeaking = false;
        let silenceMs = 0;
        const SILENCE_HOLD = Math.max(0, silenceHoldMs);
        const STEP = Math.max(60, pollStepMs);

        const markSpeaking = () => { sawSpeaking = true; silenceMs = 0; };

        const isAnyAudioPlaying = () => {
          try {
            const audios = Array.from(document.querySelectorAll("audio"));
            return audios.some(a => {
              const src = (a.currentSrc || a.src || "").toLowerCase();
              const isBed = a.loop || a.getAttribute("data-bed") === "1" || src.includes("/sfx/396595_valo_0-120.mp3");
              if (isBed) return false;
              return !a.paused && !a.ended && a.currentTime > 0;
            });
          } catch { return false; }
        };

        const hookAudio = (el) => {
          if (!el || el.__ttsHooked) return;
          el.__ttsHooked = true;
          try {
            el.addEventListener("playing", markSpeaking, { passive: true });
            el.addEventListener("timeupdate", () => {
              if (!el.paused && !el.ended) markSpeaking();
            }, { passive: true });
            el.addEventListener("ended", () => finish("audio_element_ended"), { once: true, passive: true });
          } catch {}
        };

        try { Array.from(document.querySelectorAll("audio")).forEach(hookAudio); } catch {}
        try {
          observer = new MutationObserver((muts) => {
            for (const m of muts) {
              m.addedNodes && m.addedNodes.forEach((n) => {
                if (n && n.nodeType === 1) {
                  if (n.tagName === "AUDIO") hookAudio(n);
                  n.querySelectorAll && n.querySelectorAll("audio").forEach(hookAudio);
                }
              });
            }
          });
          observer.observe(document.documentElement, { childList: true, subtree: true });
        } catch {}

        pollId = window.setInterval(() => {
          let speaking = false;
          try {
            if (window.speechSynthesis) {
              speaking = window.speechSynthesis.speaking || window.speechSynthesis.pending;
            }
          } catch {}
          if (!speaking) speaking = isAnyAudioPlaying();

          if (speaking) {
            sawSpeaking = true;
            silenceMs = 0;
          } else if (sawSpeaking) {
            silenceMs += STEP;
            if (silenceMs >= SILENCE_HOLD) finish("silence_hold");
          }
        }, STEP);

        noDetectId = window.setTimeout(() => {
          if (!sawSpeaking) {
            const words = Math.max(1, String(line).trim().split(/\s+/).length);
            const estimate = Math.min(maxMs, Math.max(approxMinMs, words * approxPerWordMs));
            approxId = window.setTimeout(() => finish("approximate_duration"), estimate);
          }
        }, Math.max(300, noDetectGraceMs));

        hardCapId = window.setTimeout(() => finish("maxMs_hard_cap"), maxMs + 500);
      }
    };

    window.setTimeout(start, Math.max(0, delayMs));
  });
}




// Helper to detect MultiChoice types robustly (no hooks needed)
function isMultiChoiceType(t) {
  const T = String(t || "").toUpperCase();
  return (
    T === "MULTICHOICE" ||
    T === "MULTIPLECHOICE" ||
    T === "MULTI" ||
    T === "MCQ" ||
    T === "MULTICHOICE_SINGLE" ||
    T === "MULTIPLE_CHOICE"
  );
}

// ShowRounds grid for the live player
// ShowRounds grid for the live host screen (only rounds yet to come)
// ShowRounds grid for the live host screen (only rounds yet to come)
// ShowRounds grid for the live host screen (only rounds yet to come)
// Adds optional "fall" prop to reuse the existing fall-in animation & stagger.
// SHOWROUNDS — reuse the same stagger class as MULTICHOICE on initial mount.
// SHOWROUNDS — standard font + standard transparent hero background for the title
// SHOWROUNDS — no internal title by default (prevents duplicate when host header already shows it)
// ShowRounds grid for the live host screen (rounds + breaks, numbered rounds, glimmer on breaks)
// ShowRounds grid for the live host screen (rounds + breaks, numbered rounds, glimmer on breaks)
// (Left-aligned pill text)
// ShowRounds grid for the live host screen (rounds + breaks, numbered rounds, glimmer on breaks)
// (Left-aligned pill text)
// === ShowRounds Grid - CORRECTED VERSION ===
function ShowRoundsGrid({ allSlides, currentIndex = 0, showTitle = false, onJumpToIndex }) {
  const slides = Array.isArray(allSlides) ? allSlides : [];
  const startIdx = Number.isFinite(currentIndex) ? currentIndex : 0;

  // Build list of ALL rounds and breaks, stopping at the first END QUIZ
  const allItems = [];
  for (let i = 0; i < slides.length; i++) {
    const s = slides[i] || {};
    const t = String(s?.type || s?.CAT || "").toUpperCase();
    if (t === "END QUIZ") break;

    if (t === "STARTROUND") {
      const source =
        s?.question ?? s?.display ?? s?.title ?? s?.text ?? s?.Q ?? "";
      const raw = typeof cleanQuestionText === "function"
        ? cleanQuestionText(String(source))
        : String(source);
      let title = (raw || "").trim() || "This Round needs a Title";
      // Remove square brackets from display (but keep original for reference if needed)
      if (isTextInBrackets(title)) {
        title = extractTextFromBrackets(title);
      }
      allItems.push({ kind: "round", title, slideIndex: i });
    }

    if (t === "BREAK TIME") {
      // *** Corrected Break Time Calculation ***
      const secs = parseInt(s?.time ?? s?.TIME ?? s?.duration ?? "", 10); // <-- Read as seconds
      const mins = Number.isFinite(secs) ? Math.max(0, Math.round(secs / 60)) : 0; // <-- Divide by 60
      const label = mins > 0 ? `BREAK - ${mins} minutes` : "BREAK"; // <-- Use the calculated 'mins'
      allItems.push({ kind: "break", title: label, slideIndex: i });
    }
  }

  // Find the next upcoming round (first round that comes after currentIndex)
  let nextRoundIndex = -1;
  for (let i = 0; i < allItems.length; i++) {
    if (allItems[i].kind === "round" && allItems[i].slideIndex > startIdx) {
      nextRoundIndex = i;
      break;
    }
  }

  const titled = allItems.map((it, idx) => {
    if (it.kind === "round") {
      const isNextUpcomingRound = idx === nextRoundIndex;
      return {
        ...it,
        show: it.title,
        highlight: isNextUpcomingRound,
      };
    }
    return { ...it, show: it.title, highlight: false };
  });

  if (!titled.length) {
    // Keep the "No upcoming rounds" display as is
    return (
      <div style={{ display: "flex", justifyContent: "center", paddingTop: "6vh" }}>
        <div
          style={{
            background: "rgba(11, 15, 26, 0.58)",
            border: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px) saturate(120%)",
            WebkitBackdropFilter: "blur(10px) saturate(120%)",
            color: "#fff",
            borderRadius: 16,
            padding: "0.6em 1.2em",
            fontWeight: 800,
            fontSize: "clamp(18px, 3.0vw, 36px)",
          }}
        >
          No upcoming rounds.
        </div>
      </div>
    );
  }

  // Layout - Fixed button size, dynamic spacing to fill screen
  // Find the tallest column to calculate sizing
  const targetColumns = titled.length > 14 ? 3 : titled.length > 5 ? 2 : 1;
  const itemsPerColumn = Math.ceil(titled.length / targetColumns);
  const columns = Array.from({ length: targetColumns }, (_, idx) =>
    titled.slice(idx * itemsPerColumn, (idx + 1) * itemsPerColumn)
  ).filter(col => col.length > 0);

  const maxItemsInColumn = Math.max(...columns.map(col => col.length), 1);

  const isMultiColumn = columns.length > 1;
  const minButtonPx = isMultiColumn ? 52 : 68;
  const maxButtonPx = isMultiColumn ? 96 : 120;
  const reservedSpacePx = showTitle ? 140 : 0;
  const availableHeight = reservedSpacePx > 0
    ? `calc(100% - ${reservedSpacePx}px)`
    : "100%";
  const availableWidth = `min(92vw, 1500px)`;

  const widthExpr = columns.length > 1
    ? `calc((${availableWidth} - (${columns.length - 1} * 2.4vw)) / ${columns.length})`
    : `min(${availableWidth}, 1020px)`;

  const BUTTON_WIDTH = (!isMultiColumn)
    ? `clamp(360px, ${widthExpr}, 860px)`
    : columns.length === 3
      ? `clamp(280px, ${widthExpr}, 460px)`
      : `clamp(340px, ${widthExpr}, 560px)`;

  const PILL_PADDING_H = "1.9em";
  const GAP_HORIZONTAL = columns.length > 1 ? "clamp(20px, 2.8vw, 48px)" : "0px";

  const columnGapVertical = columns.length > 1
    ? "clamp(8px, 1vh, 18px)"
    : "clamp(12px, 1.5vh, 26px)";

  const verticalGapVh = columns.length > 1 ? 1.1 : 1.9;
  const buttonHeightClamp = `clamp(${minButtonPx}px, calc((60vh - (${maxItemsInColumn - 1}) * ${verticalGapVh}vh) / ${maxItemsInColumn}), ${maxButtonPx}px)`;

  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column",
      alignItems: "center",
      width: "92vw",
      maxWidth: "1500px",
      padding: 0,
      margin: "0 auto",
      overflow: "hidden",
      height: "100%",
      boxSizing: "border-box",
      justifyContent: showTitle ? "flex-start" : "center"
    }}>
      {showTitle && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "1vh",
            flexShrink: 0
          }}
        >
          {/* Keep the title bar style as is */}
          <div
            style={{
              background: "rgba(11, 15, 26, 0.58)",
              border: "1px solid rgba(255,255,255,0.12)",
              backdropFilter: "blur(10px) saturate(120%)",
              WebkitBackdropFilter: "blur(10px) saturate(120%)",
              color: "#fff",
              borderRadius: 16,
              padding: "0.4em 0.9em",
              fontWeight: 900,
              fontSize: "clamp(20px, 3.4vw, 48px)",
              lineHeight: 1.1,
            }}
          >
            Upcoming Rounds
          </div>
        </div>
      )}

      {/* Grid container for columns - takes remaining space and fills vertically */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: columns.length > 1
            ? `repeat(${columns.length}, ${BUTTON_WIDTH})`
            : `minmax(${BUTTON_WIDTH}, 1fr)`,
          columnGap: GAP_HORIZONTAL,
          justifyContent: "center",
          alignItems: "stretch",
          width: "100%",
          maxWidth: "100%",
          flex: 1,
          minHeight: 0,
          height: availableHeight,
          maxHeight: availableHeight,
          overflow: "visible",
        }}
      >
        {columns.map((col, ci) => {
          const columnOffset = columns.slice(0, ci).reduce((sum, c) => sum + c.length, 0);
          return (
            <ol
            key={ci}
            className="answer-list answers-stagger-in"
            style={{ 
              listStyle: "none", 
              margin: 0, 
              padding: 0, 
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              gap: columnGapVertical,
              pointerEvents: typeof onJumpToIndex === "function" ? "auto" : "none",
              width: columns.length > 1 ? BUTTON_WIDTH : "100%",
              height: "100%",
              overflow: "visible",
            }}
          >
            {col.map((item, ri) => {
              // Cycle through a-f colors using modulo
              const letter = String.fromCharCode(97 + ((columnOffset + ri) % 6));
              const extraClass = item.highlight
                ? "correct-glimmer"
                : item.kind === "break"
                  ? ""
                  : "";
              const pillText = item.show;
              const isClickable = typeof onJumpToIndex === "function" && Number.isFinite(item.slideIndex);

              // Smart font size: start large and scale down only when needed
              // Buttons are taller (100–120px) to support big-room readability
              const textLength = String(pillText || "").length;
              
              // Base font size clamps keep short titles bold and readable on large displays
              const useTwoCols = columns.length > 1;

              const shortClamp = useTwoCols ? "clamp(24px, 3vw, 40px)" : "clamp(28px, 3.4vw, 48px)";
              const mediumClamp = useTwoCols ? "clamp(21px, 2.6vw, 34px)" : "clamp(25px, 3vw, 42px)";
              const longClamp = useTwoCols ? "clamp(18px, 2.1vw, 28px)" : "clamp(24px, 2.8vw, 36px)";
              const veryLongClamp = useTwoCols ? "clamp(17px, 1.95vw, 26px)" : "clamp(22px, 2.6vw, 32px)";
              const overflowClamp = useTwoCols
                ? `clamp(17px, calc((100% - 3.6em) / ${textLength} * 0.9), 26px)`
                : `clamp(20px, calc((100% - 3.6em) / ${textLength} * 0.95), 32px)`;
              
              // Calculate if text needs to scale down based on length
              // Narrower pill width + slightly reduced padding (~4.2em) mean long titles must shrink earlier
              
              let fontSize;
              if (textLength <= 18) {
                fontSize = shortClamp;
              } else if (textLength <= 24) {
                fontSize = mediumClamp;
              } else if (textLength <= 32) {
                fontSize = longClamp;
              } else if (textLength <= 40) {
                fontSize = veryLongClamp;
              } else {
                fontSize = overflowClamp;
              }

              return (
                <li key={ri} style={{ 
                  display: "flex", 
                  justifyContent: "flex-start",
                  width: "100%",
                  flex: "0 0 auto",
                  height: buttonHeightClamp,
                  minHeight: `${minButtonPx}px`,
                }}>
                  <div
                    className={`answer-option option-${letter} ${extraClass}`}
style={{
                        "--i": ri,
                        width: "100%",
                        height: "100%", // Fill the container exactly
                        paddingLeft: "clamp(1.1em, 2vw, 1.6em)", // Bring text closer to left edge
                        paddingRight: "clamp(1.2em, 2.4vw, 2em)",
                        paddingTop: "0.4em",
                        paddingBottom: "0.4em",
                        // *** Font and Text Shadow like MCQ ***
                        fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                        fontWeight: 900,
                        color: "#fff",
                        textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                        // *** Text alignment and overflow ***
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textAlign: "left",
                        justifyContent: "flex-start",
                        textOverflow: "ellipsis",
                        // *** Smart Font Size: Large for short text, scale down only when needed ***
                        fontSize: fontSize,
                        // *** Standard Pill Appearance ***
                        borderRadius: 999,
                        display: "flex",
                        justifyContent: "flex-start", // Left-align content
                        alignItems: "center", // Center text vertically
                        lineHeight: 1.1,
                        userSelect: "none",
                        opacity: .9,
                        boxSizing: "border-box",
                        cursor: isClickable ? "pointer" : "default",
                        pointerEvents: isClickable ? "auto" : "none",
                      }}
                    title={pillText}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    aria-disabled={isClickable ? undefined : true}
                    onClick={
                      isClickable
                        ? () => onJumpToIndex(item.slideIndex)
                        : undefined
                    }
                    onKeyDown={
                      isClickable
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onJumpToIndex(item.slideIndex);
                            }
                          }
                        : undefined
                    }
                  >
                    {pillText}
                  </div>
                </li>
              );
            })}
            </ol>
          );
        })}
      </div>
    </div>
  );
}








// Run a callback while TTS is temporarily suppressed (to prevent HostAgent speaking)
function withTTSSuppressed(cb) {
  const origAiSpeak = window.aiSpeak;
  const origWebSpeechSpeak =
    (window.speechSynthesis && window.speechSynthesis.speak) || null;

  try {
    // Hard-mute both paths during the callback
    window.aiSpeak = () => {};
    if (window.speechSynthesis && typeof window.speechSynthesis.speak === "function") {
      window.speechSynthesis.speak = () => {};
    }
    cb && cb();
  } finally {
    // Restore
    window.aiSpeak = origAiSpeak;
    if (origWebSpeechSpeak && window.speechSynthesis) {
      window.speechSynthesis.speak = origWebSpeechSpeak;
    }
  }
}




// Seeded shuffle for WHEEL letter order
// Seeded shuffle for WHEEL letter order
function seededShuffle(arr, seed) {
  let a = arr.slice();
  let m = a.length, t, i;
  let s = seed;
  while (m) {
    i = Math.floor(Math.abs(Math.sin(s++)) * m--);
    t = a[m];
    a[m] = a[i];
    a[i] = t;
  }
  return a;
}




function ensurePreloadLink(url) {
  if (!url) return;
  const key = `preload-${url}`;
  if (document.querySelector(`link[data-preload="${key}"]`)) return;
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = url;
  link.setAttribute("data-preload", key);
  document.head.appendChild(link);
}

// Map many CSV/UX labels to a canonical internal type
// Map many CSV/UX labels to a canonical internal type
function normalizeType(q) {
  const raw = String(q?.type || q?.CAT || "").trim().toUpperCase();

  // Canonical buckets:
  //  - DISPLAY covers: all display-like slides
  //  - MULTICHOICE, TRUE/FALSE, etc stay as-is
 const displayAliases = new Set([
    "DISPLAY", "SLIDE", "DEFAULT", "STARTROUND", "ENDROUND", "SHOWROUNDS",
    "JOIN QUIZ", "INTRODUCTION", "END QUIZ", "BREAK TIME", "DEFAULT CATEGORY" // <-- ADD THIS
  ]);

  if (displayAliases.has(raw)) return "DISPLAY";

  // Preserve other known types verbatim
  const known = [
    "MULTICHOICE", "TRUE/FALSE", "TRUEFALSE", "MULTIANSWER",
    "NEARESTANSWER", "TYPE", "BREADCRUMBS", "BETANSWER", "WHEEL", "PICTURE",
    "AUDIO", "VIDEO", "PUZZLE", "VANISHINGCHOICE", "OUTOFORDER" 
  ];
  if (known.includes(raw)) return raw.replace("TRUEFALSE", "TRUE/FALSE");

  // Fallback: if there are no answers and no time, behave like a display
  const answers = Array.isArray(q?.answers)
    ? q.answers
    : [q?.A1, q?.A2, q?.A3, q?.A4, q?.A5, q?.A6].filter(v => v !== undefined);
  const noAnswers = answers.every(a => String(a ?? "").trim() === "");
  const t = parseInt(q?.time ?? q?.TIME ?? 0, 10) || 0;
  if (noAnswers && t === 0) return "DISPLAY";

  return raw || "DISPLAY";
}


function getClampedAnswersOrder(q) {
  const t = normalizeType(q);
  
const answers = Array.isArray(q?.answers) ? q.answers : [];
  const order = Array.isArray(q?.answersOrder) ? q.answersOrder.slice() : Array.from({ length: answers.length }, (_, i) => i);

  // Handle TRUE/FALSE (accept both the old raw and the new normalized just in case)
  if (t === "TRUE/FALSE" || t === "TRUEFALSE") {
    // Only allow A1 (index 0 = CORRECT) and A2 (index 1)
    const allowed = [0, 1].filter(i => answers[i] != null && String(answers[i]).trim() !== "");
    const filtered = order.filter(i => allowed.includes(i));
    // Fallback to the first two if order is missing
    return (filtered.length ? filtered : allowed).slice(0, 2);
  }

  return order;
}



// Theme colours per question type (transparent tints)
// Theme colours per question type (transparent tints)
function getTypeTheme(q) {
  switch (normalizeType(q)) {
    case "MULTICHOICE": // green
      return { bgFrom:"rgba(20,120,60,.62)", bgTo:"rgba(12,80,40,.58)", border:"rgba(72,221,124,.6)", pillFrom:"#1b5e32", pillTo:"#114822", dotFrom:"#c8ffd9", dotTo:"#49e58a" };
       case "MULTIANSWER": // ORANGE to match editor (#e67e22 / #f39c12)
      return {
        bgFrom: "rgba(230,126,34,0.62)",  // #e67e22
        bgTo:   "rgba(210,110,24,0.58)",  // darker orange
        border: "rgba(243,156,18,0.60)",  // #f39c12
        pillFrom: "#ab5e12",
        pillTo:   "#8b4a0e",
        dotFrom:  "#fff0d6",
        dotTo:    "#ffd08a"
      };
        case "BETANSWER": // amber
      return { bgFrom:"rgba(160,110,0,.62)", bgTo:"rgba(110,70,0,.58)", border:"rgba(255,208,92,.6)", pillFrom:"#6c4a0a", pillTo:"#513805", dotFrom:"#ffe9b0", dotTo:"#ffc64d" };
    case "TYPE": // purple
      return { bgFrom:"rgba(90,35,140,.60)", bgTo:"rgba(60,22,95,.56)", border:"rgba(202,170,255,.6)", pillFrom:"#4f267e", pillTo:"#3b1a61", dotFrom:"#e8d7ff", dotTo:"#c09bff" };
    case "BREADCRUMBS": // brown/tan
      return { bgFrom:"rgba(141,110,99,.62)", bgTo:"rgba(109,76,65,.58)", border:"rgba(161,136,127,.6)", pillFrom:"#5d4037", pillTo:"#4e342e", dotFrom:"#d7ccc8", dotTo:"#a1887f" };
    case "NEARESTANSWER": // deep purple
      return { bgFrom:"rgba(110,35,170,.64)", bgTo:"rgba(72,18,120,.58)", border:"rgba(210,170,255,.68)", pillFrom:"#5b2396", pillTo:"#3e1670", dotFrom:"#f0e3ff", dotTo:"#bf98ff" };
    case "JACKPOT": // gold (#d4af37)
      return { bgFrom:"rgba(212,175,55,.64)", bgTo:"rgba(180,145,35,.58)", border:"rgba(230,200,102,.68)", pillFrom:"#b89523", pillTo:"#9a7a1a", dotFrom:"#fff9e6", dotTo:"#ffe8a8" };
    case "WHEEL": // indigo
      return { bgFrom:"rgba(45,56,150,.60)", bgTo:"rgba(28,34,100,.56)", border:"rgba(162,172,255,.6)", pillFrom:"#303c92", pillTo:"#242d72", dotFrom:"#dadaff", dotTo:"#a8afff" };
        
    case "TRUE/FALSE": // LEMON to match editor (#f4d03f / #f7dc6f)
      return {
        bgFrom: "rgba(244,208,63,0.62)",  // #f4d03f
        bgTo:   "rgba(223,186,45,0.58)",  // slightly darker
        border: "rgba(247,220,111,0.60)", // #f7dc6f
        pillFrom: "#6a5605",
        pillTo:   "#524203",
        dotFrom:  "#fff5b0",
        dotTo:    "#ffe566"
      };
    case "OUTOFORDER": // apricot (match editor palette)
    return {
        bgFrom: "rgba(255, 200, 162, 0.62)",
        bgTo:   "rgba(255, 170, 120, 0.58)",
        border: "rgba(255, 218, 185, 0.60)",
        pillFrom: "#e6a97f",
        pillTo:   "#d68f63",
        dotFrom:  "#fff0e6",
        dotTo:    "#ffd1b5"
      };
   
   
      default: // fallback navy
      return { bgFrom:"rgba(16,22,36,.58)", bgTo:"rgba(10,14,24,.54)", border:"rgba(128,168,255,.5)", pillFrom:"#2a3f6a", pillTo:"#1b2b4d", dotFrom:"#d4e3ff", dotTo:"#7aa2ff" };
  }
}



// Auto-fit text so long lines shrink instead of overflowing
function FitText({ children, max = 120, min = 24, step = 2, style = {} }) {
  const boxRef = React.useRef(null);
  const textRef = React.useRef(null);
  const [size, setSize] = React.useState(max);

  React.useLayoutEffect(() => {
    const box = boxRef.current, txt = textRef.current;
    if (!box || !txt) return;
    let s = max;
    // shrink until it fits within box (both width & height)
    for (; s >= min; s -= step) {
      txt.style.fontSize = s + "px";
      if (txt.scrollWidth <= box.clientWidth && txt.scrollHeight <= box.clientHeight) break;
    }
    setSize(Math.max(min, s));
  }, [children, max, min, step]);

  return (
    <div ref={boxRef} style={{ width: "86vw", height: "74vh", ...style, overflow: "hidden" }}>
      <div ref={textRef} style={{ fontSize: size, lineHeight: 1.12 }}>
        {children}
      </div>
    </div>
  );
}




function hashSeed(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
  }
  return hash >>> 0;
}
function mulberry32(a) {
  return function() {
    var t = a += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  }
}
function seededShuffleJigsaw(arr, seed = 1) {
  let a = arr.slice();
  let random = mulberry32(seed);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}


// === ResponsiveContainVideo (keep aspect, max out screen) ===
function ResponsiveContainVideo({
  src,
  autoPlay = true,
  onPlay,
  onEnded,
  onPause,
  style = {},
  videoStyle = {},
  controls = false,
  loop = false,
  muted = false,
}) {
  const ref = React.useRef(null);

  // try unmuted first; if blocked, start muted and auto-unmute
  const startPlayback = React.useCallback(async (v) => {
    try {
      v.muted = muted;
      await v.play();
      return true;
    } catch {
      try {
        v.muted = true;
        await v.play();
        // brief delay before unmuting, browsers allow this after a user gesture or first frame
        setTimeout(() => { try { v.muted = muted; } catch {} }, 150);
        return true;
      } catch {
        return false;
      }
    }
  }, [muted]);

  React.useEffect(() => {
    const v = ref.current;
    if (!v) return;

    const handlePlay  = () => onPlay && onPlay(v);
    const handleEnd   = () => onEnded && onEnded(v);
    const handlePause = () => onPause && onPause(v);

    v.addEventListener("play", handlePlay);
    v.addEventListener("ended", handleEnd);
    v.addEventListener("pause", handlePause);

    if (autoPlay) {
      // kick playback once metadata is known for best sizing
      const kick = async () => { await startPlayback(v); };
      if (v.readyState >= 1) kick(); else v.addEventListener("loadedmetadata", kick, { once: true });
    }

    return () => {
      v.removeEventListener("play", handlePlay);
      v.removeEventListener("ended", handleEnd);
      v.removeEventListener("pause", handlePause);
    };
  }, [autoPlay, onPlay, onEnded, onPause, startPlayback]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2vh 2vw",
        zIndex: 2000,
        ...style,
      }}
    >
      <video
        ref={ref}
        src={src}
        playsInline
        controls={controls}
        loop={loop}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",   // <— fill as big as possible, never distort/crop
          maxWidth: "100vw",
          maxHeight: "100vh",
          ...videoStyle,
        }}
      />
    </div>
  );
}

// === VideoSlide (handles overlay text auto-hide after 5s) ===
// === VideoSlide (handles overlay text auto-hide after 5s) ===
// === VideoSlide (handles overlay text auto-hide after 5s) ===
function VideoSlide({ src, overlayText }) {
  const [showText, setShowText] = React.useState(true);
  const hideTO = React.useRef(0);

  const handlePlay = () => {
    // hide the overlay text 5s after first play
    if (!hideTO.current) {
      hideTO.current = window.setTimeout(() => setShowText(false), 5000);
    }
    // expose to global “busy” watchers
    window.__videoPlaying = true;
  };

  const handleEnded = () => {
    window.__videoPlaying = false;
  };

  const handlePause = (v) => {
    // consider paused-but-not-ended as “not busy”
    if (v?.ended) return;
    window.__videoPlaying = false;
  };

  // Cleanup: clear timers, pause any video in this slide, drop busy flag
  React.useEffect(() => {
    return () => {
      if (hideTO.current) clearTimeout(hideTO.current);
      try {
        // Pause just in case the element survives a tick during unmount
        document.querySelectorAll("video").forEach(v => {
          try { v.pause(); } catch {}
        });
      } catch {}
      try { window.__videoPlaying = false; } catch {}
    };
  }, []);

  return (
    <>
      <ResponsiveContainVideo
        src={src}
        autoPlay
        controls={false}
        loop={false}
        muted={false}
        onPlay={handlePlay}
        onEnded={handleEnded}
        onPause={handlePause}
      />

      {/* Centered label that auto-hides after 5s */}
      {showText && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2100,
          }}
        >
          <div
            style={{
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              fontWeight: 900,
              fontFamily: "'Arial Black', Impact, Arial, sans-serif",
              fontSize: "min(6vw, 8vh)",
              padding: "0.25em 0.6em",
              borderRadius: 16,
            }}
          >
            {overlayText}
          </div>
        </div>
      )}
    </>
  );
}






// Keeps images fully visible: scale to fit without cropping.
function ResponsiveContainImage({ src, alt = "Visual", style = {}, imgStyle = {} }) {
  const containerStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...style,
  };

  return (
    <div style={containerStyle}>
      <img
        src={src}
        alt={alt}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "100%",
          objectFit: "contain",
          display: "block",
          ...imgStyle,
        }}
      />
    </div>
  );
}


function JigsawImageReveal({ src, timer, totalTime, rows = 6, cols = 8, style }) {
  const [naturalSize, setNaturalSize] = React.useState({ width: 1, height: 1 });
  const [boxSize, setBoxSize] = React.useState({ width: 1, height: 1 });
  const boxRef = React.useRef(null);

  React.useEffect(() => {
    if (!src) return;
    const img = new window.Image();
    img.onload = () => setNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = src;
  }, [src]);

  React.useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const updateSize = () => {
      const rect = el.getBoundingClientRect();
      setBoxSize({ width: rect.width || 1, height: rect.height || 1 });
    };
    updateSize();
    const ro = new ResizeObserver(() => updateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const numTiles = rows * cols;
  const elapsed = Math.max(0, totalTime - timer);
  const revealCount = Math.floor((elapsed / totalTime) * numTiles);

  const scale = Math.min(
    boxSize.width / naturalSize.width,
    boxSize.height / naturalSize.height
  );
  const imageW = Math.max(1, naturalSize.width * scale);
  const imageH = Math.max(1, naturalSize.height * scale);
  const offsetX = (boxSize.width - imageW) / 2;
  const offsetY = (boxSize.height - imageH) / 2;

  const indexes = Array.from({ length: numTiles }, (_, i) => i);
  const seed = hashSeed(src || "");
  const shuffledIndexes = seededShuffleJigsaw(indexes, seed);
  const revealedSet = new Set(shuffledIndexes.slice(0, revealCount));

  return (
    <div
      ref={boxRef}
      style={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        ...style
      }}
    >
      <img
        src={src}
        alt="Jigsaw Visual"
        style={{
          position: "absolute",
          left: offsetX,
          top: offsetY,
          width: imageW,
          height: imageH,
          objectFit: "contain",
          display: "block",
        }}
      />
      {/* Overlay grid of tiles */}
      <div
        style={{
          pointerEvents: "none",
          position: "absolute",
          top: 0, left: 0, width: "100%", height: "100%",
          zIndex: 20,
        }}
      >
        {shuffledIndexes.map((idx) => {
          if (revealedSet.has(idx)) return null;
          const row = Math.floor(idx / cols);
          const col = idx % cols;
          return (
            <div
              key={idx}
              style={{
                position: "absolute",
                top: offsetY + (row * imageH) / rows,
                left: offsetX + (col * imageW) / cols,
                width: imageW / cols,
                height: imageH / rows,
                background: "linear-gradient(120deg, #252b3e 60%, #111 100%)",
                border: "1px solid #222",
                borderRadius: 2,
                boxShadow: "0 1px 9px #1117",
                transition: "opacity 0.33s cubic-bezier(.67,0,.38,1.1)",
                willChange: "opacity",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}




// COMPLETE FUNCTION: simple background bed that starts on question change and stops at time=0
// COMPLETE FUNCTION: simple background bed that starts on question change and stops at time=0
// COMPLETE FUNCTION: background bed that fades in last 10s and cuts at 0
// REPLACEMENT: background bed with NO fade; hard-cuts at timer === 0
function BackgroundBed({ src, current, timer, volume = 0.28 }) {
  const ref = React.useRef(null);

  // Start fresh each time the question index changes (play in lobby too)
  React.useEffect(() => {
    const a = ref.current;
    if (!a) return;

    try {
      a.currentTime = 0;
      a.volume = volume;
      a.dataset.baseVolume = String(volume); // snapshot base for later
      const p = a.play();
      if (p && typeof p.catch === "function") {
        // Mobile autoplay fallback
        p.catch(() => {
          a.muted = true;
          a.play()
            .then(() => setTimeout(() => { a.muted = false; }, 120))
            .catch(() => {});
        });
      }
    } catch {}
  }, [current, volume]);

  // Hold steady volume; ONLY cut at 0 (but not in lobby)
  React.useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const t = Number(timer) || 0;

    // keep volume fixed at the saved base
    try {
      const base = parseFloat(a.dataset.baseVolume ?? String(volume));
      if (!Number.isNaN(base)) a.volume = Math.max(0, Math.min(1, base));
    } catch {}

    // hard cut at 0 (but allow playing in lobby where current === 0)
    if (t <= 0 && current !== 0) {
      try { a.pause(); } catch {}
      try { a.currentTime = 0; } catch {}
      // restore base for next question start
      try {
        const base = parseFloat(a.dataset.baseVolume ?? String(volume));
        if (!Number.isNaN(base)) a.volume = Math.max(0, Math.min(1, base));
      } catch {}
    }
  }, [timer, volume, current]);

  return (
    <audio
      ref={ref}
      src={src}
      preload="auto"
      loop
      playsInline
      data-bed="1"
      style={{ display: "none" }}
    />
  );
}






// Build background CSS. Fades only when isDisplay === false.
function buildBackgroundCSS(url, isDisplay) {
  if (!url) return undefined;
  return isDisplay
    ? `url(${url}) center center / cover no-repeat`
    : `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${url}) center center / cover no-repeat`;
}




function cleanQuestionText(question) {
  if (!question) return "";
  return question
    .replace(/^\{B\}\s*/i, "")                            // Remove {B}
    .replace(/^\{Q(?:,TXT|,NUM)?\}\s*/i, "")               // Remove {Q}, {Q,TXT}, {Q,NUM}
    .replace(/^\{TXT\}\s*/i, "")                           // Remove {TXT}
    .replace(/^\{NUM\}\s*/i, "")                           // Remove {NUM}
    .replace(/^\{E\}\s*/i, "")                            // Remove {E}
    .replace(/\{LB\}/gi, "\n")                            // <--- ADD THIS LINE
    .trim();
}

// Hide dynamic hashtag placeholders before real question loads
function isHashtagPlaceholder(text) {
  if (!text || typeof text !== "string") return false;
  const trimmed = text.trim();
  if (!trimmed.startsWith("#")) return false;
  if (trimmed.includes("\n")) return false;
  return trimmed.length <= 80;
}

// Build the on-screen question text, prefixing TRUE/FALSE types
// Build the on-screen question text (no automatic TRUE/FALSE prefix)
function buildQuestionDisplayText(q) {
  const raw = q?.question ?? q?.Q ?? "";
  if (isHashtagPlaceholder(raw)) return "";
  return cleanQuestionText(raw) + getMultiAnswerSuffix(q);
}

// Helper: Check if text is wrapped in square brackets like "[xxxxxx]"
function isTextInBrackets(text) {
  if (!text || typeof text !== 'string') return false;
  let trimmed = text.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  return /^\[.*\]$/.test(trimmed);
}

// Helper: Extract text from brackets (remove brackets) for TTS
function extractTextFromBrackets(text) {
  if (!text || typeof text !== 'string') return text;
  let trimmed = text.trim();
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  if (/^\[.*\]$/.test(trimmed)) {
    return trimmed.slice(1, -1); // Remove first [ and last ]
  }
  return text;
}

function getMultiAnswerSuffix(q) {
  if ((q.type || "").toUpperCase() !== "MULTIANSWER") return "";
  // Count answers with brackets
  const count = q.answers.filter(a => /^\s*\[.*?\]\s*$/.test(a)).length;
  if (count > 1) {
    return ` (SELECT ${count})`;
  }
  return "";
}

// Human-readable label for the question type
function getTypeDisplayName(q) {
  const type = String(q?.type || q?.CAT || "").toUpperCase();
  switch (type) {
    case "MULTICHOICE":     return "Multiple Choice";
    case "MULTIANSWER":     return "Multi Answer";
    case "BETANSWER":       return "Bet Your Points";
    case "TYPE":            return "Type Your Answer";
    case "BREADCRUMBS":     return "Breadcrumbs";
    case "NEARESTANSWER":   return "Nearest Answer";
    case "JACKPOT":         return "Jackpot";
    case "WHEEL":           return "Wheel of Fortune";
    case "TRUE/FALSE":      return "TRUE or FALSE";
    case "VANISHINGCHOICE": return "Vanishing Choice";
     case "OUTOFORDER":      return "Out of Order";
    case "BUZZER":          return "Buzzer";
    case "PICTURE":         return "Picture";
    case "AUDIO":           return "Audio";
    case "VIDEO":           return "Video";
    case "PUZZLE":          return "Puzzle";
    case "WHOAMI":          return "Who Am I";
    case "BREAK TIME":      return "Break Time";
    case "INTRODUCTION":    return "Introduction";
    case "JOIN QUIZ":       return "Join Quiz";
    case "END QUIZ":        return "End of Quiz";
    default:                return "Question";
  }
}

const WHEEL_TYPE_PALETTE = {
  "MULTICHOICE":     { bg: "#1f8a4c", fg: "#fff" },
  "VANISHINGCHOICE": { bg: "#1f5fbf", fg: "#fff" },
  "MULTIANSWER":     { bg: "#e67e22", fg: "#111" },
  "TRUE/FALSE":      { bg: "#f4d03f", fg: "#111" },
  "NEARESTANSWER":   { bg: "#6a3fb2", fg: "#fff" },
  "JACKPOT":         { bg: "#d4af37", fg: "#111" },
  "WHEEL":           { bg: "#3949ab", fg: "#fff" },
  "TYPE":            { bg: "#00acc1", fg: "#111" },
  "BREADCRUMBS":     { bg: "#8d6e63", fg: "#fff" },
  "BUZZER":          { bg: "#d81b60", fg: "#fff" },
  "BETANSWER":       { bg: "#ffb300", fg: "#111" },
  "OUTOFORDER":      { bg: "#ffc8a2", fg: "#111" },
  "WHOAMI":          { bg: "#00897b", fg: "#fff" },
  "PICTURE":         { bg: "#6d4c41", fg: "#fff" },
  "AUDIO":           { bg: "#00838f", fg: "#fff" },
  "VIDEO":           { bg: "#8e24aa", fg: "#fff" },
  "PUZZLE":          { bg: "#546e7a", fg: "#fff" }
};

// Order must match the quiz selection wheel image: pointer at 12 o'clock; wedges clockwise from top = NEAREST ANSWER, TYPE, WHEEL, BUZZER, BET YOUR POINTS, BREADCRUMBS, MULTI CHOICE, TRUE/FALSE, VANISHING CHOICE, MULTI ANSWER
const WHEEL_TYPE_DEFS = [
  { type: "NEARESTANSWER" },
  { type: "TYPE" },
  { type: "WHEEL" },
  { type: "BUZZER" },
  { type: "BETANSWER" },
  { type: "BREADCRUMBS" },
  { type: "MULTICHOICE" },
  { type: "TRUE/FALSE" },
  { type: "VANISHINGCHOICE" },
  { type: "MULTIANSWER" }
];

const WHEEL_TYPE_ICONS = {
  "MULTICHOICE": "/icons/multiple-choice.png",
  "TRUE/FALSE": "/icons/true-false.png",
  "VANISHINGCHOICE": "/icons/vanishing-choice.png",
  "MULTIANSWER": "/icons/multi-answer.png",
  "NEARESTANSWER": "/icons/nearest-answer.png",
  "TYPE": "/icons/type-answer.png",
  "BREADCRUMBS": "/icons/type-answer.png",
  "WHEEL": "/icons/wheel-of-fortune.png",
  "BUZZER": "/icons/buzzer.png",
  "BETANSWER": "/icons/bet-your-points.png",
  "OUTOFORDER": "/icons/out-of-order.png"
};

const WHEEL_IMAGE_SRC = "/media/quiz-wheel.png";
const WHEEL_POINTER_SRC = "/media/Wheel-pointer.png";

function getWheelTypeColors(type) {
  const key = String(type || "").toUpperCase();
  return WHEEL_TYPE_PALETTE[key] || { bg: "#2b3d55", fg: "#fff" };
}

function adjustHexColor(hex, amt) {
  const cleaned = String(hex || "").replace("#", "");
  if (cleaned.length !== 6) return hex;
  const num = parseInt(cleaned, 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amt));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amt));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amt));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function QuestionTypeWheelTransition({ visible, targetType, spinMs = 2500 }) {
  if (!visible) return null;

  const slices = WHEEL_TYPE_DEFS;
  const sliceAngle = 360 / slices.length;
  const targetKey = String(targetType || "").toUpperCase();
  const targetIndex = Math.max(0, slices.findIndex((s) => s.type === targetKey));
  const spins = 4;
  // Rotate so the target wedge center aligns with the pointer at top (12 o'clock). WHEEL_TYPE_DEFS order = clockwise from pointer (NEAREST first). -4 wedges: wheel image is drawn with first wedge offset from our math.
  const wedgeOffset = -4 * sliceAngle;
  const spinDeg = spins * 360 - (targetIndex * sliceAngle - sliceAngle / 2) + wedgeOffset;

  const wheelStops = slices
    .map((s, i) => {
      const start = i * sliceAngle;
      const end = (i + 1) * sliceAngle;
      const base = getWheelTypeColors(s.type).bg;
      return `${base} ${start}deg ${end}deg`;
    })
    .join(", ");

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 400000,
        pointerEvents: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <style>{`
        @keyframes wheelSpin {
          0% { transform: rotateZ(0deg); }
          100% { transform: rotateZ(var(--spin-deg)); }
        }
        @keyframes wheelFade {
          0% { opacity: 1; }
          90% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes wheelBackdrop {
          0% { opacity: 0; }
          18% { opacity: 1; }
          86% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes wheelEnter {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>

      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(6,10,18,1)",
          animation: `wheelBackdrop ${spinMs}ms ease-out forwards`
        }}
      />

      <div
        style={{
          position: "relative",
          width: "96vmin",
          maxWidth: "96vmin",
          aspectRatio: "1 / 1",
          animation: "wheelEnter 900ms ease-out"
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-5%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "20%",
            height: "24%",
            zIndex: 4,
            filter: "drop-shadow(0 8px 10px rgba(0,0,0,0.55))"
          }}
        >
          <img
            src={WHEEL_POINTER_SRC}
            alt="Wheel pointer"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: 0.92
            }}
          />
        </div>

        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            animation: `wheelFade ${spinMs}ms ease-out forwards`
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              boxShadow: "0 40px 80px rgba(0,0,0,0.6)",
              animation: `wheelSpin ${spinMs}ms cubic-bezier(0.1, 0.6, 0.05, 1) forwards`,
              "--spin-deg": `${spinDeg}deg`
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: "2%",
                borderRadius: "50%",
                background: "#0b0f1a"
              }}
            />
            <img
              src={WHEEL_IMAGE_SRC}
              alt="Quiz wheel"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                borderRadius: "50%",
                clipPath: "circle(50% at 50% 50%)",
                objectFit: "cover",
                boxShadow: "inset 0 0 24px rgba(255,255,255,0.12)"
              }}
            />
          </div>
        </div>

        <div
          style={{
            position: "absolute",
            inset: "-8%",
            borderRadius: "50%",
            border: "10px solid rgba(10,14,24,0.75)",
            boxShadow: "inset 0 0 40px rgba(255,255,255,0.08), 0 18px 40px rgba(0,0,0,0.6)"
          }}
        />
      </div>
    </div>
  );
}

// Build rules to show on the card.
// If the CSV carries RULES / RULES_TEXT, those win; otherwise we generate sensible defaults per type.
function getRulesForQuestion(q) {
  // Prefer CSV-provided rules if present
  const rawCustom = q?.RULES_TEXT || q?.RULES || q?.rules_text || q?.rules || "";
  if (rawCustom && String(rawCustom).trim()) {
    return String(rawCustom)
      .replace(/\{LB\}/gi, "\n")
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(Boolean)
      .slice(0, 6);
  }

  const t = normalizeType(q);
  const ans = Array.isArray(q?.answers) ? q.answers : [];
  const multiPickCount =
    ans.filter(a => /^\s*\[.*?\]\s*$/.test(String(a ?? ""))).length;





  switch (t) {
    case "MULTICHOICE":
      return [
        "Select one answer",
        "No penalty points or bonus points"
      ];

    case "VANISHINGCHOICE":
      return [
        "Wrong answers vanish over time",
        "DO NOT GUESS! Penalty points apply"
      ];

    case "OUTOFORDER":
      return [
        "Drag the items into the correct sequence",
        "Points are awarded for each item in the correct position"
        ];

    case "MULTIANSWER":
      return [
        multiPickCount > 1 ? `Select ${multiPickCount} answers` : "Select multiple answers",
        "Select multiple answers for each question",
        "Each correct answer is rewarded"
      ];
    case "BETANSWER":
      return [
        "Place a bet before answering",
        "Correct answer wins your bet, wrong loses it",
        "Bet smart for maximum return"
      ];
    case "TYPE":
      return [
        "You're allowed the odd typo!",
        "More points for faster answers"
      ];
    case "BREADCRUMBS":
      return [
        "Clues reveal over time",
        "Unlimited guesses",
        "Faster answer = more points"
      ];
    case "NEARESTANSWER":
      return [
        "Guess a number closest to the answer",
        "Nearest 5 teams score points",
        "Fastest answers break ties"
      ];
    case "JACKPOT":
      return [
        "Guess a number closest to the answer",
        "No points - scored manually by host",
        "Players can send representatives to the front"
      ];
    case "WHEEL":
      return [
        "Letters reveal over time",
        "Solve the phrase at any moment",
        "Earlier solves earn more points"
      ];
    case "TRUE/FALSE":
      return [
        "Choose True or False",

      ];
    default:
      return [
        "Answer as fast as you can",
        "Highest points early, then tapering"
      ];
  }
}


function buildWheelBoard(answer, rows = 3, cols = 12) {
  // Clean and normalize input
  const cleaned = answer
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return Array.from({ length: rows }, () => Array(cols).fill(""));

  const words = cleaned.split(" ");
  const board = Array.from({ length: rows }, () => Array(cols).fill(""));
  let row = 0;
  let col = 0;

  for (let w = 0; w < words.length; w++) {
    let word = words[w];

    // Split long words as a last resort (if longer than the row)
    while (word.length > cols) {
      if (col !== 0) { row++; col = 0; }
      for (let i = 0; i < cols && word.length; i++) {
        board[row][col++] = word[0];
        word = word.slice(1);
      }
      row++; col = 0;
      if (row >= rows) return board;
    }

    // If the word fits on this line (with a space if not at start), add it
    if ((col === 0 && word.length <= cols) || (col > 0 && (col + 1 + word.length) <= cols)) {
      if (col > 0) board[row][col++] = " ";
      for (let i = 0; i < word.length; i++) {
        board[row][col++] = word[i];
      }
    } else {
      // Doesn't fit, go to new row
      row++;
      col = 0;
      if (row >= rows) return board;
      // Place the word at the new row
      for (let i = 0; i < word.length && col < cols; i++) {
        board[row][col++] = word[i];
      }
    }
  }
  return board;
}


// Bottom-right rules card: 20% width, 40% height; raises up then reverses

// REPLACE the whole RulesSlideInCard component with this version
// Key changes:
//  • Base card no longer forces nowrap globally (so the list can wrap).
//  • First three “pill” rows use nowrap + ellipsis and a slightly smaller font.
//  • Question type and points are mixed case (no all caps).
//  • Removed the dot/bullet before the Question type row.
//  • Rules list text wraps normally (no ellipsis), and the panel still never shows a scrollbar.
// REPLACE the whole RulesSlideInCard component
function RulesSlideInCard({ q, lines = [], visible = false, questionNumber = 0 }) {
  const theme = getTypeTheme(q);

  // Present mixed-case labels for type
  const toTitleCase = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // Points display
  const rawPoints =
    q?.POINTS ?? q?.points ?? q?.MaxPoints ?? q?.max_points ?? q?.max ?? "";
  const basePoints = Number.parseInt(String(rawPoints).trim(), 10);
  const hasBasePoints = Number.isFinite(basePoints) && basePoints > 0;

  let pointsText = "";
  if (hasBasePoints) {
    const t = normalizeType(q);

    // >>> CHANGE: for MULTIANSWER, show "points per correct" (no total math) <<<
    if (t === "MULTIANSWER") {
      pointsText = `${basePoints} points per correct`;
    }
    // Keep MULTICHOICE behavior the same (total = pts × #correct)
    else if (t === "MULTICHOICE") {
      const correctCount = countCorrectOptions(q);
      if (correctCount > 0) {
        const total = basePoints * correctCount;
        pointsText = `${basePoints} pts × ${correctCount} correct = ${total} pts max`;
      } else {
        pointsText = `${basePoints} pts available`;
      }
    }
    else if (t === "BETANSWER") {
      pointsText = `Up to ${basePoints} pts (your bet)`;
    } else {
      pointsText = `${basePoints} pts max`;
    }
  }

  const typeLabelMixedCase = toTitleCase(getTypeDisplayName(q));

  // Animation + base container
  const showTransition = "transform 560ms cubic-bezier(.2,.7,.2,1), opacity 360ms ease";
  const hideTransition =
    "transform 2200ms cubic-bezier(.16,.74,.12,1), opacity 1600ms ease 250ms";

  const base = {
    position: "fixed",
    right: "2vw",
    bottom: "3vh",
    width: "20vw",
    minWidth: 260,
    height: "40vh",
    minHeight: 220,
    padding: "14px 16px",
    borderRadius: 16,
    color: "#f3f6ff",
    background: `linear-gradient(180deg, ${theme.bgFrom}, ${theme.bgTo})`,
    border: `1px solid ${theme.border}`,
    boxShadow: "0 10px 44px rgba(0,0,0,.55), inset 0 1px 1px rgba(255,255,255,.08)",
    transform: visible ? "translateY(0) scale(1)" : "translateY(120%) scale(.96)",
    opacity: visible ? 1 : 0,
    transition: visible ? showTransition : hideTransition,
    zIndex: 10300,
    pointerEvents: "none",
    display: "grid",
    gridTemplateRows: "auto auto auto 1fr",
    rowGap: 8,
    // Allow wrapping by default so the rules list can wrap
    whiteSpace: "normal",
    // Never show scrollbars on the panel itself
    overflow: "hidden",
    willChange: "transform, opacity"
  };

  // Shared pill styling (mixed case)
  const pillBase = {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 900,
    letterSpacing: ".06em",
    textTransform: "none",
    color: "#eaf3ff",
    background: `linear-gradient(180deg, ${theme.pillFrom}, ${theme.pillTo})`,
    border: `2px solid ${theme.border}`,
    borderRadius: 999,
    padding: "9px 14px",
    textShadow: "0 2px 10px #001a",
    boxShadow:
      "inset 0 2px 4px rgba(255,255,255,.10), 0 6px 16px rgba(0,0,0,.35), 0 0 22px rgba(0,0,0,.15)"
  };

  // First three rows: slightly smaller, single line only
  const pillSmallNowrap = {
    ...pillBase,
    fontSize: "clamp(12px, 1.1vw, 17px)", // keep these a touch smaller
    whiteSpace: "nowrap",                  // single line
    overflow: "hidden",
    textOverflow: "ellipsis"
  };

  // Rules list container: can wrap (no scrollbars on panel)
  const ul = {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gap: "8px",
    fontWeight: 800,
    fontSize: "clamp(14px, 1.1vw, 18px)",
    lineHeight: 1.25,
    overflow: "hidden"
  };

  const li = {
    display: "grid",
    gridTemplateColumns: "18px 1fr",
    alignItems: "start",
    gap: "10px",
    background: "linear-gradient(180deg, rgba(255,255,255,.06), rgba(255,255,255,0))",
    border: `1px solid ${theme.border}`,
    borderRadius: 12,
    padding: "8px 10px",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)"
  };

  const dot = {
    width: 12,
    height: 12,
    marginTop: 4,
    borderRadius: 999,
    background: `radial-gradient(circle at 30% 30%, ${theme.dotFrom}, ${theme.dotTo})`
  };

  return (
    <div style={base}>
      {/* 1) Question number — smaller, single line */}
      {questionNumber > 0 && (
        <div style={pillSmallNowrap}>{`Question ${questionNumber}`}</div>
      )}

      {/* 2) Question type — smaller, mixed case, single line (no bullet) */}
      <div style={pillSmallNowrap}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {typeLabelMixedCase}
        </span>
      </div>

      {/* 3) Points — smaller, mixed case, single line */}
      {pointsText && (
        <div style={pillSmallNowrap}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {pointsText}
          </span>
        </div>
      )}

      {/* 4) Rules list — wraps normally */}
      <ul style={ul}>
        {lines.slice(0, 6).map((t, i) => (
          <li key={i} style={li}>
            <span style={dot} />
            <span
              style={{
                textShadow: "0 1px 6px #0008",
                whiteSpace: "normal",
                overflow: "visible",
                textOverflow: "clip",
                display: "block"
              }}
            >
              {t}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}


// Count how many correct options this question has.
// Handles a few common encodings: answers as objects with isCorrect,
// or strings marked with [brackets], ✓, or '(correct)'.
// Count how many correct options this question has.
// Supports multiple encodings:
// 1) answers as objects: { text: "...", isCorrect: true } or { correct: true }
// 2) answers as strings marked as correct via:
//    - [Correct answer]    (wrapped in square brackets)
//    - "Answer ✓"          (ending with a tick)
//    - "Answer (correct)"  (suffix tag)
// Falls back to 0 if nothing is marked.
function countCorrectOptions(q) {
  const ans = Array.isArray(q?.answers) ? q.answers : [];

  // Object-encoded answers with flags
  if (ans.length && typeof ans[0] === "object" && ans[0] !== null) {
    return ans.filter(a => {
      const flag = a && (a.isCorrect === true || a.correct === true || a.is_right === true);
      if (flag) return true;
      // Also check text markers if present
      const s = String(a.text ?? a.label ?? a.value ?? "").trim();
      return (
        /^\[.*?\]$/.test(s) ||
        /[✓]\s*$/.test(s) ||
        /\(correct\)\s*$/i.test(s)
      );
    }).length;
  }

  // String-encoded answers
  let count = 0;
  for (const a of ans) {
    const s = String(a ?? "").trim();
    if (!s) continue;
    if (/^\[.*?\]$/.test(s)) { count++; continue; }         // [Correct]
    if (/[✓]\s*$/.test(s)) { count++; continue; }            // ends with ✓
    if (/\(correct\)\s*$/i.test(s)) { count++; continue; }   // ends with (correct)
  }
  return count;
}


// --- shared phase util (matches your BETANSWER split) ---
// --- shared phase util (matches your BETANSWER split) ---
function getTimerPhase(q, timer) {
  const total = parseInt(q.time ?? q.TIME ?? 0, 10) || 0;

  let phaseTotal = total;
  let phaseRemaining = Math.max(0, timer);
  let phaseLabel = null;

  if (String(q.type || "").toUpperCase() === "BETANSWER") {
    if (timer > 30) {
      phaseTotal = Math.max(1, total - 30);
      phaseRemaining = Math.max(0, timer - 30);
      phaseLabel = "Bet";
    } else {
      phaseTotal = 30;
      phaseRemaining = Math.max(0, timer);
      phaseLabel = "Answer";
    }
  }

  const progress = Math.max(0, Math.min(1, phaseRemaining / (phaseTotal || 1)));
  return { progress, phaseLabel, phaseTotal, phaseRemaining };
}


// --- REVERSED-COLOUR 3D THERMOMETER BAR ---
// Smooth, in-sync thermometer with edge-locked flame + clear explosion
// Smooth, in-sync thermometer with edge-locked flame + BIG explosion
// Smooth thermometer with edge-locked flame + big explosion (no wick)
// Smooth thermometer with edge-locked flame, 3D cap, and full burn at the end
// 3D thermometer: solid track, edge-locked flame, huge explosion, full burn
// 3D thermometer with bigger edge-locked flame, dense sparks, huge explosion, full inferno burn
// 3D thermometer with bigger edge-locked flame, dense sparks,
// massive explosion, and full-bar inferno overlay
// Thermometer: big edge-locked flame, massive clean fireball, auto-reset inferno
// Thermometer: single edge-locked flame (no double), massive clean fireball, auto-reset
function ThermoTimerBar({ q, timer, questionNumber = 0 }) {
  const total = Math.max(1, parseInt(q.time ?? q.TIME ?? 0, 10) || 0);

  // smooth in-sync motion anchored to last tick
  const anchorTimerRef = React.useRef(timer);
  const anchorTsRef = React.useRef(performance.now());
  const prevRemainRef = React.useRef(timer);

  const [pct, setPct] = React.useState((timer / total) * 100);   // 0..100
  const [boom, setBoom] = React.useState(false);
  const [boomPct, setBoomPct] = React.useState(0);               // 0..100
  const [inferno, setInferno] = React.useState(false);

  // key by question to reset visuals
  const qKey = React.useMemo(() => {
    const id = q?.id ?? q?.ID ?? q?.uuid ?? q?.UUID ?? q?.key ?? q?.Q ?? q?.question ?? "";
    return `${id}|${q?.time ?? q?.TIME ?? ""}`;
  }, [q]);

const elapsed = Math.max(0, total - Math.max(0, timer));
const showTimer = elapsed >= 5 && timer > 0;


 


  // reset on question change (NOT every tick)
  /* eslint-disable-next-line */
  React.useEffect(() => {
    setBoom(false);
    setInferno(false);
    setBoomPct(0);
    const startPct = (timer / total) * 100;
    setPct(startPct);
    anchorTimerRef.current = timer;
    anchorTsRef.current = performance.now();
    prevRemainRef.current = timer;
  }, [qKey, total]);

  // re-anchor each parent tick
  React.useEffect(() => {
    anchorTimerRef.current = timer;
    anchorTsRef.current = performance.now();
  }, [timer]);

  // rAF loop (continuous)
  React.useEffect(() => {
    let raf;
    const loop = () => {
      const elapsed = (performance.now() - anchorTsRef.current) / 1000;
      const remaining = Math.max(0, anchorTimerRef.current - elapsed);

      if (prevRemainRef.current > 0 && remaining <= 0) {
        setBoomPct((prevRemainRef.current / total) * 100);
        setBoom(true);
        setInferno(true);
        setTimeout(() => setBoom(false), 1200);
        setTimeout(() => setInferno(false), 1200);
      }
      prevRemainRef.current = remaining;

      setPct((remaining / total) * 100);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [total]);



  
  return (
    <>
      {/* explosion visuals (unchanged)… */}
      {boom && (
        <>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "calc(var(--boom) * 1%)",
              transform: "translate(-50%,-50%)",
              width: 480, height: 480, borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(255,255,255,.99), rgba(255,238,0,.97) 34%, rgba(255,175,0,.88) 66%, rgba(255,90,0,.68) 78%, rgba(0,0,0,0) 80%)",
              animation: "boomFlash 700ms ease-out forwards",
              pointerEvents: "none"
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "calc(var(--boom) * 1%)",
              width: 84, height: 84, borderRadius: "50%",
              border: "12px solid rgba(255,210,0,.98)",
              transform: "translate(-50%,-50%)",
              animation: "boomRing 1600ms ease-out forwards",
              pointerEvents: "none"
            }}
          />
          {Array.from({ length: 220 }).map((_, i) => {
            const angle = i * (360 / 220);
            const r = 440 + (i % 9) * 28;
            const tx = Math.cos((angle * Math.PI) / 180) * r + "px";
            const ty = Math.sin((angle * Math.PI) / 180) * -r * 0.72 + "px";
            return (
              <div
                key={`sh-${i}`}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "calc(var(--boom) * 1%)",
                  width: (i % 5 === 0 ? 26 : 18),
                  height: 4,
                  borderRadius: 3,
                  background: "linear-gradient(90deg,#ffd166,#ff8a00)",
                  transformOrigin: "left center",
                  animation: "boomShard 1500ms ease-out forwards",
                  "--r": `${angle}deg`,
                  "--tx": tx,
                  "--ty": ty,
                  pointerEvents: "none"
                }}
              />
            );
          })}
        </>
      )}

      <div
        style={{
          position: "fixed", left: 0, right: 0, bottom: "5vh",
          zIndex: 10250, pointerEvents: "none", padding: "0 2vw",
          "--pct": pct, "--boom": boomPct
        }}
      >
        <style>{`
          @keyframes sparkPulse { 0%{transform:scale(1);filter:brightness(1)}100%{transform:scale(1.08);filter:brightness(1.25)} }
          @keyframes emberFloat { 0%{transform:translate(0,0) scale(1);opacity:1}100%{transform:translate(-18px,-24px) scale(.6);opacity:0} }
          @keyframes sparkDrift { 0%{transform:translate(0,0);opacity:1} 100%{transform:translate(var(--dx),var(--dy));opacity:0} }
          @keyframes boomRing { 0%{transform:translate(-50%,-50%) scale(.25);opacity:.98}100%{transform:translate(-50%,-50%) scale(5.6);opacity:0} }
          @keyframes boomShard {
            0% { transform: translate(-50%,-50%) rotate(var(--r)) scale(1); opacity:1; }
            100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) rotate(var(--r)) scale(.7); opacity:0; }
          }
          @keyframes boomFlash { 0%{opacity:.98}100%{opacity:0} }
          @keyframes infernoFlicker { 0%,100% { opacity:.88 } 40% { opacity:1 } 70% { opacity:.92 } }
          @keyframes infernoFadeOut { 0% { opacity:1 } 100% { opacity:0 } }
        `}</style>

        <div style={{ position: "relative", height: 56, transform: "perspective(900px) rotateX(7deg)" }}>
          {/* solid 3D track */}
          <div
            style={{
              position: "absolute", inset: 0, borderRadius: 28,
              background: "linear-gradient(180deg,#151a2b 0%, #0f1424 55%, #0a0f1e 100%)",
              boxShadow: "0 16px 36px rgba(0,0,0,.55), inset 0 2px 1px rgba(255,255,255,.12), inset 0 -12px 24px rgba(0,0,0,.68)"
            }}
          />
          {/* rim gloss */}
          <div
            style={{
              position: "absolute", inset: 0, borderRadius: 28,
              background: "linear-gradient(180deg, rgba(255,255,255,.16), rgba(255,255,255,0) 42%, rgba(0,0,0,.28) 72%)"
            }}
          />
          {/* ticks */}
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              style={{
                position: "absolute", top: 8, bottom: 8,
                left: `${((i + 1) / 11) * 100}%`, width: 2, background: "rgba(255,255,255,.12)"
              }}
            />
          ))}

          {/* inner clip area */}
          <div
            style={{
              position: "absolute", top: 4, bottom: 4, left: 4, right: 4,
              borderRadius: 24, overflow: "visible"
            }}
          >
            {/* colour fill */}
            <div
              style={{
                position: "absolute", top: 0, bottom: 0, left: 0,
                width: "calc(var(--pct) * 1%)", borderRadius: 24,
                background:
                  "linear-gradient(90deg,#ef4444 0%, #fb923c 25%, #facc15 50%, #a3e635 75%, #22c55e 100%)",
                boxShadow: "inset 0 2px 6px rgba(255,255,255,.35), inset 0 -6px 10px rgba(0,0,0,.35)"
              }}
            />

            {/* QUESTION NUMBER BADGE — sits on the red end / Bottom-left badge: show Q# for first 5s, then replace with countdown */}
          


{questionNumber > 0 && (showTimer || elapsed < 5) && (
  <div
    style={{
      position: "absolute",
      top: "50%",
      left: 12,
      transform: "translateY(-50%)",
      pointerEvents: "none",
      zIndex: 5
    }}
  >
    <div
      style={{
        minWidth: 64,
        height: 34,
        padding: "0 14px",
        borderRadius: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 900,
        fontSize: 18,
        color: "#fff",
        textShadow: "0 2px 6px #000a",
        background: "linear-gradient(180deg,#b91c1c,#7a0f0f)",
        boxShadow:
          "inset 0 2px 4px rgba(255,255,255,.18), inset 0 -4px 8px rgba(0,0,0,.40), 0 4px 16px rgba(0,0,0,.45)"
      }}
    >
      {showTimer ? `${Math.ceil(timer)}s` : `Q${questionNumber}`}
    </div>
  </div>
)}


            {/* flame & explosions (unchanged)… */}
            {!boom && pct > 0 && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: "calc(50% - 30px)",
                    left: "calc(var(--pct) * 1% - 1.5%)",
                    transform: "translate(-50%,-50%)",
                    width: 64, height: 64, borderRadius: "50%",
                    background:
                      "radial-gradient(circle at 32% 32%, #fff8e8, #ffd166 45%, #ff8a00 65%, #b91c1c 90%)",
                    boxShadow:
                      "0 0 28px 14px rgba(255,136,0,.82), 0 0 54px 22px rgba(255,72,0,.48)",
                    animation: "sparkPulse .1s ease-in-out infinite alternate"
                  }}
                />
                {Array.from({ length: 22 }).map((_, i) => {
                  const back = 28 + i * 6;
                  return (
                    <div
                      key={i}
                      style={{
                        position: "absolute",
                        top: `calc(50% - ${18 + i * 3}px)`,
                        left: "calc(var(--pct) * 1%)",
                        transform: `translate(calc(-50% - ${back}px), -50%)`,
                        width: i % 4 === 0 ? 8 : 6,
                        height: i % 4 === 0 ? 8 : 6,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, #ffd166, #ff8a00)",
                        boxShadow: "0 0 14px 4px rgba(255,136,0,.65)",
                        animation: "emberFloat .75s ease-out infinite",
                        animationDelay: `${i * 0.06}s`
                      }}
                    />
                  );
                })}
                {Array.from({ length: 30 }).map((_, i) => {
                  const angle = (i * 12) * Math.PI / 180;
                  const dx = Math.cos(angle) * (70 + (i % 5) * 16);
                  const dy = -Math.sin(angle) * (48 + (i % 7) * 12);
                  return (
                    <div
                      key={`hs-${i}`}
                      style={{
                        position: "absolute",
                        top: "calc(50% - 20px)",
                        left: "calc(var(--pct) * 1%)",
                        transform: "translate(-50%, -50%)",
                        width: 5, height: 5, borderRadius: "50%",
                        background: "radial-gradient(circle, #ffe08a, #ff9300)",
                        boxShadow: "0 0 12px 4px rgba(255,140,0,.55)",
                        animation: "sparkDrift 900ms ease-out forwards",
                        "--dx": `${dx}px`, "--dy": `${dy}px`
                      }}
                    />
                  );
                })}
              </>
            )}

            {boom && (
              <>
                <div
                  style={{
                    position: "absolute",
                    top: "50%", left: "calc(var(--boom) * 1%)",
                    transform: "translate(-50%,-50%)",
                    width: 420, height: 420, borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(255,255,255,.99), rgba(255,238,0,.96) 34%, rgba(255,170,0,.88) 66%, rgba(255,90,0,.65) 78%, rgba(0,0,0,0) 80%)",
                    animation: "boomFlash 700ms ease-out forwards",
                    pointerEvents: "none"
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "50%", left: "calc(var(--boom) * 1%)",
                    width: 76, height: 76, borderRadius: "50%",
                    border: "12px solid rgba(255,210,0,.98)",
                    transform: "translate(-50%,-50%)",
                    animation: "boomRing 1600ms ease-out forwards",
                    pointerEvents: "none"
                  }}
                />
                {Array.from({ length: 200 }).map((_, i) => {
                  const angle = i * (360 / 200);
                  const r = 420 + (i % 9) * 26;
                  const tx = Math.cos((angle * Math.PI) / 180) * r + "px";
                  const ty = Math.sin((angle * Math.PI) / 180) * -r * 0.72 + "px";
                  return (
                    <div
                      key={`sh-${i}`}
                      style={{
                        position: "absolute",
                        top: "50%", left: "calc(var(--boom) * 1%)",
                        width: (i % 5 === 0 ? 24 : 16),
                        height: 4, borderRadius: 3,
                        background: "linear-gradient(90deg,#ffd166,#ff8a00)",
                        transformOrigin: "left center",
                        animation: "boomShard 1500ms ease-out forwards",
                        "--r": `${angle}deg`, "--tx": tx, "--ty": ty,
                        pointerEvents: "none"
                      }}
                    />
                  );
                })}
              </>
            )}

            {inferno && (
              <div
                style={{
                  position: "absolute",
                  inset: "-40% -8%",
                  background:
                    "radial-gradient(ellipse at center, rgba(255,210,0,.98), rgba(255,120,0,.94) 58%, rgba(0,0,0,0) 75%)",
                  filter: "blur(10px)",
                  mixBlendMode: "screen",
                  animation:
                    "infernoFlicker 320ms ease-in-out infinite, infernoFadeOut 1000ms ease-out 200ms forwards"
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}




// --- DYNAMITE WICK BAR (underneath) ---
function VisibleExplanationMarquee({ visibleExplanation, timer }) {
  // Only show when timer reaches 0 and explanation exists
  if (timer > 0 || !visibleExplanation || !visibleExplanation.trim()) return null;

  return (
    <div style={{ position: "fixed", left: "2vw", right: "2vw", bottom: "5vh", zIndex: 10260, pointerEvents: "none", overflow: "hidden", height: "56px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <style>{`
        @keyframes marqueeScroll {
          0% { transform: translateX(calc(100vw - 4vw)); }
          100% { transform: translateX(calc(-100% - 4vw)); }
        }
        .visible-explanation-marquee {
          animation: marqueeScroll 21.7s linear;
          white-space: nowrap;
        }
      `}</style>
      <div
        className="visible-explanation-marquee"
        style={{
          background: "transparent",
          color: "#fff",
          fontFamily: "'Arial Black', Impact, Arial, sans-serif",
          fontWeight: 900,
          fontSize: "clamp(1.2rem, 2.8vw, 2.6rem)",
          textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
          display: "inline-block",
          lineHeight: "1",
        }}
      >
        {visibleExplanation}
      </div>
    </div>
  );
}

function WickTimerBar({ q, timer }) {
  const { progress, phaseLabel } = getTimerPhase(q, timer);
  const leftPercent = Math.max(0, Math.min(100, progress * 100)); // spark X
  const sparkLeft = `calc(${leftPercent}% - 12px)`;
  const burntRightPercent = 100 - leftPercent; // burnt from the right side

  return (
    <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 3999, pointerEvents: "none", padding: "0 2vw 4px" }}>
      {/* keyframes for spark + embers */}
      <style>{`
        @keyframes sparkPulse { 
          0% { transform: scale(1); filter: brightness(1); } 
          100% { transform: scale(1.08); filter: brightness(1.25); } 
        }
        @keyframes emberFloat {
          0% { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: translate(-12px,-16px) scale(0.6); opacity: 0; }
        }
      `}</style>

      <div
        style={{
          position: "relative",
          height: 22,
          borderRadius: 14,
          background:
            // base rope look
            "repeating-linear-gradient(90deg, #60402a 0 10px, #6c4931 10px 20px)",
          boxShadow: "inset 0 2px 4px rgba(0,0,0,.4), 0 6px 16px rgba(0,0,0,.35)",
          overflow: "hidden"
        }}
      >
        {/* slight top highlight */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(255,255,255,.18), rgba(255,255,255,0) 35%, rgba(0,0,0,.25) 85%)",
            pointerEvents: "none"
          }}
        />

        {/* BURNT (charred) section from the right */}
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: `${burntRightPercent}%`,
            background: "linear-gradient(180deg,#1b1b1b,#0d0d0d)",
            boxShadow: "inset 0 0 8px #000",
            transition: "width 1s linear"
          }}
        />

        {/* SPARK */}
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: sparkLeft,
            transform: "translate(-50%,-50%)",
            width: 24,
            height: 24,
            borderRadius: "50%",
            background:
              "radial-gradient(circle at 32% 32%, #fff7e6, #ffd166 45%, #ff8a00 65%, #b91c1c 90%)",
            boxShadow:
              "0 0 10px 4px rgba(255,136,0,.65), 0 0 22px 8px rgba(255,72,0,.35)",
            animation: "sparkPulse .1s ease-in-out infinite alternate"
          }}
        />






        {/* tiny embers around spark */}
        {[0,1,2].map(i => (
          <div
            key={i}
            style={{
              position: "absolute",
              top: `calc(50% - ${6 + i * 4}px)`,
              left: `calc(${leftPercent}% - ${6 + i * 6}px)`,
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "radial-gradient(circle, #ffd166, #ff8a00)",
              boxShadow: "0 0 10px 2px rgba(255,136,0,.5)",
              animation: "emberFloat .6s ease-out infinite",
              animationDelay: `${i * 0.12}s`
            }}
          />
        ))}





        {/* phase label (small) */}
        {phaseLabel && (
          <div
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              fontWeight: 900,
              fontSize: 12,
              color: "#fff",
              textShadow: "0 2px 6px #0008",
              opacity: 0.9
            }}
          >
            {phaseLabel}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Generic 10s overlay cue (video) ---
// Preloads silently for instant start; plays UNMUTED when shown; auto-hides on end.
// --- Generic 10s overlay cue (video) ---
// Preloads silently for instant start; plays UNMUTED when shown; auto-hides on end/pause.
// Also includes a watchdog to hide after (duration + 120ms) in case 'ended' is missed.
// --- Generic 10s overlay cue (video) ---
// No warm-up/caching. Plays UNMUTED when visible. Auto-hides on end/pause/error.
// Also has a watchdog so it disappears even if 'ended' is missed.
// --- Generic 10s overlay cue (video) ---
// No warm-up or HEAD request. Plays UNMUTED when visible.
// Hides on 'ended' / 'pause' / 'error'. Also has a duration-based watchdog.
// REPLACEMENT: TenSecondCueOverlay — stable onHidden + safe deps
// --- Generic 10s overlay cue (video) ---
// Robust autoplay: try UNMUTED; if blocked, start MUTED then auto-unmute.
// Falls back to TTS only if playback truly can't start.
// --- Generic 10s overlay cue (video) ---
// Robust autoplay: try UNMUTED; if blocked, start MUTED then auto-unmute.
// Falls back to TTS only if playback truly can't start.
// --- Generic 10s overlay cue (video) ---
// Plays over the UI (non-blocking). No layout shift.
/**
 * Apple browsers (Safari on iPad, iPhone and Mac, and every iOS browser) can't show WebM
 * transparency, so Ollie's clips have HEVC-with-alpha .mov twins with the same name.
 */
function ollieVideoSrc(src) {
  const s = String(src || "");
  if (!/\.webm$/i.test(s) || typeof navigator === "undefined") return s;
  return /Apple/i.test(navigator.vendor || "") ? s.replace(/\.webm$/i, ".mov") : s;
}

function TenSecondCueOverlay({
  src,
  visible = false,
  onHidden = () => {},
  fallbackLine = "Ten seconds remaining.", // said if the video can't play; null = say nothing
  style = {},
  fadeMs = 420,
}) {
  const ref = React.useRef(null);
  const [show, setShow] = React.useState(false);
  const hideTORef = React.useRef(0);

  // keep latest onHidden without re-wiring effects
  const onHiddenRef = React.useRef(onHidden);
  React.useEffect(() => { onHiddenRef.current = onHidden; }, [onHidden]);
  const fallbackRef = React.useRef(fallbackLine);
  React.useEffect(() => { fallbackRef.current = fallbackLine; }, [fallbackLine]);

  // wire events once
  React.useEffect(() => {
    const v = ref.current; if (!v) return;
    const onPlaying = () => setShow(true);
    const end = () => { setShow(false); onHiddenRef.current?.(); };
    const onError = () => { setShow(false); try { if (fallbackRef.current) window.aiSpeak?.(fallbackRef.current); } catch {} onHiddenRef.current?.(); };

    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause",   end);
    v.addEventListener("ended",   end);
    v.addEventListener("error",   onError);
    return () => {
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause",   end);
      v.removeEventListener("ended",   end);
      v.removeEventListener("error",   onError);
    };
  }, []);

  const startPlayback = React.useCallback(async (v) => {
    v.muted = false;
    try { await v.play(); return true; } catch {
      try { v.muted = true; await v.play(); setTimeout(() => { try { v.muted = false; } catch {} }, 150); return true; }
      catch { return false; }
    }
  }, []);

  // play/pause + watchdog
  React.useEffect(() => {
    const v = ref.current; if (!v) return;
    if (hideTORef.current) { clearTimeout(hideTORef.current); hideTORef.current = 0; }

    if (visible) {
      try { v.currentTime = 0; } catch {}
      const kick = async () => {
        const ok = await startPlayback(v);
        if (!ok) { try { if (fallbackRef.current) window.aiSpeak?.(fallbackRef.current); } catch {} onHiddenRef.current?.(); return; }
        const ms = Number.isFinite(v.duration) && v.duration > 0 ? Math.ceil(v.duration * 1000) + 120 : 6000;
        hideTORef.current = setTimeout(() => { try { v.pause(); } catch {} onHiddenRef.current?.(); }, ms);
      };
      if (v.readyState >= 1) kick(); else v.addEventListener("loadedmetadata", kick, { once: true });
    } else {
      try { v.pause(); } catch {}
    }
  }, [visible, src, startPlayback]);

  React.useEffect(() => () => { if (hideTORef.current) clearTimeout(hideTORef.current); }, []);

  return (
    <video
      ref={ref}
      src={ollieVideoSrc(src)}
      playsInline
      preload="metadata"
      loop={false}
      controls={false}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        objectFit: "contain",
        pointerEvents: "none",
        opacity: show ? 1 : 0,
        transition: `opacity ${fadeMs}ms ease`,
        zIndex: 5000,              // <— sits UNDER timer (9000) & rules (10050)
        ...style,
      }}
    />
  );
}


/**
 * useSlideTiming — single source of truth for *all* auto-advance rules.
 *
 * What it does:
 *   • If autoplay (opt_autoplay) is OFF → no auto-advance and no holds.
 *   • Slide 0 (lobby) → hard HOLD until Start Time (opt_start_time, HH:MM local).
 *   • END QUIZ → never schedule auto-advance.
 *   • BREAK TIME → show countdown, never schedule.
 *   • While any media is busy (TTS / audio / blocking overlay sets window.__mediaBusy) →
 *       show "after audio", do not schedule.
 *   • DISPLAY-like slides (DISPLAY / SLIDE / STARTROUND / ENDROUND / SHOWROUNDS / INTRODUCTION / JOIN QUIZ) →
 *       schedule hero dwell = 10s after the big hero text *first became visible*.
 *       (We read the stamp from window.__heroShownAt, which your component sets when it shows the hero.)
 *   • Question slides →
 *       do not schedule while timer > 0. Once it hits 0, schedule +10s dwell
 *       starting from window.__introDoneAt (when answers appeared).
 *
 * Returns:
 *   autoCountdown: number of seconds remaining until the scheduled auto-advance,
 *                  or the string "after audio" while media is busy,
 *                  or null when nothing is scheduled.
 *

 *
 * Your UI footer already reads autoCountdown. Keep that as-is.
 */
/**
 * useSlideTiming — the single owner of auto-advance logic.

 *
 * What it schedules/holds:
 *   • Autoplay OFF → nothing scheduled, no holds.
 *   • Slide 0 (lobby) → hard HOLD until Start Time (opt_start_time HH:MM). Shows a live countdown.
 *   • BREAK TIME → show live timer, never schedule.
 *   • While media is busy (TTS/video/per-question audio) → no schedule; we show a specific reason.
 *   • INTRODUCTION / DISPLAY / STARTROUND / ENDROUND / SHOWROUNDS → 10s dwell after hero became visible.
 *     (We read the moment from window.__heroShownAt, set by QuizScreen when it shows the hero.)
 *   • Question slides → only once the question timer hits 0, then +10s dwell (from window.__introDoneAt).
 *
 * Return shape:
 *   { mode: "countdown", seconds: number }                 → show “Auto-advance in Ns”
 *   { mode: "wait", reason: "tts"|"video"|"audio"|"start"} → show “Waiting for …”
 *   null                                                   → nothing to render
 */
// Trigger intro — keep Hero up for DISPLAY/STARTROUND/ENDROUND; retract only for real questions


// ==// === CENTRAL AUTO-ADVANCE LOGIC (single source of truth) ===
// Publishes a single global deadline (epoch ms):
//   window.__autoAdvanceAtMs   (0 = no schedule)
// Returns footer status for your UI:
//   • { mode:"countdown", seconds }
//   • { mode:"wait", reason: "start"|"video"|"audio"|"tts" }
//   • null
// ———————————————————————————————————————————————————————————————
// useSlideTiming — single source of truth for all auto-advance rules
// - Per-game start time key so a new quiz doesn't inherit an old time
// - Live hh:mm:ss countdown while waiting for start
// - Local executor advances slides (incl. lobby at start), no legacy watchers
// ———————————————————————————————————————————————————————————————
// ——————————————————————————————————————————————————————————————
// useSlideTiming — single source of truth for ALL auto-advance
// Publishes:
//   window.__autoAdvanceAtMs     (epoch ms, 0 = none)
//   window.__autoAdvanceReason   ("lobby" | "display" | "question" | "")
// Guarantees:
//   • Slide 0 never advances unless the reason is "lobby" AND the
//     start time (per-game) is reached.
//   • Display-like slides advance 10s after the hero becomes visible.
//   • Questions only advance after timer==0, then +10s dwell.
//   • If TTS / per-question audio / blocking overlay is active, we wait.
// ——————————————————————————————————————————————————————————————
// In Quizscreen.js, replace the entire useSlideTiming function

// In Quizscreen.js, replace the entire useSlideTiming function with this block:

// In Quizscreen.js, REPLACE the entire `useSlideTiming` function with this corrected version:
// In Quizscreen.js, REPLACE your entire existing useSlideTiming function with this one:
// In Quizscreen.js, REPLACE your entire existing useSlideTiming function with this one:

// Quizscreen.js — REPLACE your entire useSlideTiming function with this one
// Quizscreen.js — REPLACE the entire useSlideTiming function with this one
// == CENTRAL AUTO-ADVANCE LOGIC (single source of truth) ==
// == CENTRAL AUTO-ADVANCE LOGIC (single source of truth) ==
function useSlideTiming({ q, current, timer, gameCode }) {
  const [status, setStatus] = React.useState(null);

  const norm = (x) => String(x ?? "").toUpperCase();
  const isDisplayLike = React.useMemo(() => {
    const t = norm(q?.type || q?.CAT);
    return [
      "DISPLAY", "SLIDE", "DEFAULT",
      "INTRODUCTION", "STARTROUND", "ENDROUND",
      "SHOWROUNDS", "JOIN QUIZ"
    ].includes(t);
  }, [q?.type, q?.CAT]);

  // publish current slide classification for the host pump
  React.useEffect(() => {
    window.__isDisplayLike = !!isDisplayLike;
    window.__currentSlideType = String(q?.type || q?.CAT || "").toUpperCase();
  }, [isDisplayLike, q?.type, q?.CAT]);

  // per-game start time so quizzes don't inherit each other
  const startKey = React.useMemo(
    () => `opt_start_time__${String(gameCode || "default")}`,
    [gameCode]
  );

  const parseStartTimeToday = (hhmm) => {
    if (!hhmm) return 0;
    const [hh, mm] = String(hhmm).split(":").map(n => parseInt(n, 10));
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
    const d = new Date();
    d.setSeconds(0, 0);
    d.setHours(hh, mm, 0, 0);
    return d.getTime();
  };

  const detectBusy = () => {
    // Team round tag choice (countdown / reveal)
    try {
      if (window.__teamChoiceGate) return "teamChoice";
    } catch {}
    // Any blocking overlay (intro/mcq/…)
    if (window.__overlayActive) return "video";

    // Ollie still talking, or with more lines queued?
    if (ollieSpeechBusy()) return "tts";

    // Any speech still speaking?
    try {
      if (window.speechSynthesis &&
          (window.speechSynthesis.speaking || window.speechSynthesis.pending)) {
        return "tts";
      }
    } catch {}

    // Any non-bed AUDIO still playing?
    try {
      const audios = Array.from(document.querySelectorAll("audio"));
      const busyAudio = audios.some(a => {
        const isBed = a.loop || a.getAttribute("data-bed") === "1";
        return !isBed && !a.paused && !a.ended && a.currentTime > 0;
      });
      if (busyAudio) return "audio";
    } catch {}

    // Any VIDEO still playing?
    try {
      const videos = Array.from(document.querySelectorAll("video"));
      const busyVideo = videos.some(v => !v.paused && !v.ended && v.currentTime > 0);
      if (busyVideo || window.__videoPlaying) return "video";
    } catch {}

    return null;
  };

  // compute ONE global deadline each frame
  React.useEffect(() => {
    let raf = 0;

    const setDeadline = (ms, reason = "") => {
      window.__autoAdvanceAtMs = Math.max(0, Math.floor(ms || 0));
      window.__autoAdvanceReason = reason || "";
    };

    const loop = () => {
      setDeadline(0, "");

      const autoplay =
        (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase() === "YES";

      // Autoplay OFF → never schedule
      if (!autoplay) {
        if (current === 0) { window.__heroShownAt = 0; window.__introDoneAt = 0; }
        setStatus(null);
        raf = requestAnimationFrame(loop);
        return;
      }

      // LOBBY (slide 0): wait for Start Time (HH:MM)
      if (current === 0) {
        window.__heroShownAt = 0;
        window.__introDoneAt = 0;

        let hhmm = localStorage.getItem(startKey) || "";
        if (!/^\d{2}:\d{2}$/.test(hhmm)) {
          // first time for this game → default to +2 min from now
          const d = new Date();
          d.setSeconds(0, 0);
          d.setMinutes(d.getMinutes() + 2);
          hhmm = `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
          localStorage.setItem(startKey, hhmm);
        }

        const target = parseStartTimeToday(hhmm);
        const now = Date.now();

        if (target > 0) {
              setDeadline(target, "lobby"); // Always publish the deadline
              const secs = Math.max(0, Math.ceil((target - now) / 1000));
              setStatus({ mode: "countdown", seconds: secs, reason: "start" });
            } else {
              // Failsafe for an invalid time string
              setDeadline(0, "");
              setStatus(null);
            }

            raf = requestAnimationFrame(loop);
            return;
        }

      // If blocking media is active, wait
      const busy = detectBusy();
      if (busy) {
        setDeadline(0, "");
        setStatus({ mode: "wait", reason: busy });
        raf = requestAnimationFrame(loop);
        return;
      }

      // DISPLAY-like slides: dwell 10s after hero actually became visible
      if (isDisplayLike) {
        const heroMono = Number(window.__heroShownAt || 0); // performance.now()
        if (heroMono > 0) {
          const dwellMono = heroMono + (isSpeedTestMode() ? 2_000 : 10_000); // +10s on-screen (2s in Speed Test mode)
          // convert perf.now() mono time to wall time
          const wallDeadline = Date.now() - performance.now() + dwellMono;
          setDeadline(wallDeadline, "display");
          const leftMs = Math.max(0, wallDeadline - Date.now());
          setStatus({ mode: "countdown", seconds: Math.ceil(leftMs / 1000) });
        } else {
          setDeadline(0, "");
          setStatus(null);
        }
        raf = requestAnimationFrame(loop);
        return;
      }

      // Questions: only after timer hits 0 → dwell from intro-done (stable baseline)
      const remain = Number(timer) || 0;
      if (remain > 0) {
        setDeadline(0, "");
        setStatus(null);
        raf = requestAnimationFrame(loop);
        return;
      }

      // Wait until the moment answers appeared has been stamped by the UI
      // Get the timestamp for when the timer *first* hit 0.
            let base = Number(window.__timerHitZeroAt || 0);

            if (base === 0) {
              // Timer was 0 on load, or stamp is missing.
              // Fall back to introDoneAt.
              const introDoneBase = Number(window.__introDoneAt || 0);
              if (introDoneBase === 0) {
                // No baseline at all, don't schedule.
                setDeadline(0, "");
                setStatus(null);
                raf = requestAnimationFrame(loop);
                return;
              }
              base = introDoneBase; // Use fallback
            }

      // >>> Dwell time from timer hitting 0 <<<
      const tNorm = String(q?.type || q?.CAT || "").toUpperCase();
      const isJackpot = tNorm === "JACKPOT";
      const isMultiChoice = tNorm === "MULTICHOICE" || tNorm === "MULTIPLECHOICE";
      
      // JACKPOT questions should NOT auto-advance after timer hits 0 - remain paused until host presses P
      if (isJackpot) {
        setDeadline(0, ""); // Don't schedule auto-advance
        setStatus(null); // Clear countdown status
        raf = requestAnimationFrame(loop);
        return;
      }
      
      // Use shorter, more reasonable dwell times for results
      // Speed Test: must stay longer than the 4 s scoring delay or the question is never scored.
      const dwellMs = isSpeedTestMode()
        ? 6_000
        : (tNorm === "VANISHINGCHOICE" || isMultiChoice) ? 15_000 : 12_000;

      const deadline = base + dwellMs;
      setDeadline(deadline, "question");


      const leftMs = Math.max(0, deadline - Date.now());
      setStatus({ mode: "countdown", seconds: Math.ceil(leftMs / 1000) });

      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [q, current, timer, isDisplayLike, startKey]);

  // perform the actual advance when our global deadline elapses
React.useEffect(() => {
    let raf = 0;

    const goNext = () => { // Original goNext function
      // Debug trace to identify who is triggering auto-advance
      try {
        const stackLines = (new Error().stack || "")
          .split("\n")
          .slice(1, 6)
          .map((s) => s.trim());
        // eslint-disable-next-line no-console
        console.log("[Quizscreen] goNext called", {
          opt_autoplay: localStorage.getItem("opt_autoplay") || "Yes",
          stack: stackLines,
        });
      } catch {
        // ignore logging errors
      }

      if (typeof window.__nextSlide === "function") { try { window.__nextSlide(); } catch {} return; }
      if (typeof window.navigateNext === "function") { try { window.navigateNext(); } catch {} return; }
      try { window.dispatchEvent(new CustomEvent("quiz:advance")); } catch {}
      try {
        const ev = new KeyboardEvent("keydown", { key: "ArrowRight", code: "ArrowRight", bubbles: true });
        document.dispatchEvent(ev);
      } catch {}
    };

    const resetDeadline = () => {
      window.__autoAdvanceAtMs = 0;
      window.__autoAdvanceReason = "";
    };

    const tick = () => {
      const d = Number(window.__autoAdvanceAtMs || 0);
      const reason = String(window.__autoAdvanceReason || "");
      const typeNow = String(q?.type || q?.CAT || "").toUpperCase();

      // === START: ADD PAUSE CHECK HERE ===
      const isPaused = (() => {
        try {
          // Replicate the pause check from the footer
          return (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase() !== "YES";
          // Add checks for autoPaused/autoPlayOn props if they exist in your component scope
          // || (typeof autoPaused !== "undefined" && !!autoPaused)
          // || (typeof autoPlayOn !== "undefined" && !autoPlayOn);
        } catch {
          return false; // Assume not paused if localStorage fails
        }
      })();

      // If paused, clear any pending deadline and stop checking
      if (isPaused) {
        resetDeadline();
        raf = requestAnimationFrame(tick); // Keep checking in case it's unpaused
        return;
      }
      // === END: ADD PAUSE CHECK HERE ===


      if (current === 0 && reason !== "lobby") {
        resetDeadline();
      }

      if (d > 0 && Date.now() >= d) {
        const displayLikes = ["DISPLAY","SLIDE","DEFAULT","INTRODUCTION","STARTROUND","ENDROUND","SHOWROUNDS","JOIN QUIZ"];
        const isDisplayLike = displayLikes.includes(typeNow);

        const shouldAdvance =
          (current === 0 && reason === "lobby") ||
          (current !== 0 && isDisplayLike && reason === "display") ||
          (current !== 0 && !isDisplayLike && reason === "question");

        if (shouldAdvance) {
          resetDeadline();
          goNext(); // Only call goNext if not paused (checked above)
        } else {
          resetDeadline();
        }
      }

      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("quiz:advance", resetDeadline);

    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("quiz:advance", resetDeadline);
    };
  }, [current, q]);

  return status;
}






// ===== Auto-advance executor (fires when the global deadline elapses) =====
// ===== Auto-advance executor (fires when the global deadline elapses) =====
function AutoAdvancePump({ status, current, gameCode }) {
  const firedRef = React.useRef({});

  // Clear any pending schedule when the host manually advances
  React.useEffect(() => {
    const onAdvance = () => {
      window.__autoAdvanceAtMs = 0;
      window.__autoAdvanceReason = "";
      firedRef.current = {};
    };
    window.addEventListener("quiz:advance", onAdvance);
    return () => window.removeEventListener("quiz:advance", onAdvance);
  }, []);

  React.useEffect(() => {
    firedRef.current = firedRef.current || {};
    let tick = 0;

    const loop = async () => {
      // Check if auto-play is paused (same check as useSlideTiming)
      const isPaused = (() => {
        try {
          return (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase() !== "YES";
        } catch {
          return false; // Assume not paused if localStorage fails
        }
      })();

      // If paused, skip advancing but keep checking
      if (isPaused) {
        tick = setTimeout(loop, 250);
        return;
      }

      const deadline = Number(window.__autoAdvanceAtMs || 0);
      const reason   = String(window.__autoAdvanceReason || "");
      const isDisplayLike = !!window.__isDisplayLike;

      // only advance if the scheduled reason matches the slide we're *on now*
      const reasonOk =
        (reason === "lobby"    && current === 0) ||
        (reason === "display"  && isDisplayLike) ||
        (reason === "question" && !isDisplayLike);

      if (deadline > 0 && Date.now() >= deadline && reasonOk) {
        if (!firedRef.current[current]) {
          firedRef.current[current] = true;
          try {
            await updateDoc(doc(db, "games", gameCode), {
              current: Number(current) + 1,
              currentAt: serverTimestamp(),
            });
          } catch (err) {
            console.error("Auto-advance failed:", err);
          }
        }
      }
      tick = setTimeout(loop, 250);
    };

    loop();
    return () => tick && clearTimeout(tick);
  }, [status, current, gameCode]);

  return null;
}



// === GlassImageCard (static image OR Jigsaw inside a glass frame) ===
function GlassImageCard({
  src,
  alt = "Quiz Visual",
  style = {},
  jigsawProps = null,
  kenBurns = true
}) {
  const resolvedStyle = { ...style };
  if (resolvedStyle.maxHeight && !resolvedStyle.height) {
    resolvedStyle.height = resolvedStyle.maxHeight;
  }

  // Glass frame — matches your requested aesthetic
  const frameStyle = {
    position: "relative",
    width: "100%",
    borderRadius: 20,
    background: "rgba(255, 255, 255, 0.12)",
    border: "1.5px solid rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 0 25px rgba(100,180,255,0.40)", // soft blue glow
    overflow: "hidden",
    transform: "translateZ(0)", // promote to own layer
    ...resolvedStyle
  };

  // Animate the media element itself (prevents layout fight with wrappers)
  const kbAnim = kenBurns ? "glassKB 24s ease-in-out forwards" : "none";
  const kbCommon = {
    animation: kbAnim,
    transformOrigin: "center center",
    willChange: "transform",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden"
  };

  return (
    <div style={frameStyle}>
      {/* Subtle reflection gloss at the top */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "22%",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0.10) 50%, rgba(255,255,255,0.0))",
          pointerEvents: "none",
          zIndex: 3
        }}
      />

      {/* Media: either Jigsaw reveal or a contained IMG */}
      {jigsawProps ? (
        // Jigsaw inside the glass; animate the reveal container
        <JigsawImageReveal
          {...jigsawProps}
          style={{
            width: "100%", // aspect ratio handled by JigsawImageReveal
            ...kbCommon,
            position: "relative",
            zIndex: 2
          }}
        />
      ) : (
        // Static image inside glass; animate the <img> only
        <ResponsiveContainImage
          src={src}
          alt={alt}
          style={{ width: "100%" }}
          imgStyle={{
            zIndex: 2,
            ...kbCommon
          }}
        />
      )}

      {/* Local keyframes for the gentle Ken Burns */}
      <style>{`
        @keyframes glassKB {
          0%   { transform: scale(1.00); }
          100% { transform: scale(1.06); }
        }
      `}</style>
    </div>
  );
}


// Simple, predictable font-size chooser for answer pills.
// Returns a CSS font-size using clamp() so it scales with viewport.




// ... (imports and other functions)

// ... (other functions remain the same)

// In Quizscreen.js, replace the OutOfOrderRevealHost function with this:

// This function assumes 'React' (and its hooks like useState, useEffect, etc.) 
// and 'computeAnswerFontSize' are available in scope.

const OutOfOrderRevealHost = React.memo(function OutOfOrderRevealHost({ q, timer }) {
  // Answers - get from answers array (matches legacy working version)
  const answers = Array.isArray(q?.answers)
    ? q.answers.map((a) => String(a ?? "").trim()).filter(Boolean)
    : [];
  const n = answers.length;

  // Early return if no answers
  if (n === 0) {
    return null;
  }

  // Clamp/sanitise an order array to 0..n-1 with no dups; fill holes
  const clampOrder = React.useCallback(
    (ord) => {
      const seen = new Set();
      const out = [];
      (Array.isArray(ord) ? ord : []).forEach((i) => {
        const ii = Number(i);
        if (Number.isInteger(ii) && ii >= 0 && ii < n && !seen.has(ii)) {
          seen.add(ii);
          out.push(ii);
        }
      });
      for (let i = 0; i < n; i++) if (!seen.has(i)) out.push(i);
      return out.slice(0, n);
    },
    [n]
  );

  // Jumbled order while clock is running (creator’s order if provided)
  const setupOrder = clampOrder(
    q?.answersOrder?.length ? q.answersOrder : [...Array(n).keys()]
  );

  // Correct order A1..An
  const correctOrder = React.useMemo(
    () => Array.from({ length: n }, (_, i) => i),
    [n]
  );

  // Stable key per question
  const questionKey = React.useMemo(() => {
    const id =
      q?.id ?? q?.ID ?? q?.uuid ?? q?.UUID ?? q?.key ?? q?.Q ?? q?.question ?? "";
    return `${id}|${n}`;
  }, [q?.id, q?.ID, q?.uuid, q?.UUID, q?.key, q?.Q, q?.question, n]);

  // PHASES (matches legacy working version)
  // If timer starts at 0, go straight to reveal phase to show correct order
  const [phase, setPhase] = React.useState(() => {
    if (Number(timer) > 0) return "running";
    // If timer is already 0, show correct order immediately (reveal phase)
    return "reveal";
  }); // "running" | "hiding" | "reveal"
  const [revealedCount, setRevealedCount] = React.useState(() => {
    // If timer is 0, show all items immediately
    return Number(timer) > 0 ? 0 : n;
  });
  const prevTimerRef = React.useRef(Number(timer) || 0);
  const phaseKeyRef = React.useRef(questionKey);
  const hideTORef = React.useRef(0);
  const revealTORef = React.useRef(0);

  // ✅ STABLE COLOUR CLASS MAP:
  // Map each original answer index -> its colour class based on the jumbled line-up.
  // This is computed once per question and reused across phases so colours don't change.
  const classByOrigIndex = React.useMemo(() => {
    const map = {};
    setupOrder.forEach((origIdx, i) => {
      const letter = String.fromCharCode(97 + (i % 6)); // a..f palette
      map[origIdx] = `option-${letter}`;
    });
    return map;
  }, [setupOrder]);

  // Reset on question change (matches legacy)
  React.useEffect(() => {
    if (phaseKeyRef.current !== questionKey) {
      phaseKeyRef.current = questionKey;
      // If timer is 0, show all items immediately (reveal phase)
      if (Number(timer) > 0) {
        setRevealedCount(0);
        setPhase("running");
      } else {
        setRevealedCount(n);
        setPhase("reveal");
      }
      window.clearTimeout(hideTORef.current);
      window.clearTimeout(revealTORef.current);
    }
  }, [questionKey, timer, n]);

  // Timer crossing drives phases (matches legacy - removed phase from deps to prevent loops)
  React.useEffect(() => {
    const prev = prevTimerRef.current;
    const now = Number(timer) || 0;
    prevTimerRef.current = now;

    // >0 → <=0 : hide then reveal
    if (prev > 0 && now <= 0) {
      window.clearTimeout(hideTORef.current);
      window.clearTimeout(revealTORef.current);

      setPhase("hiding");
      setRevealedCount(0);

      const HIDE_MS = 240;
      const STEP_MS = 900;

      hideTORef.current = window.setTimeout(() => {
        setPhase("reveal");
        setRevealedCount(0);

        const tick = (i) => {
          setRevealedCount((c) => Math.min(n, c + 1));
          if (i + 1 < n) {
            revealTORef.current = window.setTimeout(() => tick(i + 1), STEP_MS);
          }
        };
        revealTORef.current = window.setTimeout(() => tick(0), 220);
      }, HIDE_MS);
    }

    // return to running (new question with positive timer) - check phase via ref
    if (now > 0) {
      setPhase((currentPhase) => {
        if (currentPhase !== "running") {
          window.clearTimeout(hideTORef.current);
          window.clearTimeout(revealTORef.current);
          setRevealedCount(0);
          return "running";
        }
        return currentPhase;
      });
    }
  }, [timer, n]); // Removed phase from deps - use functional setState instead

  React.useEffect(() => {
    return () => {
      window.clearTimeout(hideTORef.current);
      window.clearTimeout(revealTORef.current);
    };
  }, []);

  // Which order to render
  const listOrder = phase === "reveal" ? correctOrder : setupOrder;

  const isRunning = phase === "running";
  const isHiding = phase === "hiding"; 
  const isReveal = phase === "reveal";

  return (
    <ol
      key={questionKey}
      className={`answer-list host-answer-list-layout${isRunning ? " answers-stagger-in" : ""}`}
      style={{ listStyle: "none", padding: 0, margin: 0 }}
    >
      {listOrder.map((origIdx, i) => {
        const text = answers[origIdx];
        if (!text) return null; // Skip empty answers
        
        // Visibility logic (matches legacy working version):
        // - When running (timer > 0): show ALL items (isRunning = true)
        // - When hiding (transition): hide all (isHiding = true)  
        // - When revealing (timer = 0): show items up to revealedCount
        const visible = isRunning ? true : isHiding ? false : i < revealedCount;

        // 🔒 Use the locked class from the initial jumbled order (setupOrder)
        const colourClass =
          classByOrigIndex[origIdx] ||
          `option-${String.fromCharCode(97 + (i % 6))}`;
        const classes = `answer-option ${colourClass}`;

        return (
          <li
            key={origIdx}
            className="host-answer-item"
          >
            <div
              className={`${classes} host-answer-pill`}
              style={{
                "--i": i, // Stagger index
                fontSize: computeAnswerFontSize(text), // Dynamic font size
                // Apply opacity/transform for hiding/revealing (matches legacy)
                ...(isRunning
                  ? {} // Running phase has no extra opacity/transform
                  : { // Hiding/Reveal phases
                      opacity: visible ? 1 : 0,
                      transform: visible ? "translateY(0px)" : "translateY(8px)",
                      transition:
                        "opacity 360ms ease, transform 420ms cubic-bezier(.2,.7,.2,1)",
                    }),
                boxShadow: // Static green halo for final reveal
                  isReveal && visible
                    ? "0 0 0 4px rgba(34,197,94,0.9), 0 0 18px rgba(34,197,94,0.55)"
                    : undefined, // Let CSS handle default shadow
              }}
              aria-hidden={!visible && !isRunning}
            >
              {text}
            </div>
          </li>
        );
      })}
    </ol>
  );
}, (prevProps, nextProps) => {
  // Only re-render if timer value changed OR question actually changed
  const timerSame = Number(prevProps.timer) === Number(nextProps.timer);
  
  // Compare question by key fields to detect real changes
  const prevQKey = `${prevProps.q?.type}_${prevProps.q?.question}_${prevProps.q?.answers?.length}_${prevProps.q?.answersOrder?.length}`;
  const nextQKey = `${nextProps.q?.type}_${nextProps.q?.question}_${nextProps.q?.answers?.length}_${nextProps.q?.answersOrder?.length}`;
  const questionSame = prevQKey === nextQKey;
  
  // Return true to skip re-render, false to re-render
  return timerSame && questionSame;
});

// ... (rest of Quizscreen.js)

// ... (rest of Quizscreen.js)


// --- Add this function back ABOVE the QuizScreen component ---

// Simple, predictable font-size chooser for answer pills.
// Returns a CSS font-size using clamp() so it scales with viewport.
// Simple, predictable font-size chooser for answer pills.
// Returns a CSS font-size using clamp() so it scales with viewport.

const computeQuestionFontSize = (text) => {
  const len = (String(text || "")).length;

  // --- Reduced values by ~15% ---
  if (len > 200) return "clamp(1.0rem, 1.7vw, 1.9rem)"; // Was 1.2, 2.0, 2.2
  if (len > 150) return "clamp(1.2rem, 2.1vw, 2.1rem)"; // Was 1.4, 2.5, 2.5
  if (len > 100) return "clamp(1.4rem, 2.4vw, 2.4rem)"; // Was 1.6, 2.8, 2.8
  if (len > 70)  return "clamp(1.5rem, 2.7vw, 2.7rem)"; // Was 1.8, 3.2, 3.2
  if (len > 40)  return "clamp(1.7rem, 3.0vw, 3.0rem)"; // Was 2.0, 3.5, 3.5
  return "clamp(1.9rem, 3.2vw, 3.4rem)";                // Was 2.2, 3.8, 4.0
};


const computeAnswerFontSize = (text) => {
  const len = (String(text || "")).length;
  // >>> REVISED: MUCH MORE AGGRESSIVE SCALING FOR LONGER TEXT <<<
  if (len > 100) return "clamp(0.6rem, 0.7vw, 1.1rem)"; // Extra Tiny
  if (len > 90) return "clamp(0.65rem, 0.8vw, 1.2rem)";
  if (len > 80) return "clamp(0.7rem, 0.9vw, 1.3rem)";
  if (len > 70) return "clamp(0.75rem, 1.0vw, 1.4rem)";
  if (len > 60) return "clamp(0.8rem, 1.1vw, 1.5rem)";
  if (len > 50) return "clamp(0.9rem, 1.2vw, 1.6rem)";
  if (len > 40) return "clamp(1.0rem, 1.4vw, 1.8rem)"; // This used to be 1.1rem
  if (len > 30) return "clamp(1.2rem, 1.6vw, 2.1rem)"; // Increased a bit
  if (len > 22) return "clamp(1.5rem, 2.0vw, 2.6rem)"; // Increased a bit
  return "clamp(1.8rem, 2.4vw, 3.0rem)";               // Base size
};

// TeamNameFit component
function TeamNameFit({ text }) {
  const containerRef = React.useRef(null);
  const textRef = React.useRef(null);
  const [fontPx, setFontPx] = React.useState(40);

  const recalc = React.useCallback(() => {
    const box = containerRef.current;
    const span = textRef.current;
    if (!box || !span) return;
    const MAX = 42, MIN = 14;
    let f = MAX;
    span.style.letterSpacing = "1px";
    while (f >= MIN) {
      span.style.fontSize = `${f}px`;
      span.offsetHeight; // force layout
      const fitsH = span.scrollHeight <= box.clientHeight + 1;
      const fitsW = span.scrollWidth <= box.clientWidth + 1;
      if (fitsH && fitsW) break;
      f -= 1;
    }
    setFontPx(f);
  }, []);

  React.useLayoutEffect(() => { recalc(); }, [text, recalc]);

  React.useEffect(() => {
    const box = containerRef.current;
    if (!box) return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(box);
    return () => ro.disconnect();
  }, [recalc]);

  return (
    <div ref={containerRef} style={{ width: "92%", marginTop: 10, marginBottom: 6, lineHeight: 1.12, height: "3.4em", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", overflow: "hidden" }}>
      <span ref={textRef} style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden", whiteSpace: "normal", wordBreak: "break-word", hyphens: "auto", fontSize: `${fontPx}px`, fontWeight: 800, color: "#fff", textShadow: "0 4px 18px #000b", letterSpacing: "1px" }}>
        {text}
      </span>
    </div>
  );
}



// ===>>> ADD THIS NEW COMPONENT <<<===
// gemini changes: Quizscreen.js
// ===>>> REPLACE the old JoinedTeamsScroll function with this new one <<<===
function JoinedTeamsScroll({ teams }) {
  // Use constants for card styling, similar to Roundscores
  const CARD_HEIGHT = "54vh"; // Fits well in the "lower half"
  const CARD_GAP = "2vw";

  // Don't render anything if no teams have joined
  if (!teams || teams.length === 0) {
    return (
      <div style={{
        position: 'fixed',
        bottom: '10vh', // Centered in lower half
        width: '100%',
        textAlign: 'center',
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#fff',
        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
        pointerEvents: 'none',
      }}>
        Waiting for teams to join...
      </div>
    );
  }

  // Create a doubled list for the infinite scroll effect
  const doubledTeams = [...teams, ...teams];
  
  // Estimate animation duration based on team count
  const duration = Math.max(40, teams.length * 5) * 0.30;

  return (
    <div className="scrolling-teams-container">
<style>
{`
/* Keep the original keyframes */
@keyframes scrollRight {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100vw); }
          }
          
          /* Keep original container positioning and height */
          .scrolling-teams-container {
            position: fixed;
            bottom: 0; /* Anchor to bottom */
            left: 0;
            width: 100%;
            height: ${CARD_HEIGHT}; /* Use original CARD_HEIGHT constant */
            margin-bottom: 2vh; /* Keep original margin */
            overflow: hidden;
            pointer-events: none; /* Keep */
            mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
            -webkit-mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
          }

          /* Keep original wrapper styles */
          .scrolling-teams-wrapper {
            display: flex;
            flex-direction: row;
            align-items: stretch;
            gap: ${CARD_GAP};
            width: max-content;
            height: 100%;
            animation: scrollRight ${duration}s linear infinite;
            will-change: transform;
            transform: translateX(-100%); /* Start off-screen */
          }

          /* Keep most card styles, but CHANGE aspect-ratio */
          .team-join-card {
            height: 95%; /* Keep original relative height */
            /* --- CHANGE ASPECT RATIO --- */
            aspect-ratio: 9/16; /* Taller 9:16 aspect ratio */
            /* --- END CHANGE --- */
            border-radius: 2.2em; /* Keep original */
            background: rgba(0,0,0,0.80); /* Keep original */
            box-shadow: 0 12px 64px #000b, 0 0 0 4px rgba(255,255,255,0.2); /* Keep original */
            display: flex; /* Keep original */
            flex-direction: column; /* Keep original */
            align-items: center; /* Keep original */
            justify-content: flex-start; /* Keep original */
            position: relative; /* Keep original */
            overflow: visible; /* Keep original */
            margin: auto 0; /* Center vertically if container is shorter */
          }

          /* Keep original selfie container styles */
          .team-join-selfie-container {
            width: 100%;
            flex-grow: 1;
            height: 70%; /* Keep original height proportion */
            border-radius: 2em 2em 1.1em 1.1em;
            overflow: hidden;
            background: #222; /* Use darker bg for contain */
            display: flex;
            align-items: center;
            justify-content: center;
          }

          /* Keep most image styles, but CHANGE object-fit */
          .team-join-selfie-img {
            width: 100%;
            height: 100%;
            /* --- CHANGE OBJECT-FIT --- */
            object-fit: contain; /* Fit entire image within container */
            /* --- END CHANGE --- */
            border-radius: 2em 2em 1.1em 1.1em; /* Keep original */
          }
          
          /* Keep original placeholder styles */
          .team-join-selfie-placeholder {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff7;
            font-size: 6.5rem; /* Keep original */
            background: #222c; /* Keep original */
          }
`}
      </style>

      <div className="scrolling-teams-wrapper">
          {teams.map((team, index) => (      
                    <div key={index} className="team-join-card">
            
            {/* Selfie Container */}
            <div className="team-join-selfie-container">
              {team.selfie ? (
                <img
                  src={team.selfie}
                  onError={(e) => e.currentTarget.src = "/default-selfie.png"}
                  alt={`${team.name}'s selfie`}
                  className="team-join-selfie-img"
                />
              ) : (
                <div className="team-join-selfie-placeholder">👥</div>
              )}
            </div>
            
            {/* Team Name (using TeamNameFit) */}
            <TeamNameFit text={team.name} />

            {/* Spacer to push name up */}
            <div style={{ marginTop: "auto" }}></div> 
            
          </div>
        ))}
      </div>
    </div>
  );
}
// ===>>> END OF REPLACEMENT <<<===
// ===>>> END OF NEW COMPONENT <<<===

// Extract track ID from Spotify embed URL
function extractSpotifyTrackId(spotifyUrl) {
  if (!spotifyUrl) return null;
  const match = spotifyUrl.match(/\/track\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

// Construct Spotify preview URL directly (no API credentials needed!)
function getSpotifyPreviewUrl(trackId) {
  if (!trackId) return null;
  // Direct preview URL pattern - publicly accessible, no auth needed
  return `https://p.scdn.co/mp3-preview/${trackId}`;
}

export default function QuizScreen({
  q,
  timer,
  timerActive = false,
  allAnswers,
  vanishingIndexes = [],
  justVanished = [],
  getGlossyColor = () => "#444",
  current,
  questions,
  gameCode,
  joinCode,
  roundQuestionStartIdx,
  teamInfo,
  advance,
  hideNearestAnswerBars = false,
  showQuizOptions: showQuizOptionsProp,
  setShowQuizOptions: setShowQuizOptionsProp,
  onAnswersGateOpened,
}) {

const introSpokenRef = useRef(false);
const lastSpokenQuestionRef = React.useRef({ current: -1, questionKey: "" });
const onAnswersGateOpenedRef = useRef(onAnswersGateOpened);
onAnswersGateOpenedRef.current = onAnswersGateOpened;
  const questionTextForDisplay = buildQuestionDisplayText(q);
const [showRoundScoresPopup, setShowRoundScoresPopup] = useState(false);
const [spotifyPreviewUrl, setSpotifyPreviewUrl] = useState(null);
const [showEndRoundScores, setShowEndRoundScores] = useState(false);

// ===>>> ADD useEffect TO TRIGGER ROUND SCORES POPUP <<<===
  useEffect(() => {
    const type = String(q?.type || q?.CAT || "").toUpperCase();
    console.log(`QuizScreen useEffect[type/current]: Current=${current}, Type=${type}, ShowPopup=${type === 'ENDROUND'}`); // Optional: Add log here
    if (type === "ENDROUND") {
      setShowRoundScoresPopup(true);
    } else {
      // Hide the popup when navigating away from the ENDROUND slide
      setShowRoundScoresPopup(false);
    }
  }, [q?.type, q?.CAT, current]);

useEffect(() => {
  const type = String(q?.type || q?.CAT || "").toUpperCase();
  if (type === "ENDROUND") {
    setShowEndRoundScores(true);
  }
}, [q]); // Runs when the question object changes

  const isShowRounds = String((q?.type ?? q?.CAT ?? "")).toUpperCase() === "SHOWROUNDS";

  const handleJumpToSlide = React.useCallback(
    async (targetIdx) => {
      if (!Number.isFinite(targetIdx) || targetIdx === current) return;
      if (typeof advance === "function") {
        await advance(targetIdx - current, true);
        return;
      }
      if (!gameCode) return;
      const target = Array.isArray(questions) ? questions[targetIdx] : null;
      const seconds = parseInt(target?.TIME ?? target?.time ?? 0, 10) || 0;
      const hasTimer = !!seconds;
      const active = timerActiveOnQuestionAdvance(target, hasTimer);
      try {
        await updateDoc(doc(db, "games", gameCode), {
          current: targetIdx,
          timer: seconds,
          timerActive: active,
          currentAt: serverTimestamp(),
        });
      } catch (err) {
        console.error("Failed to jump to round:", err);
      }
    },
    [advance, current, gameCode, questions]
  );

/// At the top of the QuizScreen component
const [stopPulse, setStopPulse] = React.useState(false);

// This effect now correctly handles the 5-second stop
React.useEffect(() => {
  // Reset the pulse stop whenever the question changes
  setStopPulse(false);

  // If the timer has ended for the current question...
  if (timer === 0) {
    // ...wait 5 seconds, then set the state to stop the pulse.
    const timeoutId = setTimeout(() => {
      setStopPulse(true);
    }, 5000);
    // Cleanup the timer if the component unmounts or re-renders
    return () => clearTimeout(timeoutId);
  }
}, [current, timer]); // Reruns when question or timer changes

const isOutOfOrder = String(q?.type || q?.CAT || "").toUpperCase() === "OUTOFORDER";


// Set hero timestamp immediately for display-like slides (runs synchronously before paint)
React.useLayoutEffect(() => {
  // Calculate isDisplay inline since it's defined later in the component
  const isDisplayLike = normalizeType(q) === 'DISPLAY';
  if (isDisplayLike && current !== 0) {
    // For display-like slides (including SHOWROUNDS), set timestamp immediately
    // so auto-advance timing can start right away
    const tMono = performance.now();
    window.__heroShownAt = tMono;
  }
}, [current, q]);

React.useEffect(() => { 
  // Don't reset for display-like slides - they need the timestamp for auto-advance
  // Calculate isDisplay inline since it's defined later in the component
  const isDisplayLike = normalizeType(q) === 'DISPLAY';
  if (!isDisplayLike) {
    window.__heroShownAt = 0; 
    window.__introDoneAt = 0; 
  }
}, [current, q]);

// Add this entire block to QuizScreen.js
const prevTimerForStampRef = React.useRef(timer);
React.useEffect(() => {
  // Just crossed from >0 to 0
  if (timer <= 0 && prevTimerForStampRef.current > 0) {
    window.__timerHitZeroAt = Date.now();
  } else if (timer > 0 || current === 0) {
    // Reset if timer is running or we're in lobby
    window.__timerHitZeroAt = 0;
  }
  prevTimerForStampRef.current = timer;
}, [timer, current]);

// Also reset on question change (belt-and-braces)
React.useEffect(() => {
  window.__timerHitZeroAt = 0;
}, [current]);

// ===>>> CURSOR HIDING EFFECT (APP-WIDE) <<<===
// Hide cursor after 10 seconds of inactivity throughout the entire quiz application
React.useEffect(() => {
  let cursorTimeout = null;
  const CURSOR_TIMEOUT_MS = 10000; // Hide cursor after 10 seconds of inactivity

  const handleMouseMove = () => {
    // Show cursor temporarily on movement
    document.body.style.cursor = 'default';
    
    // Clear existing timeout
    if (cursorTimeout) {
      clearTimeout(cursorTimeout);
    }
    
    // Hide cursor again after timeout
    cursorTimeout = setTimeout(() => {
      document.body.style.cursor = 'none';
    }, CURSOR_TIMEOUT_MS);
  };

  // Set up mouse move listener
  document.addEventListener('mousemove', handleMouseMove);
  
  // Initial timeout to hide cursor after 10 seconds
  cursorTimeout = setTimeout(() => {
    document.body.style.cursor = 'none';
  }, CURSOR_TIMEOUT_MS);

  // Cleanup
  return () => {
    if (cursorTimeout) {
      clearTimeout(cursorTimeout);
    }
    document.removeEventListener('mousemove', handleMouseMove);
    // Restore default cursor on cleanup
    document.body.style.cursor = 'default';
  };
}, []); // Run once on mount, cleanup on unmount
// ===>>> END OF CURSOR HIDING EFFECT <<<===

// ===>>> ADD THIS MEMO <<<===
const teamsArray = React.useMemo(() => {
  if (!teamInfo) return [];
  return Object.entries(teamInfo).map(([name, selfie]) => ({
    name,
    selfie: selfie || null // Use null or a default placeholder
  }));
}, [teamInfo]);
// ===>>> END OF MEMO <<<===



  if (!q) return null;


// Promise resolver for any blocking overlay (MCQ, etc.)
const overlayResolveRef = React.useRef(null);

// Await this anywhere you need to pause until an overlay (e.g., MCQ video) finishes.
// Race-safe: if the overlay is marked active but no Promise is attached yet, we spin briefly
// until either the Promise appears or the overlay deactivates.
async function awaitOverlayGate() {
  const p = window.__overlayPromise;
  if (p && typeof p.then === "function") {
    try { await p; } catch {}
    return;
  }
  // Race-safe: overlay flagged active but Promise not set yet
  const start = performance.now();
  while (window.__overlayActive && (performance.now() - start) < 30000) {
    if (window.__overlayPromise && typeof window.__overlayPromise.then === "function") {
      try { await window.__overlayPromise; } catch {}
      return;
    }
    await new Promise(r => setTimeout(r, 30));
  }
}


// Global per-question fired set (survives remounts/HMR)
if (!window.__tenCueFiredByKey) window.__tenCueFiredByKey = new Set();

// Tracks whether the previous question was multichoice
const prevWasMcqRef = React.useRef(false);

// One-shot guard for the 10s cue (per question) + de-bounce against StrictMode
// One-shot guard for the 10s cue (per question) + de-bounce against StrictMode
// Multiple Choice intro (character-driven, one-time per quiz)
const [showMcqOverlay, setShowMcqOverlay] = React.useState(false);
const [mcqOverlaySrc, setMcqOverlaySrc]   = React.useState("");
// Any other Ollie clip (end of round, …): played by playOllieVideo, which resolves when it ends.
const [ollieOverlaySrc, setOllieOverlaySrc] = React.useState("");
const ollieOverlayDoneRef = React.useRef(null);
const [internalShowQuizOptions, setInternalShowQuizOptions] = React.useState(true);
const showQuizOptions = showQuizOptionsProp !== undefined ? showQuizOptionsProp : internalShowQuizOptions;
const setShowQuizOptions = setShowQuizOptionsProp !== undefined ? setShowQuizOptionsProp : setInternalShowQuizOptions;


// Before: const autoCountdown = useSlideTiming({ q, current, timer });
const status = useSlideTiming({ q, current, timer, gameCode });





// Always re-open the options popup whenever we navigate back to the lobby (slide 0)
// But only when transitioning TO slide 0, not when already on it
const prevCurrentRef = React.useRef(current);
React.useEffect(() => {
  // Only auto-open if we just navigated TO slide 0 (transition from non-0 to 0)
  if (current === 0 && prevCurrentRef.current !== 0) {
    setShowQuizOptions(true);
  }
  prevCurrentRef.current = current;
}, [current]);

// Keyboard shortcut: "o" or Menu (☰) opens existing Quiz Options popup (same panel used on slide 0)
// Fire Stick: ☰ may send ContextMenu or keyCode 82; in live quiz we use it for options (Silk menu still works on other pages)
React.useEffect(() => {
  const handleKeyDown = (e) => {
    const k = (e.key || "").toLowerCase();
    const code = (e.code || "").toLowerCase();
    const keyCode = e.keyCode;
    const isMenuKey = k === "contextmenu" || code === "contextmenu" || keyCode === 82;
    const isOptionsToggle = k === "o" || isMenuKey;

    if (isOptionsToggle) {
      const t = e.target;
      const tag = (t && t.tagName) ? t.tagName.toUpperCase() : "";
      const isTypingText = (tag === "INPUT" && t.type !== "button" && t.type !== "checkbox" && t.type !== "radio") ||
                           tag === "TEXTAREA" ||
                           (t && (t.isContentEditable || t.getAttribute?.("contenteditable") === "true"));
      if (isTypingText && k === "o" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        return;
      }
      e.preventDefault();
      setShowQuizOptions(prev => !prev);
      return;
    }
    
    // For other keys, check if in editable field (original logic)
    const t = e.target;
    const tag = (t && t.tagName) ? t.tagName.toUpperCase() : "";
    const inEditable =
      tag === "INPUT" ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      (t && (t.isContentEditable || t.getAttribute?.("contenteditable") === "true"));
    if (inEditable) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
  };
  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}, []);




  const [rulesCardVisible, setRulesCardVisible] = React.useState(false);

  const p1Url = q.P1_URL || q.p1;
  const backgroundUrl = q.BACKGROUND_URL || q.background;
  const soundUrl = q.SOUND || q.SOUND_URL || q.SPOTIFY_URL || q.sound;
  const videoUrl = q.VIDEO_URL || q.video;

  const [countdownOverlay, setCountdownOverlay] = React.useState(null);

  // Cinematic intro (big centered question visible)
const [showIntroState, setShowIntro] = React.useState(false);  // hero on when true
const [answersReady, setAnswersReady] = React.useState(false);
const heroHideRequestRef = React.useRef(null);
const [heroHideSignal, setHeroHideSignal] = React.useState(0);
const firstAnswerAnimLoggedRef = React.useRef(-1);
const handleFirstAnswerAnimStart = React.useCallback(() => {
  setAnswersReady((ready) => {
    if (ready) return ready;
    firstAnswerAnimLoggedRef.current = current;
    try {
      console.info("[Hero/TTS] First answer animation started", { current });
    } catch {}
    return true;
  });
}, [current]);
  const [introDone, setIntroDone] = React.useState(false);  // when true, render answers
  const [answersStagger, setAnswersStagger] = React.useState(false);
  const [animKey, setAnimKey] = React.useState(0);
const rafRef = React.useRef({ r1: 0, r2: 0 });
const tRef = React.useRef({ hold: 0, clear: 0 });
// When the hero actually became visible + a hard minimum on-screen time
const heroShownAtRef = React.useRef(0);
const HERO_MIN_VISIBLE_MS = 2200; // tweak this to hold the hero longer/shorter

// Debug hero visibility changes (initialized after showIntro is computed)
const prevShowIntroRef = React.useRef(false);

React.useEffect(() => {
  if (!introDone) {
    setAnswersReady(false);
  }
}, [introDone]);

React.useEffect(() => {
  if (introDone && !answersStagger) {
    setAnswersReady(true);
  }
}, [introDone, answersStagger]);

React.useEffect(() => {
  const pending = heroHideRequestRef.current;
  if (!pending) return;
  if (pending.questionIndex !== current) return;
  if (pending.waitForAnswers && !answersReady) return;

  try {
    console.info("[Hero/TTS] Executing hero hide request", {
      current,
      waitForAnswers: pending.waitForAnswers,
      answersReady,
    });
  } catch {}

  setHeroIntro(false, "hero-hide-request");
  heroHideRequestRef.current = null;
}, [answersReady, heroHideSignal, current]);


  const lastQuestionRef = React.useRef("");

  // Special block types
  const isBBlock = q.question && q.question.trim().startsWith("{B}");
  const hasNoQuestion = !q.question || !q.question.trim() || q.question.trim() === "?";
  const hasImageOnly = !!p1Url && hasNoQuestion;

  const isMulti = (q.type || "").toUpperCase() === "MULTIANSWER";
  const correctIndexes = isMulti
    ? q.answers.map((a, i) => (/^\s*\[.*?\]\s*$/.test(a) ? i : null)).filter((i) => i !== null)
    : [0];

// Robust Default/Display detector (treat STARTROUND/ENDROUND/SHOWROUNDS as display-like)
// Robust Default/Display detector (treat STARTROUND/ENDROUND/SHOWROUNDS as display-like)
const rawType = String(q.type ?? "").toUpperCase();
const rawCat  = String(q.CAT ?? q.cat ?? q.category ?? q.Category ?? "").toUpperCase();
const rawTypeUpper = rawType || rawCat;


const answersArr = Array.isArray(q.answers) ? q.answers : [];
const looksDisplayByContent =
  !isShowRounds &&
  answersArr.every(a => String(a ?? "").trim() === "") &&
  (parseInt(q.time ?? q.TIME ?? 0, 10) === 0);

const isStartEnd = rawTypeUpper === "STARTROUND" || rawTypeUpper === "ENDROUND";

const isDisplay = normalizeType(q) === 'DISPLAY';

// Compute showIntro directly for DISPLAY slides to avoid timing issues during auto-advance
// This ensures showIntro is true during render, not after useEffect runs
const showIntro = React.useMemo(() => {
  // For DISPLAY slides, always show intro immediately
  if (isDisplay) {
    return true;
  }
  // For other slides, use the state
  return showIntroState;
}, [isDisplay, showIntroState]);

// Debug hero visibility changes (moved here so showIntro is defined)
React.useEffect(() => {
  if (prevShowIntroRef.current !== showIntro) {
    try {
      console.info("[Hero/TTS] showIntro changed", {
        from: prevShowIntroRef.current,
        to: showIntro,
        current,
      });
    } catch {}
    prevShowIntroRef.current = showIntro;
  }
}, [showIntro, current]);



  // Audio
  const audioRef = React.useRef(null);
const vanishSfxRef = React.useRef(null);
// Background bed (plays after TTS + any SOUND_URL have finished)
const bgBedRef = React.useRef(null);
const bgBedKeyRef = React.useRef(null);   // fire-once key per question
const bgIdlePollRef = React.useRef(null); // polling handle (cleared on unmount/change)


// track the previous justVanished contents
const prevJVRef = React.useRef([]);

const endPingRef = React.useRef(null);   // NEW: end-of-question sound
const prevTimerRef = React.useRef(timer); // NEW: track last timer to detect crossing to 0
const wheelWhooshRef = React.useRef(null);
const wheelAudioCtxRef = React.useRef(null);
const wheelClickTimeoutsRef = React.useRef([]);
const wheelTransitionMs = 5000; // wheel spin duration; overlay visible for full spin
const wheelTtsDelayAfterMs = 0; // extra delay after wheel ends before question TTS (so type isn't given away)
const [wheelTransition, setWheelTransition] = React.useState({
  active: false,
  key: 0,
  type: ""
});
const wheelTimeoutRef = React.useRef(null);

const hostRef = React.useRef(null);

// === Character prompt manager (OLLIE) ===
// === Character prompt manager (OLLIE) — PERSIST ACROSS REMOUNTS ===
const promptMgrRef = React.useRef(null);
const [promptMgrReady, setPromptMgrReady] = React.useState(false);
const activeHostId = "OLLIE";

const audioLog = (level, ...args) => {
  if (typeof window === "undefined") return;
  if (!window.__enableAudioLogs) return;
  try {
    (console?.[level] || console.log)?.apply(console, args);
  } catch {}
};

const playWheelClick = React.useCallback(() => {
  if (typeof window === "undefined") return;
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  let ctx = wheelAudioCtxRef.current;
  if (!ctx) {
    ctx = new AudioContext();
    wheelAudioCtxRef.current = ctx;
  }
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "square";
  osc.frequency.value = 1100;
  const now = ctx.currentTime;
  gain.gain.setValueAtTime(0.0, now);
  gain.gain.linearRampToValueAtTime(0.05, now + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.06);
}, []);

const setHeroIntro = React.useCallback(
  (value, reason) => {
    setShowIntro((prev) => {
      if (prev === value) return prev;
      try {
        console.info("[Hero/TTS] setShowIntro", {
          from: prev,
          to: value,
          reason,
          current,
        });
      } catch {}
      return value;
    });
  },
  [current]
);

// Note: showIntro for DISPLAY slides is now computed directly via useMemo above
// No need for useEffect - it's available synchronously during render

// Stable per-question type so wheel effect only runs when question changes, not when q gets a new reference (e.g. from Firebase/timer re-renders)
const questionType = (q && normalizeType(q)) || "";

React.useEffect(() => {
  if (current <= 0) return;
  if (isDisplay) return;
  let resolveWheelComplete;
  window.__wheelCompletePromise = new Promise((r) => { resolveWheelComplete = r; });
  setWheelTransition((prev) => ({
    active: true,
    key: prev.key + 1,
    type: questionType
  }));
  if (wheelTimeoutRef.current) {
    clearTimeout(wheelTimeoutRef.current);
  }
  wheelTimeoutRef.current = setTimeout(() => {
    setWheelTransition((prev) => ({ ...prev, active: false }));
    if (typeof resolveWheelComplete === "function") resolveWheelComplete();
  }, wheelTransitionMs);
  return () => {
    if (wheelTimeoutRef.current) {
      clearTimeout(wheelTimeoutRef.current);
    }
  };
}, [current, isDisplay, questionType, wheelTransitionMs]);

React.useEffect(() => {
  if (!wheelTransition.active) return;
  const timeouts = [];
  const whoosh = wheelWhooshRef.current;
  if (whoosh) {
    try {
      whoosh.currentTime = 0;
      whoosh.volume = 0.55;
      whoosh.play().catch(() => {});
    } catch {}
  }
  const clickCount = Math.max(12, WHEEL_TYPE_DEFS.length);
  for (let i = 0; i < clickCount; i += 1) {
    const t = clickCount === 1 ? 1 : i / (clickCount - 1);
    const eased = 1 - Math.pow(1 - t, 2.2);
    const at = Math.round(eased * Math.max(0, wheelTransitionMs - 260));
    timeouts.push(setTimeout(() => playWheelClick(), at));
  }
  wheelClickTimeoutsRef.current = timeouts;
  return () => {
    timeouts.forEach((id) => clearTimeout(id));
  };
}, [wheelTransition.key, playWheelClick, wheelTransitionMs]);




// Slide index whose own sound clip has started; the music bed stays off for the rest of it.
const questionSoundPlayedRef = React.useRef(-1);
React.useEffect(() => {
  const el = audioRef?.current;
  if (!el) return;
  // The question's own clip plays alone: the bed would drown out anything quiet.
  const onPlaying = () => {
    questionSoundPlayedRef.current = current;
    try {
      document.querySelectorAll('audio[data-bed="1"]').forEach((bed) => { try { bed.pause(); } catch {} });
    } catch {}
  };
  el.addEventListener("playing", onPlaying);
  return () => el.removeEventListener("playing", onPlaying);
}, [q, current]);

React.useEffect(() => {
  const el = audioRef?.current;
  if (!el) return;
  const onPlay = () => { window.__mediaBusy = true; };
  const onEnded = () => { window.__mediaBusy = false; };
  const onPause = () => { if (el.ended) window.__mediaBusy = false; };
  el.addEventListener("play", onPlay);
  el.addEventListener("ended", onEnded);
  el.addEventListener("pause", onPause);
  return () => {
    el.removeEventListener("play", onPlay);
    el.removeEventListener("ended", onEnded);
    el.removeEventListener("pause", onPause);
  };
}, [q, current]);






/**
 * We keep a single OLLIE manager on window so it survives:
 * - route changes
 * - going back to slide 0 (lobby)
 * - hot reloads / remounts
 *
 * This prevents the ten_second queue from “starting over” just because
 * the display component remounted.
 */
React.useEffect(() => {
  let alive = true;
  (async () => {
    // Reuse the existing, already-loaded manager if present
    if (window.__ollieMgr && window.__ollieMgr.loaded) {
      promptMgrRef.current = window.__ollieMgr;
      if (alive) setPromptMgrReady(true);
      return;
    }

    // Otherwise create and load it once, then stash globally
    const mgr = new CharacterPromptManager({ hostId: activeHostId, basePath: "/characters" });
    await mgr.load(); // silent if missing
    if (!alive) return;

    window.__ollieMgr = mgr;
    promptMgrRef.current = mgr;
    setPromptMgrReady(true);
  })();

  return () => { alive = false; };
}, [activeHostId]);


// Reset Ollie’s once-per-quiz tracking when returning to the lobby




// Question # that excludes slides/display/default items
// Question # that excludes slides/display/default items (SHOWROUNDS is excluded too)
// Question # that excludes slides/display/default items (SHOWROUNDS is excluded too)
const displayQuestionNumber = React.useMemo(() => {
  if (!Array.isArray(questions) || current < 0) return 0;

  let count = 0;
  for (let i = 0; i <= current; i++) {
    const it = questions[i] || {};
    const tNorm = normalizeType(it); // maps STARTROUND/ENDROUND/DEFAULT/SLIDE -> "DISPLAY"

    if (tNorm !== "DISPLAY") count++;
  }
  return count;
}, [questions, current]);




// --- VIDEO + TTS SYNC (first screen) ---
const videoRef = React.useRef(null);
const [videoReady, setVideoReady] = React.useState(false);
const [ttsReady, setTtsReady] = React.useState(Boolean(window.aiSpeak));
const [spokenOnce, setSpokenOnce] = React.useState(false);

// Music Bed Volume State (read from localStorage, 0.0-1.0 range)
const [bedVolume, setBedVolumeState] = React.useState(() => {
  try {
    const key = `opt_bed_volume__${String(gameCode || "default")}`;
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const volume = parseFloat(stored);
      return Math.max(0, Math.min(1, isNaN(volume) ? 0.25 : volume));
    }
  } catch {}
  return 0.25; // Default to 0.25 (25%)
});

// Stable identity for the current question (doesn't change on shallow re-renders)
const bedQuestionKey = React.useMemo(() => {
  const id =
    q?.id ?? q?.ID ?? q?.uuid ?? q?.UUID ?? q?.key ?? q?.Q ?? q?.question ?? "";
  const t  = q?.time ?? q?.TIME ?? "";
  return `${id}|${t}|${current}`;
  // Depend on the fields, not the whole q object
}, [q?.id, q?.ID, q?.uuid, q?.UUID, q?.key, q?.Q, q?.question, q?.time, q?.TIME, current]);

const introDoneAtRef = React.useRef(0);

// BREADCRUMBS TTS: render path sets this; effect speaks newly revealed clues
const breadcrumbsCluesRef = React.useRef({ facts: [], revealedCount: 0, questionKey: "" });
const breadcrumbsLastSpokenRef = React.useRef(-1);
const breadcrumbsQuestionKeyRef = React.useRef(null);

// --- 10s cue system (one-time per question, StrictMode/HMR safe) ---
const [showTenOverlay, setShowTenOverlay] = React.useState(false);
const [tenOverlaySrc, setTenOverlaySrc]   = React.useState("");
// --- Quiz Introduction (one-time per quiz run) ---
const [showIntroOverlay, setShowIntroOverlay] = React.useState(false);
const [introOverlaySrc, setIntroOverlaySrc]   = React.useState("");


// Keep __mediaBusy in sync with overlay visibility
function useMediaBusy(showIntroOverlay, showMcqOverlay) {
  React.useEffect(() => {
    const busy = !!(showIntroOverlay || showMcqOverlay);
    window.__mediaBusy = busy;
    return () => {
      if (busy) window.__mediaBusy = false;
    };
  }, [showIntroOverlay, showMcqOverlay]);
}

// Call it after the overlay state hooks are defined
useMediaBusy(showIntroOverlay, showMcqOverlay);



// Global per-question fired set (survives remounts/HMR/StrictMode)
if (!window.__tenCueFiredByKey) window.__tenCueFiredByKey = new Set();

// One-shot guards scoped to this mount
const tenCueFiredRef  = React.useRef(false);
const lastTenCueAtRef = React.useRef(0);
const tenCueKeyRef    = React.useRef("");       // remembers which question we’re on
const prevTenRef      = React.useRef(Number(timer) || 0); // previous timer snapshot


// The “take next cue” helper (video first, then TTS fallbacks)
// Prefer Ollie’s character script; fall back to the legacy library
// Prefer Ollie’s character script; fall back to the legacy library
// The “take next cue” helper — Ollie only (skip blanks), with safe text fallback
// Generic: pull the next OLLIE cue and loop to the top if exhausted.
// The “take next cue” helper — category-scoped only (no global resets)
// === Ollie: what he knows about the moment ===
// Every answer in the game (team, q, points, correct), for the standings and score chatter.
const ollieAnswersRef = React.useRef([]);
React.useEffect(() => {
  if (!gameCode) return undefined;
  return onSnapshot(
    collection(db, "games", gameCode, "answers"),
    (snap) => {
      const rows = [];
      snap.forEach((d) => {
        const a = d.data() || {};
        rows.push({ team: a.team, q: a.q, points: a.points, correct: a.correct });
      });
      ollieAnswersRef.current = rows;
    },
    () => {}
  );
}, [gameCode]);

// Lines never repeat within a quiz: start each new game with a fresh script.
React.useEffect(() => {
  if (!gameCode || !promptMgrReady) return;
  if (window.__ollieGameCode === gameCode) return;
  window.__ollieGameCode = gameCode;
  try { promptMgrRef.current?.resetQuiz?.(); } catch {}
}, [gameCode, promptMgrReady]);

/** Total points per team over [from, to) (all teams that have joined count, at 0). */
const ollieTotals = (from = 0, to = Infinity, includeAllTeams = true) => {
  const totals = {};
  if (includeAllTeams) Object.keys(teamInfo || {}).forEach((t) => { if (t) totals[t] = 0; });
  (ollieAnswersRef.current || []).forEach((a) => {
    if (typeof a.q !== "number" || a.q < from || a.q >= to) return;
    if (typeof a.points !== "number" || Number.isNaN(a.points) || !a.team) return;
    totals[a.team] = (totals[a.team] || 0) + a.points;
  });
  return totals;
};

/** Facts about this moment that some of Ollie's lines depend on (their `needs`). */
const ollieFacts = () => {
  const facts = [];
  if (SPEED_BONUS_TYPES.has(String(q?.type || q?.CAT || "").trim().toUpperCase())) facts.push("speedBonus");
  try {
    if (getTeamRoundChoiceSettingsFromLocalStorage().enabled && findNextTeamChoiceStartIndex(questions, current) >= 0) {
      facts.push("teamChoiceNext");
    }
  } catch {}
  return facts;
};

/**
 * Values for the {PLACEHOLDERS} in Ollie's lines: the table as it stands, plus whatever the caller
 * knows about the moment. Blank values are left out, so lines that need them are skipped.
 */
const ollieContext = (extra = {}, standings = null) => {
  const s = standings || rankTeamsByPoints(ollieTotals());
  const c = {};
  // No leader or last place until somebody has actually scored.
  const scored = s.some((x) => x.points !== 0);
  if (scored && s[0]) { c["{LEADER}"] = s[0].team; c["{SCORE}"] = olliePointsLabel(s[0].points); }
  if (scored && s.length > 1) {
    c["{SECOND}"] = s[1].team;
    c["{GAP}"] = olliePointsLabel(s[0].points - s[1].points);
    c["{LAST}"] = s[s.length - 1].team;
  }
  const teamCount = Object.keys(teamInfo || {}).filter(Boolean).length;
  if (teamCount > 0) c["{TOTAL}"] = String(teamCount);
  const roundNum = (questions || []).slice(0, Math.max(0, current) + 1)
    .filter((x) => String(x?.type || x?.CAT || "").toUpperCase() === "STARTROUND").length;
  c["{ROUND}"] = String(Math.max(1, roundNum));
  if (displayQuestionNumber > 0) c["{QNUM}"] = String(displayQuestionNumber);
  const firstAnswer = Array.isArray(q?.answers) ? q.answers[0] : null;
  if (firstAnswer && normalizeType(q) !== "DISPLAY") {
    c["{ANSWER}"] = String(firstAnswer).replace(/^\s*\[(.*?)\]\s*$/, "$1");
  }
  for (const [k, v] of Object.entries(extra || {})) {
    if (v !== undefined && v !== null && String(v) !== "") c[k] = String(v);
  }
  return c;
};

/** Queues one of Ollie's spoken lines for `event`. Returns false when no line fits. */
const speakOllie = (event, { extra = {}, standings = null, delayMs = 0, repeats = true } = {}) => {
  const mgr = promptMgrRef.current;
  if (!mgr || !promptMgrReady) return false;
  let cue = null;
  try {
    cue = mgr.next(event, { allowVideo: false, context: ollieContext(extra, standings), facts: ollieFacts(), repeats });
  } catch {}
  if (!cue || cue.kind !== "tts" || !cue.text) return false;
  sayOllieLine(cue.text, delayMs);
  return true;
};

const nextCueLoop = React.useCallback((kind, opts = {}) => {
  const mgr = promptMgrRef.current;
  if (!mgr || !promptMgrReady) return null;
  try {
    // The manager starts the event's lines over by itself once they're used up (unless repeats: false).
    return mgr.next?.(kind, opts) || null;
  } catch {
    return null;
  }
}, [promptMgrReady]);

/**
 * What Ollie says as the type wheel lands: the type's name, with his intro to it the first time
 * it comes up in a round (intro lines that don't say the name get it in front). Nothing when the
 * type's intro video has just played for this question.
 */
const ollieTypeLine = (qq, slideKey) => {
  const type = normalizeType(qq);
  const name = getTypeDisplayName({ type });
  if (!name || name === "Question") return "";
  const titled = name.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase());
  let line = `${titled}!`;
  if (!window.__ollieTypesIntroduced) window.__ollieTypesIntroduced = {};
  const roundKey = `${gameCode}|${findRoundStartQuestionIndex(questions, current)}`;
  const introduced = window.__ollieTypesIntroduced[roundKey] || (window.__ollieTypesIntroduced[roundKey] = []);
  if (window.__typeIntroVideoKey === slideKey) {
    if (!introduced.includes(type)) introduced.push(type);
    return "";
  }
  const event = OLLIE_TYPE_INTRO_EVENTS[type];
  if (event && !introduced.includes(type)) {
    introduced.push(type);
    const cue = nextCueLoop(event, { allowVideo: false, context: ollieContext(), facts: ollieFacts() });
    if (cue?.kind === "tts" && cue.text) {
      const keyword = name.toLowerCase().split(" ")[0];
      line = cue.text.toLowerCase().includes(keyword) ? cue.text : `${line} ${cue.text}`;
    }
  }
  return line;
};




// The “take next cue” helper — uses OLLIE "ten_second" and loops when exhausted.
// NOTE: we do NOT skip blank lines anymore (you have an intentional blank in OLLIE.json).
const takeNextTenSecondCue = React.useCallback(() => {
  // A team that hasn't answered yet (real teams only) for the ten-second lines.
  const answered = new Set(
    (ollieAnswersRef.current || []).filter((a) => a.q === current).map((a) => a.team)
  );
  const waiting = Object.keys(teamInfo || {}).filter((t) => t && !answered.has(t) && !isSimulatorTestTeam(t));
  const cue = nextCueLoop("ten_second", {
    context: ollieContext({ "{UNANSWERED}": waiting[Math.floor(Math.random() * waiting.length)] || "" }),
    facts: ollieFacts(),
  });
  if (cue) return cue;

  // Safety fallback if OLLIE isn't ready or list is empty
  return { id: "ollie.ten.default", kind: "tts", text: TEN_SECOND_DEFAULT_TTS };
}, [nextCueLoop, current, teamInfo]);



// True while any blocking overlay (quiz_intro or mcq_intro or 10s) is visible
const overlaysUp = showIntroOverlay || showMcqOverlay || showTenOverlay;   // existing
const blockingOverlaysUp = showIntroOverlay || showMcqOverlay;             // NEW: 10s is NOT blocking



// When the MCQ intro video finishes, resolve the gate and *re-assert* the hero intro.
const handleMcqOverlayHidden = React.useCallback(() => {
  setShowMcqOverlay(false);
  setMcqOverlaySrc("");

  // Stamp overlay end and resolve the blocking promise
  window.__overlayActive    = false;
  window.__overlayEndedAtMs = performance.now();

  const r = overlayResolveRef.current;
  overlayResolveRef.current = null;
  if (typeof r === "function") r();
  window.__overlayPromise = null;

  // Make sure the big hero card is definitely visible now
  if (!isDisplay && !introDone) {
    setHeroIntro(true, "mcq-overlay-hidden");
    window.__heroShownAt = performance.now();

    heroShownAtRef.current = performance.now(); // restart the dwell from AFTER the video
  }
}, [isDisplay, introDone]);


/**
 * Plays an Ollie clip over the screen (with its own baked-in voice) and resolves when it has
 * finished, failed, or after `capMs` as a safety net. Nothing is spoken if it can't play.
 */
const playOllieVideo = React.useCallback((src, capMs = 12000) => new Promise((resolve) => {
  let settled = false;
  const done = () => {
    if (settled) return;
    settled = true;
    clearTimeout(cap);
    ollieOverlayDoneRef.current = null;
    setOllieOverlaySrc("");
    resolve();
  };
  const cap = setTimeout(done, capMs);
  ollieOverlayDoneRef.current = done;
  setOllieOverlaySrc(src);
}), []);

const handleOllieOverlayHidden = React.useCallback(() => {
  ollieOverlayDoneRef.current?.();
}, []);

// When the QUIZ INTRO video finishes, resolve the gate and ensure Hero starts fresh
const handleQuizIntroHidden = React.useCallback(() => {
  // hide overlay + clear its src
  setShowIntroOverlay(false);
  setIntroOverlaySrc("");

  // mark overlay end and resolve the blocking promise
  window.__overlayActive    = false;
  window.__overlayEndedAtMs = performance.now();

  const r = overlayResolveRef.current;
  overlayResolveRef.current = null;
  if (typeof r === "function") r();
  window.__overlayPromise = null;

  // IMPORTANT: For INTRODUCTION (treated as DISPLAY-like),
  // we *must* stamp the hero so useSlideTiming can dwell + auto-advance.
  // Always (re)assert the hero and stamp the moment it became visible.
  setHeroIntro(true, "quiz-intro-hidden");
  const tMono = performance.now();
  heroShownAtRef.current = tMono;
  window.__heroShownAt   = tMono; // ensures DISPLAY-like auto-advance has a baseline
}, []);






// Re-arm the 10s cue library once per quiz so the VIDEO is first again
// Prime guard only; we no longer use a local TEN_SECOND_CUE_LIBRARY queue
const tenCuePrimedRef = React.useRef(false);
React.useEffect(() => {
  if (!tenCuePrimedRef.current && displayQuestionNumber === 1 && current > 0 && !isDisplay) {
    tenCuePrimedRef.current = true;
  }
}, [displayQuestionNumber, current, isDisplay]);




// Reset the “intro video shown” flag whenever we return to the lobby
// Reset one-shot flags whenever we return to the lobby
// Reset one-shot and per-question guards when returning to lobby
React.useEffect(() => {
  if (current === 0) {
    window.__introVideoShown    = false;
    window.__mcqIntroShown      = false;   // harmless if unused
    window.__mcqCueFiredKeys    = new Set();
    window.__mcqBoundaryPending = null;
    prevWasMcqRef.current       = false;
  }
}, [current]);



// Play the Quiz Introduction video ONCE for the INTRODUCTION slide.
React.useEffect(() => {
  const isIntroSlide = String(q?.type || q?.CAT || "").toUpperCase() === "INTRODUCTION";
  if (isIntroSlide && !window.__introVideoShown) {
    window.__introVideoShown = true;

    // Start a blocking overlay gate
    window.__overlayStartedAtMs = performance.now();
    window.__overlayEndedAtMs   = 0;
    window.__overlayActive      = true;
    window.__overlayPromise = new Promise((resolve) => {
      overlayResolveRef.current = resolve;
      // A random quiz_intro video from OLLIE.json (see CharacterPromptManager.nextVideo)
      let introSrc = "/video/BeautifulNerds_FreeConvert.webm";
      try {
        const cue = promptMgrRef.current && promptMgrReady ? promptMgrRef.current.nextVideo?.("quiz_intro") : null;
        if (cue?.src) introSrc = cue.src;
      } catch {}
      setIntroOverlaySrc(introSrc);
      setShowIntroOverlay(true);
    });

    // optional: pause any one-off host tick right now
    window.__skipHostTickOnce = true;
  }
}, [current, q]);


// Ollie's question-type intro VIDEO: plays when the quiz moves onto a different question type
// (e.g. into Multiple Choice from anything else) and that type's event in OLLIE.json has a video
// not yet played this quiz. If an overlay is up, defer and fire as soon as overlays clear.
// prevWasMcqRef holds the previous question's type (false = none / lobby).
React.useEffect(() => {
  // Never in lobby or display mode
  if (isDisplay || current === 0) {
    prevWasMcqRef.current = false;
    return;
  }

  const thisType = normalizeType(q);
  const introEvent = OLLIE_TYPE_INTRO_EVENTS[thisType];
  const prevType = prevWasMcqRef.current;
  const key = bedQuestionKey;

  // Global guards
  if (!window.__mcqCueFiredKeys) window.__mcqCueFiredKeys = new Set();
  const overlaysUp = showIntroOverlay || showTenOverlay || showMcqOverlay;

  const boundaryIntoType = !!introEvent && thisType !== prevType;
  if (boundaryIntoType) {
    if (overlaysUp) {
      // Defer until overlays clear — DO NOT mark the type yet
      window.__mcqBoundaryPending = key;
      return;
    }
    if (!window.__mcqCueFiredKeys.has(key)) {
      // Only the intro VIDEO plays here; spoken intros come as the wheel lands.
      let cue = null;
      try {
        cue = promptMgrRef.current && promptMgrReady ? promptMgrRef.current.nextVideo?.(introEvent) : null;
      } catch {}

      if (cue) {
        window.__mcqCueFiredKeys.add(key);
        if (cue.kind === "video" && cue.src) {
          // The video is this question's type intro, so Ollie doesn't name the type as well.
          window.__typeIntroVideoKey = key;
          // Start a blocking promise that resolves when the overlay hides
          window.__overlayStartedAtMs = performance.now();
          window.__overlayEndedAtMs   = 0;
          window.__overlayActive      = true;

          window.__overlayPromise = new Promise((resolve) => {
            overlayResolveRef.current = resolve;
            setMcqOverlaySrc(cue.src);
            setShowMcqOverlay(true);
          });

          // optional: pause any one-off host tick right now
          window.__skipHostTickOnce = true;
        }
      }
    }
    prevWasMcqRef.current = thisType;
    return;
  }

  // Clear pending boundary once overlays finish
  if (!overlaysUp && window.__mcqBoundaryPending === key) {
    window.__mcqBoundaryPending = null;
  }

  // Track the last slide's type for the next boundary (a display slide counts, as before)
  prevWasMcqRef.current = thisType;
}, [
  isDisplay, current, q, bedQuestionKey,
  showIntroOverlay, showTenOverlay, showMcqOverlay,
  promptMgrReady
]);




// Reset the per-question local guard whenever the question changes
React.useEffect(() => {
  tenCueFiredRef.current = false;
}, [current, q?.id]);

// --- Fire EXACTLY ONCE when we cross the boundary (>10 → <=10) ---
// Make this behave IDENTICALLY to intro + MCQ overlays (blocking w/ gate).
// --- Fire EXACTLY ONCE when we cross the boundary (>10 → <=10) ---
// 10s video is NON-BLOCKING: do not start the overlay gate/promise.
// --- Fire EXACTLY ONCE when we cross the boundary (>10 → <=10) ---
// 10s video is NON-BLOCKING: do not start the overlay gate/promise.
// DISABLED for TRUE/FALSE questions.
React.useEffect(() => {
  // Never run for display-like slides (includes INTRODUCTION, END QUIZ etc)
  if (isDisplay) return;
  
  // Also explicitly check for these types to ensure they're blocked
  // Use q prop first, fallback to questions[current] for robustness
  const currentQ = q || (Array.isArray(questions) && questions[current]) || {};
  const rawType = String(currentQ?.type || currentQ?.CAT || "").trim().toUpperCase();
  const normalizedType = normalizeType(currentQ);
  // Check both raw type and normalized type to catch all cases
  const isBreakOrRound = 
    ["BREAK", "BREAK TIME", "STARTROUND", "ENDROUND", "INTRODUCTION", "DISPLAY", "SHOWROUNDS"].includes(rawType) ||
    normalizedType === "DISPLAY";
  if (isBreakOrRound) return;

  // Re-key per question so we can track the previous tick
  if (tenCueKeyRef.current !== bedQuestionKey) {
    tenCueKeyRef.current = bedQuestionKey;
    prevTenRef.current   = Number.isFinite(timer) ? Number(timer) : 0;
  }

  const tNow  = Number(timer) || 0;
  const tPrev = Number(prevTenRef.current) || 0;
  prevTenRef.current = tNow;

  const crossedIntoWindow = tPrev > 10 && tNow > 0 && tNow <= 10;
  if (!crossedIntoWindow) return;

  // Global once-per-question guard
  const globalKey = `tenCue|${bedQuestionKey}`;
  if (window.__tenCueFiredByKey.has(globalKey)) return;
  window.__tenCueFiredByKey.add(globalKey);

  // Local once guard (StrictMode-safe)
  if (tenCueFiredRef.current) return;
  tenCueFiredRef.current = true;

  // Skip if a blocking overlay is visible
  if (showIntroOverlay || showMcqOverlay) return;

  // Briefly pause HostAgent so it doesn't speak over the cue
  window.__skipHostTickOnce = true;

  // Pull next Ollie cue; show video or speak TTS
  const cue = takeNextTenSecondCue();
  if (cue?.kind === "video" && cue.src) {
    setTenOverlaySrc(cue.src);
    setShowTenOverlay(true);
  } else if (cue?.kind === "tts" && cue.text) {
    try { window.aiSpeak?.(cue.text); } catch {}
  }
}, [
  q,
  timer,
  isDisplay,
  current,
  bedQuestionKey,
  showIntroOverlay,
  showMcqOverlay,
  takeNextTenSecondCue,
  questions
]);

// BREADCRUMBS: speak each clue via TTS when it is revealed (ref set by render path)
React.useEffect(() => {
  if (!q || normalizeType(q) !== "BREADCRUMBS") {
    breadcrumbsLastSpokenRef.current = -1;
    breadcrumbsQuestionKeyRef.current = null;
    return;
  }
  if (localStorage.getItem("opt_tts") === "Off") return;

  const { facts, revealedCount, questionKey } = breadcrumbsCluesRef.current;
  if (questionKey !== breadcrumbsQuestionKeyRef.current) {
    breadcrumbsQuestionKeyRef.current = questionKey;
    breadcrumbsLastSpokenRef.current = -1;
  }
  if (revealedCount <= breadcrumbsLastSpokenRef.current || !Array.isArray(facts) || facts.length === 0) return;

  const nextIndex = breadcrumbsLastSpokenRef.current + 1;
  if (nextIndex >= facts.length || nextIndex >= revealedCount) return;
  const text = String(facts[nextIndex] ?? "").trim();
  if (!text) return;

  breadcrumbsLastSpokenRef.current = nextIndex;
  try {
    window.aiSpeak?.(text);
  } catch (e) {
    if (typeof console !== "undefined" && console.warn) console.warn("[BREADCRUMBS TTS]", e);
  }
}, [q, timer, bedQuestionKey]);

// Hide overlay callback (keeps your existing behavior)
// Hide overlay callback — mirror quiz/MCQ overlay cleanup exactly
const handleTenOverlayHidden = React.useCallback(() => {
  setShowTenOverlay(false);
  setTenOverlaySrc("");

  
}, []);









React.useEffect(() => {
  if (!introDone) return;
  const t = Date.now();
  introDoneAtRef.current = t;
  // expose once, exactly when intro finishes
  window.__introDoneAt = t;
}, [introDone]);


// Start the background bed AFTER the narrator and any per-question audio have finished.
// If q.SOUND_URL exists, we wait for that to finish too. Otherwise we start after TTS silence.
// We detect "silence" by checking all <audio> elements except our own bed, plus Web Speech speaking state.
// Start the background bed AFTER the narrator and any per-question audio have finished.
// If q.SOUND_URL exists, we wait for that to finish too. Otherwise we start after TTS silence.
// We detect "silence" by checking all <audio> elements except our own bed, plus Web Speech speaking state.
React.useEffect(() => {
  const bed = bgBedRef.current;
  if (!bed) return;
  if (current === 0) return; // never in the lobby

  // Build a stable key for this question
  const qKey =
    (q?.id ?? q?.ID ?? q?.uuid ?? q?.UUID ?? q?.key ?? q?.Q ?? `Q${current}`) + "";

  // If already started for this question, do nothing
  if (bgBedKeyRef.current === qKey) return;

  const hasSoundUrl = Boolean(q?.SOUND || q?.SOUND_URL || q?.SPOTIFY_URL || q?.sound);
  const audioEl = audioRef?.current || null; // your per-question audio element (SOUND_URL)
  
  // Debug logging for background bed logic
  if (hasSoundUrl) {
    console.log('[Background Bed] Has sound URL, checking:', {
      SOUND: q?.SOUND,
      SOUND_URL: q?.SOUND_URL,
      SPOTIFY_URL: q?.SPOTIFY_URL,
      sound: q?.sound,
      audioEl: audioEl ? 'exists' : 'null'
    });
  }

  // Is any non-bed audio currently playing (including Cloud TTS audio element)?
  const othersPlaying = () => {
    // 1) Any <audio> except our bed still playing?
    const audios = Array.from(document.querySelectorAll("audio"));
    for (const el of audios) {
      if (el === bed) continue;
      // If a SOUND_URL exists and its element hasn't started yet, give it a chance first
      if (hasSoundUrl && el === audioEl && !el.ended && el.currentTime === 0) return true;
      if (!el.paused && !el.ended && el.currentTime > 0) return true;
    }
    // 2) Browser WebSpeech speaking (fallback)
    try {
      if (window.speechSynthesis && window.speechSynthesis.speaking) return true;
    } catch {}
    return false;
  };

  // If the question includes a SOUND_URL, wait until the intro phase flips before starting to poll
  if (hasSoundUrl && !introDone) return;

  let silentMs = 0;
  const stepMs = 200;

  const tick = () => {
    // The question's sound clip has played: the bed stays off for this question.
    if (questionSoundPlayedRef.current === current) {
      if (bgIdlePollRef.current) { clearInterval(bgIdlePollRef.current); bgIdlePollRef.current = null; }
      return;
    }
    if (othersPlaying()) { silentMs = 0; return; }
    silentMs += stepMs;
    if (silentMs >= 600) {
      // Start the bed now, mark this question as started
      bgBedKeyRef.current = qKey;
      try {
        bed.currentTime = 0;
        bed.volume = bedVolume; // Use volume from localStorage
        const p = bed.play();
        if (p && typeof p.catch === "function") {
          // iOS/autoplay fallback
          p.catch(() => {
            bed.muted = true;
            bed.play().then(() => { setTimeout(() => { bed.muted = false; }, 120); })
              .catch(() => {});
          });
        }
      } catch {}
      // Stop polling once started
      if (bgIdlePollRef.current) { clearInterval(bgIdlePollRef.current); bgIdlePollRef.current = null; }
    }
  };

  // (Re)start polling
  if (bgIdlePollRef.current) clearInterval(bgIdlePollRef.current);
  bgIdlePollRef.current = setInterval(tick, stepMs);

  // Cleanup
  return () => {
    if (bgIdlePollRef.current) { clearInterval(bgIdlePollRef.current); bgIdlePollRef.current = null; }
  };
}, [q, current, introDone]);

// Pause the bed the instant time hits 0 (don’t bleed into next screens)
React.useEffect(() => {
  const bed = bgBedRef.current;
  if (!bed) return;
  if ((Number(timer) || 0) <= 0) {
    try { bed.pause(); } catch {}
  }
}, [timer]);

// Reset the bed (and polling state) when the question changes
React.useEffect(() => {
  const bed = bgBedRef.current;
  if (bed) {
    try { bed.pause(); bed.currentTime = 0; } catch {}
  }
  bgBedKeyRef.current = null;
  if (bgIdlePollRef.current) { clearInterval(bgIdlePollRef.current); bgIdlePollRef.current = null; }
}, [q, current]);


function parseStartTimeToday(hhmm) {
  if (!hhmm) return 0;
  const [hh, mm] = String(hhmm).split(":").map(n => parseInt(n, 10));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return 0;
  const d = new Date();
  d.setHours(hh, mm, 0, 0);
  return d.getTime();
}


// Play the vanish SFX only when NEW items enter justVanished
React.useEffect(() => {
  if (!vanishSfxRef.current) return;

  // snapshot current & previous
  const curr = Array.isArray(justVanished) ? [...justVanished] : [];
  const prev = prevJVRef.current || [];

  // detect newly vanished indexes
  const newlyVanished = curr.filter(i => !prev.includes(i));
  prevJVRef.current = curr;

  if (newlyVanished.length === 0) return; // nothing new → no sound

  const a = vanishSfxRef.current;
  try {
    a.currentTime = 0;
    a.playbackRate = 0.95 + Math.random() * 0.10;
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        // autoplay fallback
        a.muted = true;
        a.play().then(() => { setTimeout(() => { a.muted = false; }, 120); }).catch(() => {});
      });
    }
  } catch {}
// include timer so this runs even if justVanished is mutated in place
}, [justVanished, timer]);

// Fade ONLY the per-question AUDIO (q.SOUND_URL) in the last 10s; hard-cut at 0
React.useEffect(() => {
  const el = audioRef.current;
  if (!el) return;

  const remaining = Number(timer) || 0;

  // remember the base volume once (so we can restore it next question)
  if (!el.dataset.baseVolume) {
    try { el.dataset.baseVolume = String(el.volume || 1); } catch {}
  }
  const base = Math.max(0, Math.min(1, parseFloat(el.dataset.baseVolume || "1")));

  // linear fade from 10s → 0s; >10s stays at base
  const factor = remaining <= 0 ? 0 : remaining >= 10 ? 1 : remaining / 10;
  try { el.volume = base * factor; } catch {}

  // hard cut right at 0 so nothing leaks into the next screen
  if (remaining <= 0) {
    try { el.pause(); } catch {}
    try { el.currentTime = 0; } catch {}
    // restore base volume for the next question cycle
    try { el.volume = base; } catch {}
  }
}, [timer]);



// Detect when TTS is ready (AIHostTTSPlayer registers window.aiSpeak)
React.useEffect(() => {
  if (ttsReady) return;
  let tries = 0;
  const id = setInterval(() => {
    if (typeof window.aiSpeak === "function") {
      setTtsReady(true);
      clearInterval(id);
    } else if (++tries > 200) {
      // stop after ~10s
      clearInterval(id);
    }
  }, 50);
  return () => clearInterval(id);
}, [ttsReady]);

// Music Bed Volume: Read from localStorage and listen for changes
React.useEffect(() => {
  const key = `opt_bed_volume__${String(gameCode || "default")}`;
  
  const updateBedVolume = () => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        const volume = parseFloat(stored);
        const clampedVolume = Math.max(0, Math.min(1, isNaN(volume) ? 0.25 : volume));
        setBedVolumeState(clampedVolume);
      } else {
        setBedVolumeState(0.25); // Default to 0.25 (25%)
      }
    } catch {}
  };

  // Initial read
  updateBedVolume();

  // Listen for storage events (when changed in another tab/window)
  const handleStorageChange = (e) => {
    if (e.key === key) {
      updateBedVolume();
    }
  };
  window.addEventListener("storage", handleStorageChange);

  // Poll localStorage periodically for same-tab updates (storage event doesn't fire in same tab)
  const pollInterval = setInterval(updateBedVolume, 500);

  return () => {
    window.removeEventListener("storage", handleStorageChange);
    clearInterval(pollInterval);
  };
}, [gameCode]);

// Mark video as ready when it can play
React.useEffect(() => {
  const v = videoRef.current;
  if (!v) return;
  const onCanPlay = () => setVideoReady(true);
  const onLoadedData = () => {
    if (v.readyState >= 3) setVideoReady(true); // HAVE_FUTURE_DATA
  };
  v.addEventListener("canplay", onCanPlay);
  v.addEventListener("loadeddata", onLoadedData);
  return () => {
    v.removeEventListener("canplay", onCanPlay);
    v.removeEventListener("loadeddata", onLoadedData);
  };
}, []);

// HostAgent timer tick — skip once when our 10s cue wants to speak instead
React.useEffect(() => {
  if (current === 0) return; // not in lobby
  const t = Number(timer) || 0;
  if (t <= 5) return;        // don't narrate during the final 5s

  // NEW: one-shot gate set by the 10-second cue effect
  if (window.__skipHostTickOnce) {
    window.__skipHostTickOnce = false; // consume the gate
    return;                            // skip HostAgent's default line this tick
  }

  // Skip the 10s TTS for BREAK, STARTROUND, ENDROUND, INTRODUCTION, DISPLAY, and SHOWROUNDS question types
  // Use q prop first, fallback to questions[current] for robustness
  const currentQ = q || (Array.isArray(questions) && questions[current]) || {};
  const rawType = String(currentQ?.type || currentQ?.CAT || "").trim().toUpperCase();
  const normalizedType = normalizeType(currentQ);
  // Check both raw type and normalized type to catch all cases
  const isBreakOrRound = 
    ["BREAK", "BREAK TIME", "STARTROUND", "ENDROUND", "INTRODUCTION", "DISPLAY", "SHOWROUNDS"].includes(rawType) ||
    normalizedType === "DISPLAY";
  if (isBreakOrRound && t === 10) {
    // Skip the 10s TTS for these types
    return;
  }

  hostRef.current?.onTimerTick({ remaining: t });
}, [timer, current, q, questions]);











React.useEffect(() => { hostRef.current = new HostAgent(); }, []);


  // Construct Spotify preview URL when Spotify URL is available and no MP3
  React.useEffect(() => {
    const soundUrl = q?.SOUND || q?.SOUND_URL || q?.sound;
    const spotifyUrl = q?.SPOTIFY_URL;
    const isSpotify = spotifyUrl && spotifyUrl.includes('open.spotify.com/embed');
    
    if (isSpotify && !soundUrl && !spotifyPreviewUrl) {
      const trackId = extractSpotifyTrackId(spotifyUrl);
      if (trackId) {
        const previewUrl = getSpotifyPreviewUrl(trackId);
        audioLog('log', '[Audio Playback] Constructed Spotify preview URL:', previewUrl);
        setSpotifyPreviewUrl(previewUrl);
      } else {
        setSpotifyPreviewUrl(null);
      }
    } else if (!isSpotify || soundUrl) {
      // Clear preview URL if conditions change
      setSpotifyPreviewUrl(null);
    }
  }, [q?.SOUND, q?.SOUND_URL, q?.SPOTIFY_URL, q?.sound, spotifyPreviewUrl]);

  React.useEffect(() => {
    if (!introDone) {
      audioLog('log', '[Audio Playback] Intro not done yet, skipping autoplay');
      return;
    }
    const el = audioRef.current;
    if (!el) {
      audioLog('log', '[Audio Playback] Audio element ref not available');
      return;
    }
    audioLog('log', '[Audio Playback] Attempting to play audio after intro:', {
      src: el.src,
      readyState: el.readyState,
      paused: el.paused,
      muted: el.muted
    });
    const id = setTimeout(() => {
      const playPromise = el.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            audioLog('log', '[Audio Playback] Audio play() succeeded');
          })
          .catch((err) => {
            audioLog('warn', '[Audio Playback] Audio play() failed, trying muted:', err);
            el.muted = true;
            el.play()
              .then(() => {
                audioLog('log', '[Audio Playback] Audio play() succeeded after muting');
                setTimeout(() => {
                  el.muted = false;
                  audioLog('log', '[Audio Playback] Audio unmuted');
                }, 150);
              })
              .catch((err2) => {
                audioLog('error', '[Audio Playback] Audio play() failed even after muting:', err2);
              });
          });
      }
    }, 150);
    return () => clearTimeout(id);
  }, [introDone]);


// === Background preload cache with budgets and concurrency ===
const BG_MAX_DECODED_BYTES = 120 * 1024 * 1024; // ~120 MiB total decoded budget
const BG_MAX_ITEMS = 12;                         // hard cap on number of cached backgrounds
const BG_MAX_CONCURRENCY = 3;                    // how many to fetch/decode at once

// LRU map: url -> { promise, width, height, bytes }
const bgEntries = (typeof window !== "undefined"
  ? (window.__bgEntries ||= new Map())
  : new Map());








function lruTouch(url) {
  const v = bgEntries.get(url);
  if (!v) return;
  bgEntries.delete(url);
  bgEntries.set(url, v); // move to end (most recent)
}

function lruEvictIfNeeded() {
  let total = 0;
  for (const [, v] of bgEntries) total += v.bytes || 0;

  while ((bgEntries.size > BG_MAX_ITEMS || total > BG_MAX_DECODED_BYTES) && bgEntries.size) {
    const [oldUrl, oldVal] = bgEntries.entries().next().value;
    total -= oldVal.bytes || 0;
    bgEntries.delete(oldUrl);
  }
}



// Decode-aware preloader that respects LRU budgets
function preloadBackground(url) {
  if (!url) return Promise.resolve();
  const existing = bgEntries.get(url);
  if (existing?.promise) { lruTouch(url); return existing.promise; }

  // Create once, store immediately to dedupe
  const img = new Image();
  img.src = url;

  const p = (img.decode ? img.decode() : new Promise(res => {
    img.onload = res; img.onerror = res; // fail-open to avoid blocking
  })).then(() => {
    const w = img.naturalWidth || 0;
    const h = img.naturalHeight || 0;
    const bytes = w * h * 4; // RGBA in memory
    bgEntries.set(url, { promise: p, width: w, height: h, bytes });
    lruTouch(url);
    lruEvictIfNeeded();
    return url;
  }).catch(() => url); // non-fatal

  bgEntries.set(url, { promise: p, width: 0, height: 0, bytes: 0 });
  return p;
}

// Preload a list with small parallelism
function preloadBackgroundsLimited(urls) {
  return new Promise(resolve => {
    const queue = urls.slice();
    let active = 0;
    let done = 0;

    const next = () => {
      if (done === urls.length) return resolve();
      while (active < BG_MAX_CONCURRENCY && queue.length) {
        const u = queue.shift();
        active++;
        ensurePreloadLink(u);
        preloadBackground(u).finally(() => {
          active--; done++; next();
        });
      }
    };
    next();
  });
}

// --- Speak the correct answer when timer hits 0 ---
React.useEffect(() => {
  // Use the *existing* ref from the `endPingRef` effect
  const justHitZero = prevTimerRef.current > 0 && timer <= 0;

  if (justHitZero) {
    const speakAnswerAtEnd = async () => {
      // 1. Don't speak for display slides
      if (isDisplay) return;

      // 2. Get intro line from "Ollie" based on question type
      let introLine = "The answer was"; // Default fallback
      const normType = normalizeType(q); // <-- Get type *before* try/catch

      try {
        if (promptMgrRef.current && promptMgrReady) {
          // Determine category based on question type
          const category = (normType === "MULTIANSWER") 
                           ? "end_question_MULTIPLEANSWERS" 
                           : "end_question_SINGLEANSWER";
          
          // A team that got it right / wrong (where the phones have already judged it).
          const rows = (ollieAnswersRef.current || []).filter((a) => a.q === current);
          const right = rows.filter((a) => a.correct === true).map((a) => a.team);
          const wrong = rows.filter((a) => a.correct === false).map((a) => a.team);
          const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)] || "";
          const cue = nextCueLoop(category, {
            allowVideo: false,
            context: ollieContext({ "{CORRECT_TEAM}": pickOne(right), "{WRONG_TEAM}": pickOne(wrong) }),
            facts: ollieFacts(),
          });
          
          if (cue?.kind === "tts" && cue.text) {
            introLine = cue.text;
          }
        }
      } catch (e) {
        console.error("Failed to get end_question cue:", e);
      }

      // 3. Get the correct answer text based on question type
      let correctAnswerText = "";
      // const normType = normalizeType(q); // <-- This is no longer needed here
      const answers = Array.isArray(q?.answers) ? q.answers : [];

      if (normType === "MULTIANSWER") { // <-- Reuse normType from above
        const correctAnswers = answers
          .map(a => String(a).match(/^\s*\[(.*?)\]\s*$/)) // Find answers in [brackets]
          .filter(Boolean)
          .map(match => match[1]); // Get the text inside the brackets

        if (correctAnswers.length > 1) {
          correctAnswerText = `The correct answers were: ${correctAnswers.join(', and ')}`;
          introLine = ""; // Use this full line instead of the generic intro
        } else if (correctAnswers.length === 1) {
          correctAnswerText = correctAnswers[0];
          // Keep the introLine (e.g., "The answer I was looking for was...")
        }
        
      } else if (normType === "OUTOFORDER") {
        const correctOrder = answers.join(', ');
        correctAnswerText = `The correct order was: ${correctOrder}`;
        introLine = ""; // Use this full line instead

      } else if (answers.length > 0) {
        // This covers MULTICHOICE, TRUE/FALSE, WHEEL, TYPE, etc.
        correctAnswerText = String(answers[0]).replace(/^\s*\[(.*?)\]\s*$/, "$1");
      }

      if (!correctAnswerText) {
        // Even if no answer text, check for visible explanation
        const visibleExplanation = q?.visibleExplanation?.trim();
        if (visibleExplanation) {
          await speakAndWait(visibleExplanation, { delayMs: 500 });
        }
        return;
      }

      // 4. Combine and speak
      const fullLine = `${introLine} ${correctAnswerText}`;
      
      // Delay slightly (500ms) to avoid collision with the "ping" sound
      await speakAndWait(fullLine.trim(), { delayMs: 500 });

      // 5. After answer TTS finishes, read visible explanation if it exists
      const visibleExplanation = q?.visibleExplanation?.trim();
      if (visibleExplanation) {
        await speakAndWait(visibleExplanation, { delayMs: 300 });
      }
    };

    // Ollie's score chatter waits for the answer (and explanation) to be read first.
    window.__answerSpeechPromise = speakAnswerAtEnd().catch(() => {});
  }

  // We don't need to update prevTimerRef.current here,
  // because the `endPingRef` effect already does it.

}, [
    timer,          // The main trigger
    isDisplay, 
    q
]);



// Which slide is up, for Ollie's delayed chatter to notice it has gone stale.
React.useEffect(() => { window.__ollieCurrent = current; }, [current]);

// After scoring: nobody / everybody got it, or a line about the scores with the top team.
// Each line once per quiz: when an event's lines are used up he moves to the general score lines,
// and once those are gone too he lets the scores speak for themselves.
const chatterPrevTimerRef = React.useRef(Number(timer) || 0);
React.useEffect(() => {
  const prev = chatterPrevTimerRef.current;
  chatterPrevTimerRef.current = Number(timer) || 0;
  if (!(prev > 0 && timer <= 0) || isDisplay) return;
  if (String(q?.type || q?.CAT || "").trim().toUpperCase() === "JACKPOT") return;
  const qIdx = current;
  const key = `${gameCode}|${qIdx}`;
  if (!window.__ollieChatterKeys) window.__ollieChatterKeys = new Set();
  if (window.__ollieChatterKeys.has(key)) return;
  window.__ollieChatterKeys.add(key);
  const stale = () => window.__ollieCurrent !== qIdx;
  const pause = (ms) => new Promise((r) => setTimeout(r, ms));
  const scoredRows = () =>
    (ollieAnswersRef.current || []).filter((a) => a.q === qIdx && typeof a.points === "number" && !Number.isNaN(a.points));
  (async () => {
    // Scoring runs ~4 s after time-up; wait for the scored answers to arrive.
    const until = Date.now() + 15000;
    while (!scoredRows().length && Date.now() < until && !stale()) await pause(250);
    if (stale() || !scoredRows().length) return;
    await pause(600); // let the rest of the scoring batch land
    try { await window.__answerSpeechPromise; } catch {}
    if (stale()) return;
    const rows = scoredRows();
    const teamCount = Object.keys(teamInfo || {}).filter(Boolean).length;
    if (!rows.length || teamCount === 0) return;
    const isRight = (a) => (a.points || 0) > 0 || a.correct === true;
    const right = rows.filter(isRight);
    const wrong = rows.filter((a) => !isRight(a));
    const best = Math.max(...rows.map((a) => a.points || 0));
    const tops = rows.filter((a) => (a.points || 0) === best);
    const pickTeam = (arr) => (arr[Math.floor(Math.random() * arr.length)] || {}).team || "";
    const extra = {
      "{COUNT}": String(right.length),
      "{CORRECT_TEAM}": pickTeam(right),
      "{WRONG_TEAM}": pickTeam(wrong),
    };
    // "{TEAM} picked up the top points" only when one team really did.
    if (best > 0 && tops.length === 1) {
      extra["{TEAM}"] = tops[0].team;
      extra["{SCORE}"] = olliePointsLabel(best);
    }
    const event = right.length === 0 ? "nobody_correct" : right.length >= teamCount ? "everyone_correct" : "question_scores";
    if (!speakOllie(event, { extra, delayMs: 1000, repeats: false }) && event !== "question_scores") {
      speakOllie("question_scores", { extra, delayMs: 1000, repeats: false });
    }
  })();
}, [timer, isDisplay, q, current]);

// End of round: a line on the round's scores, then on the overall table (a comeback, a tight
// race, or a runaway leader). The scores stay up until he has finished (RoundScores onClose).
React.useEffect(() => {
  if (!showRoundScoresPopup || !gameCode) return;
  const endIdx = current;
  const key = `${gameCode}|${endIdx}`;
  if (!window.__ollieRoundChatterKeys) window.__ollieRoundChatterKeys = new Set();
  if (window.__ollieRoundChatterKeys.has(key)) return;
  window.__ollieRoundChatterKeys.add(key);
  const stale = () => window.__ollieCurrent !== endIdx;
  (async () => {
    // After the end-of-round line has started.
    await new Promise((r) => setTimeout(r, 1500));
    await waitForOllieSpeech(10000, stale);
    if (stale()) return;
    const start = findRoundStartQuestionIndex(questions, endIdx);
    const round = rankTeamsByPoints(ollieTotals(start, endIdx, false));
    if (round[0]) {
      const extra = { "{LEADER}": round[0].team, "{SCORE}": olliePointsLabel(round[0].points) };
      if (round.length > 1) {
        extra["{LAST}"] = round[round.length - 1].team;
        extra["{SECOND}"] = round[1].team;
      }
      speakOllie("round_scores", { extra });
    }
    const overall = rankTeamsByPoints(ollieTotals());
    const places = {};
    overall.forEach((row, i) => {
      places[row.team] = i > 0 && overall[i - 1].points === row.points ? places[overall[i - 1].team] : i + 1;
    });
    if (!window.__ollieLastRoundPlaces) window.__ollieLastRoundPlaces = {};
    const lastPlaces = window.__ollieLastRoundPlaces[gameCode] || {};
    const climber = overall.find((row) => (lastPlaces[row.team] ?? places[row.team]) - places[row.team] >= 2);
    if (climber) {
      speakOllie("comeback", { extra: { "{TEAM}": climber.team } });
    } else if (overall.length > 1) {
      const gap = overall[0].points - overall[1].points;
      if (gap <= 10) speakOllie("close_race");
      else if (gap >= 40) speakOllie("runaway_leader");
    }
    window.__ollieLastRoundPlaces[gameCode] = places;
  })();
}, [showRoundScoresPopup, current, gameCode]);

React.useEffect(() => {
  // Fire only on the moment we cross from >0 to <=0
  if (prevTimerRef.current > 0 && timer <= 0) {
    const el = endPingRef.current;
    if (el) {
      el.currentTime = 0;
      el.play().catch(() => {
        // iOS/Safari fallback: briefly mute to satisfy autoplay, then unmute
        el.muted = true;
        el.play().then(() => {
          setTimeout(() => { el.muted = false; }, 120);
        }).catch(() => {});
      });
    }
  }
  prevTimerRef.current = timer;
}, [timer]);


// Preload ALL distinct backgrounds once, with budgets and small concurrency
React.useEffect(() => {
  const urls = Array.from(
    new Set((questions || [])
      .map(x => x?.BACKGROUND_URL || x?.background)
      .filter(Boolean))
  );
  
  // ✅ FIX: Keep track of added links
  const addedLinks = [];
  
  const preloadWithTracking = (url) => {
    const link = ensurePreloadLink(url); // This now returns the link
    if (link) addedLinks.push(link);
    return preloadBackground(url); // Your existing function
  };
  
  // A modified version of your preloadBackgroundsLimited
  const preloadTracked = (urlsToLoad) => {
    return new Promise(resolve => {
      const queue = urlsToLoad.slice();
      let active = 0;
      let done = 0;

      const next = () => {
        if (done === urlsToLoad.length) return resolve();
        while (active < BG_MAX_CONCURRENCY && queue.length) {
          const u = queue.shift();
          active++;
          // Use our new tracking preloader
          preloadWithTracking(u).finally(() => {
            active--; done++; next();
          });
        }
      };
      next();
    });
  };

  if (urls.length) {
    preloadTracked(urls);
  }

  // ✅ FIX: RETURN THE CLEANUP FUNCTION
  return () => {
    addedLinks.forEach(link => {
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    });
    // You might also want to clear your global JS cache here
    // if you don't want it to persist between quizzes.
    // if (window.__bgEntries) window.__bgEntries.clear();
  };
}, [questions]);




  // Countdown overlay
// Countdown overlay (hidden for DISPLAY/STARTROUND/ENDROUND)
React.useEffect(() => {
  if (isDisplay) return;
  if (q.time > 0 && timer <= 5 && timer >= 0) {
    if (timer > 0) setCountdownOverlay(`${timer}`);
    const timeout = setTimeout(() => setCountdownOverlay(null), 900);
    return () => clearTimeout(timeout);
  }
}, [timer, q.time, isDisplay]);


  // Helper: compute a responsive hero font size that fits long strings
  const computeHeroFontSize = (text) => {
    const len = (text || "").length;
    // map length bands to vw; clamped by vh to avoid overflow on very wide screens
    let vw;
    if (len > 220) vw = 3.2;
    else if (len > 170) vw = 3.6;
    else if (len > 130) vw = 4.2;
    else if (len > 100) vw = 4.8;
    else if (len > 70)  vw = 5.6;
    else if (len > 45)  vw = 6.6;
    else                vw = 7.6;
    return `clamp(18px, ${vw}vw, 10.5vh)`;
  };





// On question change: clear narration, stop per-question media, and RESTART the music bed for the new slide
React.useEffect(() => {
  // Drop everything Ollie had queued or was saying for the previous slide
  cancelOllieSpeech();

  try {
    document.querySelectorAll('video, audio').forEach(media => {
      const isBed = media.tagName === 'AUDIO' && media.getAttribute('data-bed') === '1';
      if (isBed) {
        // Restart music bed for the new slide (builds suspense each question)
        media.currentTime = 0;
        media.volume = Math.max(0, Math.min(1, bedVolume));
        media.play().catch(() => {});
      } else {
        // Stop all other video and audio
        if (!media.paused) media.pause();
        media.currentTime = 0;
      }
    });
  } catch {}

  // Reset last spoken question when current changes
  lastSpokenQuestionRef.current = { current: -1, questionId: "" };

}, [current, bedVolume]);


// Trigger intro — keep Hero up for DISPLAY/STARTROUND/ENDROUND; retract only for real questions
// Trigger intro — keep Hero up for DISPLAY/STARTROUND/ENDROUND; retract only for real questions
const heroNameCandidates = React.useMemo(() => ([
  q?.question,
  q?.display,
  q?.title,
  q?.text,
  q?.defaultText,
  q?.prompt,
  q?.slideText,
  q?.ROUNDNAME,
  q?.RoundName,
  q?.roundName,
  q?.ROUND_TITLE,
  q?.RoundTitle,
  q?.roundTitle,
  q?.Q,
]), [
  q?.question,
  q?.display,
  q?.title,
  q?.text,
  q?.defaultText,
  q?.prompt,
  q?.slideText,
  q?.ROUNDNAME,
  q?.RoundName,
  q?.roundName,
  q?.ROUND_TITLE,
  q?.RoundTitle,
  q?.roundTitle,
  q?.Q,
]);

const heroSourceTextRaw = React.useMemo(() => {
  const picked = heroNameCandidates.find((v) => {
    if (typeof v !== "string") return false;
    return v.trim().length > 0;
  });
  return typeof picked === "string" ? picked : "";
}, [heroNameCandidates]);

const heroSourceText = React.useMemo(() => String(heroSourceTextRaw ?? ""), [heroSourceTextRaw]);

React.useEffect(() => {
  // Guard: Don't run TTS if question is still a placeholder or not fully loaded
  // This prevents multiple TTS calls during placeholder replacement
  const trimmedText = heroSourceText.trim();

  // Check for various placeholder patterns: {{...}}, #CATEGORY, #Category, etc.
  const isPlaceholder =
    (trimmedText.startsWith("{{") && trimmedText.endsWith("}}")) ||
    (trimmedText.startsWith("#") && trimmedText.length < 50); // #Sport, #SPORT, etc. (short placeholder-like patterns)

  // For TTS: extract text from brackets if needed (for STARTROUND/ENDROUND/DISPLAY)
  const textForTTS = isTextInBrackets(trimmedText) 
    ? extractTextFromBrackets(trimmedText)
    : trimmedText;
  const cleaned = cleanQuestionText(textForTTS);
  const cleanedHasText = cleaned.trim().length > 0;
  const shouldSpeak = cleanedHasText && !isPlaceholder;

  if (!cleanedHasText) {
    console.log("[TTS] Display text missing, using fallback for hero only.", heroSourceText);
  }

  // Build a stable key for this hero question
  const qId = q?.id ?? q?.ID ?? q?.uuid ?? q?.UUID ?? q?.key ?? q?.Q ?? "";
  const questionKey = `${current}|${qId}|${heroSourceText}`; // Include question text to detect placeholder->real transitions

  // Global per-question guard to avoid double hero TTS (StrictMode / re-renders)
  if (!window.__heroTtsStarted) window.__heroTtsStarted = new Set();
  const skipSpeak = shouldSpeak && window.__heroTtsStarted.has(questionKey);
  if (skipSpeak) {
    console.info("[Hero/TTS] Skipping hero intro TTS (already started)", { questionKey });
  } else if (shouldSpeak) {
    window.__heroTtsStarted.add(questionKey);
  }
  const effectiveShouldSpeak = shouldSpeak && !skipSpeak;

  // Keep the existing ref for any local logic (optional)
  lastSpokenQuestionRef.current = { current, questionKey };

  // Capture which question index this effect instance belongs to.
  // Used later to ignore stale TTS completions after the slide has advanced.
  const effectQuestionIndex = current;

if (rawTypeUpper === 'JOIN QUIZ') {
  // Lobby: never seed a hero dwell; the hook controls start time only.
  window.__heroShownAt = 0;
  window.__introDoneAt = 0;
  setHeroIntro(true, "join-quiz");
  setIntroDone(true);
  return;
}


  const isRoundCard = rawTypeUpper === "STARTROUND" || rawTypeUpper === "ENDROUND";
  const typeName = getTypeDisplayName(q);

  // Use cleaned text (which already has brackets removed if needed) for TTS
  const safeCleaned = cleanedHasText ? cleaned : (isTextInBrackets(trimmedText) ? extractTextFromBrackets(trimmedText) : trimmedText);
  const speakLineRound = safeCleaned
    ? ` ${safeCleaned}`
    : rawTypeUpper === "STARTROUND"
      ? " Welcome to the next round."
      : " Let's check those scores.";

  // qId already declared above, reuse it
  const dedupeKey = `Q|${displayQuestionNumber}|${String(qId)}|${String(typeName)}|${cleaned}`;

  if (rafRef.current.r1) cancelAnimationFrame(rafRef.current.r1);
  if (rafRef.current.r2) cancelAnimationFrame(rafRef.current.r2);
  clearTimeout(tRef.current.hold);
  clearTimeout(tRef.current.clear);
  clearTimeout(tRef.current.rulesUp);
  clearTimeout(tRef.current.rulesDown);
  rafRef.current = { r1: 0, r2: 0 };
  tRef.current   = { hold: 0, clear: 0, rulesUp: 0, rulesDown: 0 };

  setRulesCardVisible(false);
  // For round cards (STARTROUND/ENDROUND) and DISPLAY slides, preserve hero state - never reset it
  // The hero should stay visible until auto-advance moves to the next screen
  if (!isRoundCard && !isDisplay) {
    setHeroIntro(false, "question-reset");
    setIntroDone(false);
  } else {
    // For round cards and display slides, only reset introDone, but keep showIntro true
    setIntroDone(false);
  }
  setAnswersStagger(false);
  setAnswersReady(false);
  heroHideRequestRef.current = null;
  setAnimKey((k) => k + 1);

  tRef.current.rulesUp   = setTimeout(() => setRulesCardVisible(true), 700);
  tRef.current.rulesDown = setTimeout(() => setRulesCardVisible(false), 12000);

  let active = true;
  let heroStamped = false;

  const showHeroNow = () => {
    if (heroStamped) return;
    heroStamped = true;
    setHeroIntro(true, "show-hero-now");
    // Phones hold the question back until it is on the big screen.
    if (gameCode) updateDoc(doc(db, "games", gameCode), { questionShownQ: current, questionShownAt: serverTimestamp() }).catch(() => {});
    const tMono = performance.now();
    heroShownAtRef.current = tMono;
    window.__heroShownAt = tMono;
  };

  // For display-like slides (including STARTROUND/ENDROUND), assert the hero immediately.
  if (isDisplay) {
    showHeroNow();
  }

  (async () => {
    await awaitOverlayGate();

    if (!isDisplay && effectiveShouldSpeak) {
      const wheelPromise = window.__wheelCompletePromise && typeof window.__wheelCompletePromise.then === "function"
        ? window.__wheelCompletePromise
        : null;
      if (wheelPromise) {
        try { await wheelPromise; } catch (_) {}
        await new Promise((r) => setTimeout(r, wheelTtsDelayAfterMs));
      }
    }

    await new Promise((r) => {
      rafRef.current.r1 = requestAnimationFrame(() => {
        rafRef.current.r2 = requestAnimationFrame(() => {
          showHeroNow();
          r();
        });
      });
    });

    if (!isDisplay) {
      if (!effectiveShouldSpeak) {
        await new Promise((r) => setTimeout(r, 1200));
      } else {
        if (!window.__speakInFlight) window.__speakInFlight = new Map();
        const existing = window.__speakInFlight.get(dedupeKey);
        if (existing) {
          await existing;
        } else {
          let resolveDefer;
          const defer = new Promise((res) => { resolveDefer = res; });
          window.__speakInFlight.set(dedupeKey, defer);
          try {
            // Wheel landed: Ollie names the type (with his intro to it the first time it comes up
            // in a round), so the question itself is read without it.
            const typeLine = ollieTypeLine(q, bedQuestionKey);
            if (typeLine) {
              await speakAndWait(typeLine, {
                delayMs: 0,
                minHoldMs: 600,
                maxMs: 10000,
                noDetectGraceMs: 1200,
                approxMinMs: 1200,
                approxPerWordMs: 380,
                postEndHoldMs: 200,
                useGenericAudioDetection: false,
              });
            }
            if (!active) return;
            const lead =
              nextCueLoop("intro_question_lead", {
                allowVideo: false,
                context: { "{QNUM}": String(displayQuestionNumber) },
              })?.text || `Question ${displayQuestionNumber}.`;
            console.info("[Hero/TTS] Question TTS starting", {
              q: displayQuestionNumber,
              typeName,
            });
            await speakAndWait(safeCleaned ? `${lead} ${safeCleaned}` : lead, {
              delayMs: 0,
              minHoldMs: 1100,
              maxMs: 60000,
              noDetectGraceMs: 1200,
              approxMinMs: 5000,
              approxPerWordMs: 520,
              postEndHoldMs: 1200,
              useGenericAudioDetection: false,
            });
            console.info("[Hero/TTS] Question TTS finished", {
              q: displayQuestionNumber,
              typeName,
            });
          } finally {
            resolveDefer && resolveDefer();
            window.__speakInFlight.delete(dedupeKey);
          }
        }
      }
    } else {
      if (isRoundCard) {
        if (!effectiveShouldSpeak) {
          await new Promise((r) => setTimeout(r, 1000));
        } else if (rawTypeUpper === "ENDROUND") {
          // An end-of-round clip (his voice is baked in) if one hasn't played yet, else a spoken line.
          let clip = null;
          try { clip = promptMgrReady ? promptMgrRef.current?.nextVideo?.("end_round") : null; } catch {}
          let lineFromOllie = null;
          try {
            if (!clip && promptMgrRef.current && promptMgrReady) {
              const cue = promptMgrRef.current.next("end_round", { allowVideo: false, context: ollieContext(), facts: ollieFacts() });
              if (cue?.kind === "tts" && cue.text) lineFromOllie = cue.text;
            }
          } catch {}
          if (clip?.src) await playOllieVideo(clip.src);
          else await speakAndWait(lineFromOllie || speakLineRound, {
            delayMs: 300,
            minHoldMs: 900,
            maxMs: 8000,
            noDetectGraceMs: 800,
            approxMinMs: 2200,
            approxPerWordMs: 380,
            postEndHoldMs: 800,
          });
        } else {
          await speakAndWait(speakLineRound, {
            delayMs: 300,
            minHoldMs: 900,
            maxMs: 8000,
            noDetectGraceMs: 800,
            approxMinMs: 2200,
            approxPerWordMs: 380,
            postEndHoldMs: 800,
          });
        }
      } else if (rawTypeUpper !== "INTRODUCTION" && rawTypeUpper !== "JOIN QUIZ") {
        await new Promise((r) => setTimeout(r, 1200));
      }
    }

    if (!active) return;

    // For display-like slides (including STARTROUND/ENDROUND), keep hero visible
    // Round cards should never hide the hero - it stays until auto-advance
    if (isDisplay || isRoundCard) {
      setIntroDone(true);
      return;
    }

    if (current !== effectQuestionIndex) {
      console.info("[Hero/TTS] Skipping hero hide for stale question", {
        effectQuestionIndex,
        current,
      });
      return;
    }

    // Check if this question type has immediate multi-option answers
    // These types show answer buttons immediately when introDone becomes true
    const hasImmediateAnswers = [
      "MULTICHOICE",
      "MULTIANSWER",
      "TRUE/FALSE",
      "TRUEFALSE",
      "VANISHINGCHOICE",
      "OUTOFORDER"
    ].includes(rawTypeUpper);

    console.info("[Hero/TTS] Opening answers gate", {
      q: displayQuestionNumber,
      current,
      hasImmediateAnswers,
    });
    
    // Always set introDone first to enable answer rendering
    setIntroDone(true);
    const timerSec = getQuestionDurationSeconds(q);
    try {
      await openAnswersGate(gameCode, current, { timerSeconds: timerSec });
    } catch (e) {
      console.error("openAnswersGate failed", e);
    }
    try {
      onAnswersGateOpenedRef.current?.(current);
    } catch (e) {
      console.error("onAnswersGateOpened failed", e);
    }
    setAnswersStagger(true);
    tRef.current.clear = setTimeout(() => setAnswersStagger(false), 1600);

    heroHideRequestRef.current = {
      questionIndex: effectQuestionIndex,
      waitForAnswers: hasImmediateAnswers,
    };
    try {
      console.info("[Hero/TTS] Hero hide request created", {
        q: displayQuestionNumber,
        waitForAnswers: hasImmediateAnswers,
      });
    } catch {}
    setHeroHideSignal((tick) => tick + 1);
    withTTSSuppressed(() => {
      hostRef.current?.onQuestionStart?.({ q, displayNumber: displayQuestionNumber });
    });
  })();

  return () => {
    active = false;
    if (rafRef.current.r1) cancelAnimationFrame(rafRef.current.r1);
    if (rafRef.current.r2) cancelAnimationFrame(rafRef.current.r2);
    clearTimeout(tRef.current.hold);
    clearTimeout(tRef.current.clear);
    clearTimeout(tRef.current.rulesUp);
    clearTimeout(tRef.current.rulesDown);
    rafRef.current = { r1: 0, r2: 0 };
    tRef.current   = { hold: 0, clear: 0, rulesUp: 0, rulesDown: 0 };
    try { window.aiStop && window.aiStop(); } catch {}
  };
}, [
  current,
  isDisplay,
  displayQuestionNumber,
  heroSourceText,
  q?.question,
  q?.Q,
  q?.display,
  q?.title,
  q?.text,
  q?.defaultText,
  q?.prompt,
  q?.slideText,
  q?.ROUNDNAME,
  q?.RoundName,
  q?.roundName,
  q?.ROUND_TITLE,
  q?.RoundTitle,
  q?.roundTitle,
]); // Track relevant fields for placeholder->real transitions




// --- Image only (responsive, no distortion) ---
// --- Image only (responsive, no distortion; NO BORDER/SHADOW) ---
if (hasImageOnly && !isShowRounds && !isStartEnd && !isDisplay) {
  return (
    <div
      className="quiz-bg"
      style={{
        background: "#191b22",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2vh 2vw",
      }}
    >
      <ResponsiveContainImage
        src={p1Url}
        alt="Quiz Visual"
        style={{
          maxWidth: "95vw",
          maxHeight: "95vh",
        }}
      />
    </div>
  );
}






  // Resolve text for DISPLAY slides (title/default). Keeps slide 0 visible even if q.question is empty.
const displayText = React.useMemo(() => {
  const rawText = heroSourceTextRaw || "";
  const cleanedDisplayText = cleanQuestionText(rawText);
  const finalText = cleanedDisplayText.trim()
    || heroSourceText.trim()
    || (isStartEnd ? "Get ready for the next round!" : "");
  
  // For STARTROUND, ENDROUND, and DISPLAY slides: if text is in brackets, don't display it
  if ((isStartEnd || isDisplay) && isTextInBrackets(rawText)) {
    return ""; // Hide the text, but TTS will still read it
  }
  
  return finalText;
}, [heroSourceTextRaw, heroSourceText, isStartEnd, isDisplay]);

  // Check if JACKPOT is paused (do this AFTER all hooks to avoid hook order issues)
  const isJackpot = String(q?.type || q?.CAT || "").toUpperCase() === "JACKPOT";
  const isPaused = (() => {
    try {
      return (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase() !== "YES";
    } catch {
      return false;
    }
  })();

  // Show placeholder for paused JACKPOT ONLY if timer hasn't run out yet
  // After timer === 0, show results even if paused (paused just prevents auto-advance)
  if (isJackpot && isPaused && timer > 0) {
    return (
      <div style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(180deg, rgba(212,175,55,0.8), rgba(180,145,35,0.85))",
        color: "#111",
        fontFamily: "'Open Sans', Arial, sans-serif",
        flexDirection: "column",
        gap: "2vh"
      }}>
        <div style={{
          fontSize: "clamp(32px, 5vw, 64px)",
          fontWeight: 900,
          textAlign: "center",
          textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
        }}>
          JACKPOT Question
        </div>
        <div style={{
          fontSize: "clamp(18px, 2.5vw, 32px)",
          fontWeight: 700,
          textAlign: "center",
          opacity: 0.9
        }}>
          Quizmaster - Press P to reveal the question
        </div>
        <div style={{
          fontSize: "clamp(16px, 2vw, 28px)",
          fontWeight: 600,
          textAlign: "center",
          opacity: 0.85,
          marginTop: "1vh",
          maxWidth: "90vw"
        }}>
          Please send one representative from your team to the front to play the Jackpot question!
        </div>
      </div>
    );
  }

  // --- Main display ---
  return (

    



    
<div
  className="quiz-bg"
  style={{
    background: buildBackgroundCSS(backgroundUrl || (isStartEnd ? p1Url : undefined), isDisplay),
  }}
>






    {/* Mount once so window.aiSpeak is available */}

<AIHostTTSPlayer
  defaultVoice="Google UK English Male"
  defaultSpeed={0.95}

  // Call the function directly (no proxy). We send text/plain so there’s no preflight.
  ttsUrl="https://us-central1-bobquiz-272a7.cloudfunctions.net/ttsSpeak"

  cloudVoiceName="en-GB-Neural2-B"
  cloudLanguageCode="en-GB"
  cloudSpeakingRate={1.10}
  cloudPitch={2}
  style={{ display: "none" }}
/>



{/* --- LOBBY / JOIN QUIZ SLIDE --- */}
      {String(q?.type || q?.CAT || "").toUpperCase() === 'JOIN QUIZ' ? (
        // Main container - Flex column layout to manage vertical space
        <div style={{ position: 'relative', height: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', overflow: 'hidden' /* Prevent scroll on container */ }}>

{/* --- LARGE COUNTDOWN TIMER (Top Right - Resized) --- */}
          {status && status.mode === 'countdown' && status.reason === 'start' && status.seconds >= 0 && (
            <div style={{
              position: 'absolute',
              top: '2vh',
              right: '2vw',
              zIndex: 2000,
              color: '#fff',
              textAlign: 'right',
            }}>
              {/* "Quiz Starts In:" Label */}
              <div style={{ marginBottom: '0.5rem', fontSize: '1.5rem', fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                Quiz Starts In:
              </div>
              {/* The Actual Timer */}
              <div
                style={{
                  fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                  // --- INCREASED FONT SIZE ---
                  fontSize: "min(14.5vw, 18.75vh)", // ~25% larger
                  fontWeight: 900,
                  textShadow: "0 0 8px #36d, 0 0 20px #36d, 0 0 40px #36d",
                  lineHeight: 1,
                }}
              >
                {fmtHMS(status.seconds)}
              </div>
            </div>
          )}
          {/* --- END COUNTDOWN TIMER --- */}

          {/* --- EXISTING LOBBY CONTENT STARTS HERE --- */}

          {/* QR Code and Join Code Container (Shifted Left & Responsive Sizes & Reduced Margins) */}
          {!(localStorage.getItem("opt_test_mode") === "On" && window.location.hostname !== 'localhost') && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start", // Shifted Left
                paddingLeft: "3vw",          // Adjusted Padding
                gap: "2vw",                  // Adjusted Gap
                marginBottom: '1vh',         // Reduced Margin
                marginTop: '1vh',            // Reduced Margin
                flexWrap: "wrap",            // Allow wrapping
                flexShrink: 0,               // Prevent this container from shrinking
              }}
            >

              
  {/* --- QR Code (Resized) --- */}
              <QRCode
                value={`${window.location.origin}/play`}
                style={{
                  // --- INCREASED SIZE ---
                  width: 'clamp(188px, 31vh, 350px)',  // ~25% larger
                  height: 'clamp(188px, 31vh, 350px)', // ~25% larger
                  background: "#fff",
                  borderRadius: 20, // Slightly larger radius to match
                  boxShadow: "0 1px 8px #0003",
                  flexShrink: 0,
                }}
              />

              {/* --- Join Code (Responsive Size) --- */}
              {joinCode && (
                <span
                  style={{
                    display: "inline-block", fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                    fontSize: "clamp(1.5em, 5vw, 2.2em)", // Responsive size
                    fontWeight: 900, color: "#e6eaff", background: "rgba(20,40,60,0.84)",
                    borderRadius: "18px", padding: "0.29em 1.1em 0.32em 1.1em",
                    boxShadow: "0 3px 22px #0015", border: "3px solid #4d8dd8",
                    letterSpacing: "0.1em", textShadow: "0 2px 14px #294b8862, 0 2px 1px #fff3",
                    position: "relative",
                    flexShrink: 1, // Allow shrinking if needed
                    minWidth: '150px',
                  }}
                >
                  <span style={{ /* Keep existing label styles */ fontSize: "0.8em", top: "-1.1em", left: "0.4em", letterSpacing: "0.09em", whiteSpace: 'nowrap' }}>
                    Join Code:
                  </span>
                  <span style={{ letterSpacing: "0.2em", marginLeft: 4 }}>
                    {joinCode}
                  </span>
                </span>
              )}
            </div>
          )}

          {/* Play URL (Responsive Size & Reduced Margin) - localhost only */}
          {window.location.hostname === 'localhost' && (
            <div style={{ textAlign: "center", marginBottom: '1vh', flexShrink: 0 }}>
              <span className="question-prompt" style={{
                background: "#f7f7ff", color: "#294b88", borderRadius: 7,
                fontFamily: "monospace, Menlo, 'Courier New', Courier",
                fontWeight: 700,
                fontSize: "clamp(1rem, 2.5vw, 1.8rem)", // Responsive size
                letterSpacing: "0.04em", display: "inline-block",
                whiteSpace: "pre-line", padding: '0.3em 0.6em',
              }}>
                {`${window.location.origin}/play/${gameCode}`}
              </span>
            </div>
          )}

          {/* Hidden Question Prompt (Keep as is) */}
          {!blockingOverlaysUp && (
             <div className={`question-prompt ${showIntro ? "question-hidden-during-intro" : ""}`} style={{ opacity: showIntro ? 0 : 1, transition: "opacity 300ms ease-out, transform 300ms ease-out", transform: showIntro ? "translateY(-20px)" : "translateY(0)", flexShrink: 0 /* Prevent shrinking */ }}>
              {buildQuestionDisplayText(q)}
            </div>
          )}

          {/* Joined Teams Scroll (Wrapper to take remaining space) */}
          {/* This div takes up the remaining vertical space */}
          <div style={{ flexGrow: 1, minHeight: 0, width: '100%', overflow: 'hidden', position: 'relative' }}>
             <JoinedTeamsScroll teams={teamsArray} />
          </div>

        </div> // This closes the main container div for JOIN QUIZ
      ): (
        <>
          {/* QUESTION RENDERING */}

{/* QUESTION RENDERING */}
{!blockingOverlaysUp && (
  // First: SHOWROUNDS shows its own title bar + grid (read-only)
  String(q.type || q.CAT || "").toUpperCase() === "SHOWROUNDS" ? (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        zIndex: 2000
      }}
    >
      {/* Hero/title bar */}
      <div
style={{
              width: "100%",
              textAlign: "center",
              color: "#fff",
              fontWeight: 900,
              fontSize: "clamp(32px, 4.5vh, 48px)",
              fontFamily: "'Arial Black', Impact, Arial, sans-serif", // Correct Font
              textShadow: "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111", // Correct Shadow ONLY
              padding: "1.5vh 2vw 0.5vh",
              position: "relative",
              zIndex: 2,
            }}
      >
        Upcoming Rounds
      </div>

      {/* Grid of round names (confined to middle 60% of viewport) */}
<div
  style={{
    position: "absolute",
    top: "20vh",
    bottom: "20vh",
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0 2vw",
    zIndex: 1,
  }}
>
  <div
    style={{
      width: "100%",
      maxWidth: "95vw",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "stretch",
    }}
  >
          <ShowRoundsGrid
            allSlides={questions}
            currentIndex={current}
            fall={answersStagger}
            onJumpToIndex={handleJumpToSlide}
          />
  </div>
</div>

    </div>
) : String(q.type || q.CAT || "").toUpperCase() === "BREAK TIME" ? (
  <div
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      color: "#fff",
    }}
  >
    {/* Title / message */}
    <div
      className="question-prompt"
      style={{ fontSize: "3rem", opacity: 1, transform: "none", position: "static", marginBottom: "2rem" }}
    >
      {displayText}
    </div>

    {/* Big digital countdown */}
    <div
      style={{
        fontFamily: "'Arial Black', Impact, Arial, sans-serif",
        fontSize: "min(24vw, 36vh)",
        fontWeight: 900,
        textShadow: "0 0 10px #36d, 0 0 30px #36d, 0 0 60px #36d",
        marginBottom: "24px",
      }}
    >
      {`${Math.floor(timer / 60).toString().padStart(2, "0")}:${(timer % 60)
        .toString()
        .padStart(2, "0")}`}
    </div>


  </div>
) : (

    // Else: your existing Display vs non-Display rendering
// Else: your existing Display vs non-Display rendering
// Else: your existing Display vs non-Display rendering
isDisplay ? (
  (() => {
    // Use video if either VIDEO_URL or P1_URL is a video file
    const mediaSrc = String((videoUrl || p1Url || "")).trim();
    const isVid = /\.(mp4|webm|ogg)(\?.*)?$/i.test(mediaSrc);

    // Full-viewport, preserve aspect ratio
    const mediaStyle = {
      position: "fixed",
      inset: 0,
      width: "100vw",
      height: "100vh",
      objectFit: "contain",
      zIndex: 1500, // below the hero box (which is 2000+)
      background: "transparent",
    };

    // Ensure non-video slides never block auto-advance
    if (!isVid) {
      try { window.__videoPlaying = false; } catch {}
    }

    // Hero overlay id
    const overlayId = `display-hero-${current}`;

    // Fade out the overlay, then call fn() after fade ends (fn optional)
    const fadeOutOverlayThen = (fn) => {
      try {
        const el = document.getElementById(overlayId);
        if (el && el.dataset.hidden !== "1") {
          el.style.transition = "opacity .45s ease";
          el.style.opacity = "0";
          el.dataset.hidden = "1";
          setTimeout(() => {
            if (el) el.style.display = "none";
            if (typeof fn === "function") fn();
          }, 460);
        } else {
          if (typeof fn === "function") fn();
        }
      } catch {
        if (typeof fn === "function") fn();
      }
    };

    // For IMAGE slides: schedule the same 5s hide as video (no playback step)
    // For round cards (STARTROUND/ENDROUND) and DISPLAY slides, never hide the hero - it stays until auto-advance
    const scheduleImageHeroHideOnce = () => {
      if (isVid) return; // only for images
      if (isStartEnd || isDisplay) return; // never hide hero for round cards or display slides
      try {
        const el = document.getElementById(overlayId);
        if (!el) return;
        if (el.dataset.hideScheduled === "1") return;
        el.dataset.hideScheduled = "1";
        setTimeout(() => fadeOutOverlayThen(), 5000);
      } catch {}
    };

    // For VIDEO slides: start only after 5s + fade
    // For round cards (STARTROUND/ENDROUND) and DISPLAY slides, never hide the hero - it stays until auto-advance
    const scheduleStartAfterHero = (v) => {
      if (!v) return;
      if (isStartEnd || isDisplay) return; // never hide hero for round cards or display slides
      if (v.dataset.startScheduled === "1") return;
      v.dataset.startScheduled = "1";

      setTimeout(() => {
        fadeOutOverlayThen(async () => {
          try {
            await v.play();
            window.__videoPlaying = true;
          } catch {
            try {
              v.muted = true;
              await v.play();
              window.__videoPlaying = true;
            } catch {
              window.__videoPlaying = false;
            }
          }
        });
      }, 5000); // hero visible for 5s before fade+play
    };

    // Kick the image hide timer (safe no-op for videos)
    scheduleImageHeroHideOnce();

    return (
      <>
        {mediaSrc && isVid && (
          <video
            key={`display-video-${current}`}
            src={mediaSrc}
            // Do NOT autoplay — we start it after hero hides
            muted
            playsInline
            preload="metadata"
            // Schedule start when ready (guarded against double-run)
            onLoadedMetadata={(e) => scheduleStartAfterHero(e.currentTarget)}
            onLoadedData={(e) => scheduleStartAfterHero(e.currentTarget)}
            onCanPlay={(e) => scheduleStartAfterHero(e.currentTarget)}
            onPlay={() => { window.__videoPlaying = true; }}
            onEnded={() => { window.__videoPlaying = false; }}
            onPause={(ev) => { if (!ev?.currentTarget?.ended) window.__videoPlaying = false; }}
            style={mediaStyle}
          />
        )}

        {mediaSrc && !isVid && (
          <img
            key={`display-image-${current}`}
            src={mediaSrc}
            alt=""
            onLoad={() => scheduleImageHeroHideOnce()}
            style={mediaStyle}
          />
        )}

        {/* Translucent hero box (video & image: show 5s, then fade) */}
        {/* For DISPLAY slides, always render the container; for others, only if displayText exists */}
        {(isDisplay || Boolean(displayText)) && (
          <div
            id={overlayId}
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 20050,
              pointerEvents: "none",
              opacity: showIntro ? 1 : 0,
              visibility: showIntro ? "visible" : "hidden",
              transition: "opacity 300ms ease",
            }}
          >
            {/* Only render the inner box if displayText exists */}
            {displayText && (
              <div
                key={animKey}
                style={{
                  maxWidth: "92vw",
                  padding: "2.2vh 3.2vw",
                  borderRadius: "28px",
                  background: "rgba(0,0,0,0.48)",
                  boxShadow: "0 12px 50px rgba(0,0,0,0.35)",
                  // For round cards and display slides, always keep at full scale - never shrink
                  transform: (showIntro || isStartEnd || isDisplay) ? "scale(1)" : "scale(0.1)",
                  transition:
                    "transform 2s cubic-bezier(.2,.7,.2,1), opacity .6s ease",
                  opacity: showIntro ? 1 : 0,
                }}
              >
                <div
                  style={{
                    fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                    fontWeight: 900,
                    lineHeight: 1.12,
                    letterSpacing: ".02em",
                    textAlign: "center",
                    color: "#fff",
                    textShadow:
                      "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
                    whiteSpace: "pre-line",
                    wordBreak: "break-word",
                    fontSize: computeHeroFontSize(displayText),
                  }}
                >
                  {displayText}
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  })()
) : (





      <>
        {/* Normal small-on-top prompt (hidden until hero finishes) */}
        {!blockingOverlaysUp && (
          <div
            className={`question-prompt ${
              showIntro ? "question-hidden-during-intro" : ""
            }`}
            style={{
              transition: "opacity 300ms ease-out, transform 300ms ease-out",
              opacity: showIntro || !introDone ? 0 : 1,
              visibility: showIntro || !introDone ? "hidden" : "visible",
              transform: showIntro ? "translateY(-20px)" : "translateY(0)",
              fontSize: computeQuestionFontSize(questionTextForDisplay),
              minHeight: "7vh",
              pointerEvents: showIntro || !introDone ? "none" : "auto",
            }}
          >
            {buildQuestionDisplayText(q)}
          </div>
        )}

        {/* Normal hero during 5s intro */}
        <div
          className="question-intro hero-hold"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 30000,          // above rules card
            pointerEvents: "none",   // don’t block clicks
            opacity: showIntro ? 1 : 0,
            visibility: showIntro ? "visible" : "hidden",
            transition: "opacity 300ms ease",
          }}
        >
          <div
            key={animKey}
            className="question-intro-inner hero"
            style={{
              zIndex: 30000,
              maxWidth: "92vw",
              padding: "2.2vh 3.2vw",
              borderRadius: "28px",
              background: "rgba(0,0,0,0.48)",
              boxShadow: "0 12px 50px rgba(0,0,0,0.35)",
              transform: showIntro ? "scale(1)" : "scale(0.1)",
              transition:
                "transform 1s cubic-bezier(.2,.7,.2,1), opacity .6s ease",
              // Disable CSS animations that shrink the hero automatically
              // Hero should only shrink when React state changes, not due to CSS timing
              animation: "none",
              fontFamily: "'Arial Black', Impact, Arial, sans-serif",
              fontWeight: 900,
              lineHeight: 1.12,
              letterSpacing: ".02em",
              textAlign: "center",
              color: "#fff",
              textShadow:
                "-2px -2px 0 #111, 2px -2px 0 #111, -2px 2px 0 #111, 2px 2px 0 #111",
              whiteSpace: "pre-line",
              wordBreak: "break-word",
              fontSize: computeHeroFontSize(cleanQuestionText(q.question)),
              opacity: showIntro ? 1 : 0,
            }}
          >
            {(() => {
              const rawQuestionText = q?.question || q?.Q || "";
              if (isHashtagPlaceholder(rawQuestionText)) {
                return "";
              }
              const questionText = cleanQuestionText(rawQuestionText);
              const rawTypeUpper = String(q?.type || q?.CAT || "").toUpperCase();
              const isStartEndOrDisplay = rawTypeUpper === "STARTROUND" || 
                                         rawTypeUpper === "ENDROUND" || 
                                         rawTypeUpper === "DISPLAY";
              
              // If it's STARTROUND/ENDROUND/DISPLAY and text is in brackets, don't display
              if (isStartEndOrDisplay && isTextInBrackets(rawQuestionText)) {
                return ""; // Hide the text, but TTS will still read it
              }
              
              return questionText + getMultiAnswerSuffix(q);
            })()}
          </div>
        </div>
      </>
    )
  )
)}




          {/* WHEEL (unchanged) */}
{(q.type || "").toUpperCase() === "WHEEL" ? (() => {
  const ROWS = 3, COLS = 12;
  const board = buildWheelBoard(q.answers[0] || "", ROWS, COLS);

  // build reveal order
  let letterTileIndexes = [];
  board.forEach((row, r) => row.forEach((ch, c) => {
    if (/[A-Z0-9]/.test(ch)) letterTileIndexes.push(r * COLS + c);
  }));
  const seed = (q.answers[0] || "").length + (q.question || "").length;
  const shuffled = seededShuffle(letterTileIndexes, seed);

  const totalLetters = shuffled.length;
  const toReveal = (q.time > 0 && timer > 0)
    ? Math.max(1, Math.floor(((q.time - timer + 1) / q.time) * totalLetters))
    : totalLetters;
  const revealed = new Set(shuffled.slice(0, toReveal));

  // fixed stage so nothing scrolls; rows fill stage with ZERO gaps
  const stageStyle = {
    position: "fixed",
    top: "16vh",
    bottom: "14vh",
    left: "2vw",
    right: "2vw",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    zIndex: 1500
  };

  const rowStyle = {
    display: "flex",
    width: "100%",
    margin: 0,
    padding: 0,
    // no vertical gap between rows
    lineHeight: 0
  };

  // each tile is a perfect square: width = 1/COLS of row; height = width
  // visible “square grid” lines: darker borders with single-pixel separators
  const tileBase = {
    width: `calc(100% / ${COLS})`,
    aspectRatio: "1 / 1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    margin: 0,
    // ensure there are NO gaps anywhere
    borderCollapse: "collapse",
    // high-contrast borders so the squares are clearly visible
    border: "3px solid #0b3a25",
    fontFamily: "'Arial Black', Impact, Arial, sans-serif",
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    userSelect: "none",
    overflow: "hidden",
    // remove shadows that can look like gutters
    boxShadow: "none"
  };

  return (
    <div style={stageStyle}>
      {board.map((row, r) => (
        <div key={r} style={rowStyle}>
          {row.map((ch, c) => {
            const isLetter = /[A-Z0-9]/.test(ch);
            const flat = r * COLS + c;
            const show = isLetter && revealed.has(flat);
            const style = {
              ...tileBase,
              background: isLetter ? "rgba(255,255,255,0.9)" : "rgba(32,178,109,0.90)",
              // letter tiles have darker border; blanks use the green border already set
              borderColor: isLetter ? "#111" : "#0b3a25",
              color: show ? "#111" : "transparent",
              fontSize: "min(6.8vw, 9.2vh)" // scales but stays inside the square
            };
            return <span key={c} style={style}>{show ? ch : ""}</span>;
          })}
        </div>
      ))}
    </div>
  );
})() : null}



  {/* === NEARESTANSWER RESULTS: Improved Horizontal Bar === */}
{!hideNearestAnswerBars && (["NEARESTANSWER", "JACKPOT"].includes((q.type || "").toUpperCase())) && timer === 0 && allAnswers && allAnswers.length > 0 ? (() => {
    const toNumber = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : 0;
    };

    // --- 1. Calculate sorted bars (closest first) ---
    const correctValue = toNumber(q.answers[0]);
    let bars = allAnswers.map((a) => ({
        label: a.team,
        value: toNumber(a.answer),
        diff: toNumber(a.answer) - correctValue,
        isCorrect: false,
        points: a.points || 0,
    }));
    bars.push({
        label: "Correct Answer",
        value: correctValue,
        diff: 0,
        isCorrect: true,
        points: undefined,
    });

    // --- Sort by absolute diff, then correct, then points ---
    bars.sort((a, b) => {
        const diffA = Math.abs(a.diff);
        const diffB = Math.abs(b.diff);
        if (diffA !== diffB) return diffA - diffB;
        if (a.isCorrect) return -1;
        if (b.isCorrect) return 1;
        return (b.points || 0) - (a.points || 0);
    });
    // Display farthest first so the nearest answer arrives last
    const displayBars = [...bars].reverse();

    // --- 2. Scale for the improved horizontal bar ---
    const values = bars.map((b) => b.value);
    const minValue = Math.min(...values, correctValue);
    const maxValue = Math.max(...values, correctValue);
    const range = Math.max(1, maxValue - minValue);
    const pad = range * 0.1;
    const displayMin = minValue - pad;
    const displayMax = maxValue + pad;
    const displayRange = Math.max(1, displayMax - displayMin);
    const toPct = (v) => Math.max(0, Math.min(100, ((v - displayMin) / displayRange) * 100));
    const correctPct = toPct(correctValue);

    const animationName = `fadeNearest-${current}`;
    const CONTAINER_HEIGHT_VH = 82;
    const HEADER_TOP_VH = 8;

    return (
        <>
            <style>{`
@keyframes ${animationName} {
    0% { opacity: 0; transform: translateX(80px); }
    100% { opacity: 1; transform: translateX(0); }
}
            `}</style>

            {/* Static Header Row */}
            <div style={{
                position: "absolute",
                top: `${HEADER_TOP_VH}vh`,
                left: "50%",
                transform: "translateX(-50%)",
                width: "90vw",
                zIndex: 9011,
                display: "grid",
                gridTemplateColumns: "2fr 4fr 1fr 1fr auto",
                alignItems: "center",
                padding: "0.4em 2.2em",
                color: "#FFFFFF",
                textShadow: "0 1px 3px rgba(0,0,0,0.7)",
                background: "linear-gradient(90deg, #32ff99, #48d488)",
                borderBottom: "1px solid rgba(120, 150, 255, 0.3)",
                fontSize: "1.4rem",
                fontWeight: "bold",
                boxSizing: "border-box",
                borderRadius: "1em 1em 0 0",
            }}>
                <span>Team Name</span>
                <span style={{ textAlign: "center" }}>Improved Horizontal Bar</span>
                <span style={{ textAlign: "center" }}>Guess</span>
                <span style={{ textAlign: "right" }}>Difference</span>
                <span style={{ minWidth: 54 }}>&nbsp;</span>
            </div>

            {/* Viewport Container */}
            <div style={{
                position: "absolute",
                top: `calc(${HEADER_TOP_VH}vh + 4em)`,
                left: "50%",
                transform: "translateX(-50%)",
                width: "90vw",
                height: `${CONTAINER_HEIGHT_VH}vh`,
                overflow: "hidden",
                zIndex: 30,
            }}>
                <div style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}>
                    {displayBars.map((bar, index) => {
                        const guessPct = toPct(bar.value);
                        const leftPct = Math.min(correctPct, guessPct);
                        const widthPct = Math.max(0.8, Math.abs(guessPct - correctPct));
                        const isOver = bar.diff > 0;
                        const isUnder = bar.diff < 0;
                        const segmentColor = isOver ? "#ff5b5b" : isUnder ? "#4aa3ff" : "#32ff99";

                        return (
                            <div
                                key={`${bar.label}-${index}`}
                                style={{
                                    background: bar.isCorrect ? "linear-gradient(90deg, #32ff99, #48d488)" : "#fffefd",
                                    color: bar.isCorrect ? "#FFFFFF" : "#191919",
                                    width: "100%",
                                    minHeight: "8vh",
                                    margin: "0.15em 0",
                                    fontWeight: 900,
                                    fontSize: bar.isCorrect ? "2.1rem" : "2rem",
                                    display: "grid",
                                    gridTemplateColumns: "2fr 4fr 1fr 1fr auto",
                                    alignItems: "center",
                                    borderRadius: "2em",
                                    pointerEvents: "none",
                                    boxShadow: "0 2px 10px #3332, 0 4px 24px #fff7",
                                    padding: "0.2em 2.2em",
                                    textShadow: bar.isCorrect ? "0 1px 3px rgba(0,0,0,0.7)" : "0 2px 10px #fff8, 0 1px 3px #0005",
                                    flexShrink: 0,
                                    boxSizing: "border-box",
                                    opacity: 0,
                                    animation: `${animationName} 0.55s ease-out forwards`,
                                    animationDelay: `${index * 0.08}s`,
                                }}
                            >
                                {/* Team Name */}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {bar.label}
                                </span>

                                {/* Improved Horizontal Bar */}
                                <div style={{ position: "relative", width: "100%", height: "18px" }}>
                                    <div style={{
                                        position: "absolute",
                                        inset: 0,
                                        background: "linear-gradient(90deg, rgba(0,0,0,0.12), rgba(0,0,0,0.06))",
                                        borderRadius: 10,
                                        boxShadow: "inset 0 2px 6px rgba(0,0,0,0.2)",
                                    }} />
                                    {bar.diff !== 0 && (
                                        <div style={{
                                            position: "absolute",
                                            left: `${leftPct}%`,
                                            width: `${widthPct}%`,
                                            top: 0,
                                            bottom: 0,
                                            background: segmentColor,
                                            borderRadius: 10,
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
                                        }} />
                                    )}
                                    <div style={{
                                        position: "absolute",
                                        left: `${correctPct}%`,
                                        top: -6,
                                        bottom: -6,
                                        width: 4,
                                        background: "#32ff99",
                                        boxShadow: "0 0 10px rgba(50,255,153,0.9)",
                                        borderRadius: 2,
                                    }} />
                                    <div style={{
                                        position: "absolute",
                                        left: `${guessPct}%`,
                                        top: -2,
                                        bottom: -2,
                                        width: 2,
                                        background: bar.diff === 0 ? "#ffffff" : "#111",
                                        opacity: bar.diff === 0 ? 0 : 0.5,
                                    }} />
                                </div>

                                {/* Guess */}
                                <span style={{ textAlign: "center" }}>
                                    {bar.value.toLocaleString()}
                                </span>

                                {/* Difference */}
                                <span style={{ textAlign: "right" }}>
                                    {bar.diff === 0 ? "" : bar.diff > 0 ? `+${Math.abs(bar.diff).toLocaleString()}` : `-${Math.abs(bar.diff).toLocaleString()}`}
                                </span>

                                {/* Points Badge */}
                                {bar.points !== undefined && (
                                    <span style={{
                                        marginLeft: 16,
                                        fontWeight: 900,
                                        color: bar.points > 0 ? "#FFFFFF" : "#aaa",
                                        background: bar.points > 0 ? "#1E8E3E" : "none",
                                        borderRadius: "1.3em",
                                        padding: "0.22em 1.1em",
                                        fontSize: "1.6rem",
                                        border: bar.points > 0 ? "1px solid #90EE90" : "none",
                                        boxShadow: bar.points > 0 ? "0 2px 6px rgba(0,0,0,0.4)" : "none",
                                        minWidth: 54,
                                        textAlign: "center",
                                    }}>
                                        {bar.points > 0 ? `+${bar.points}` : ""}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </>
    );
})() : null}
{/* === END OF NEARESTANSWER BLOCK === */}

{!isDisplay && (q.type || "").toUpperCase() === "NEARESTANSWER" && p1Url && (
  <div className="gameshow-buttons" style={{
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    margin: "0 auto",
    gap: "2vw",
    padding: "0 1vw"
  }}>
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%"
    }} />
    <div className={`image-container ${timer === 0 ? "fade-out" : ""}`}>
      <GlassImageCard
        src={p1Url}
        alt="Quiz Visual"
        style={{
          width: "clamp(280px, 45vw, 800px)",
          maxWidth: "50vw",
          maxHeight: "65vh",
          marginTop: "2vh",
          marginRight: "2vw",
          flexShrink: 1
        }}
        jigsawProps={
          isJigsawOn(q)
            ? { src: p1Url, timer: timer, totalTime: q.time || 60, rows: 6, cols: 8 }
            : null
        }
        kenBurns={!isJigsawOn(q)}
      />
    </div>
  </div>
)}


          

          {/* ALL OTHER TYPES */}
          {!isDisplay && !["WHEEL", "NEARESTANSWER", "JACKPOT"].includes((q.type || "").toUpperCase()) && (
              /* <div style={{   */ 
              <div className="gameshow-buttons" style={{

 display: "flex",
    // justifyContent: "center", // Let flex properties handle distribution
    alignItems: "flex-start", // Align items to the top
    width: "100%",            // Use full available width
    // maxWidth: "1900px",    // Removing this constraint might help flexbox
    margin: "0 auto",
    gap: p1Url ? "2vw" : 0,    // Space between columns if image exists
    padding: "0 1vw"           // Minimal horizontal padding
              }}>

              <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                //alignItems: p1Url ? "flex-end" : "center",
                alignItems: "center",
                justifyContent: "center",
                height: "100%"
              }}>



                {(q.type || "").toUpperCase() === "BETANSWER" && current !== 0 && timer > 30 && (
                  <div style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "column",
                    minHeight: "40vh",
                    padding: "5vh 2vw",
                    color: "#fff",
                    fontSize: "2.4rem",
                    fontWeight: 700,
                    textAlign: "center"
                  }}>
                    Teams are placing their bets...
                    <div style={{
                      fontFamily: "'Arial Black', Impact, Arial, sans-serif",
                      fontSize: "min(24vw, 36vh)",
                      fontWeight: 900,
                      color: "#fff",
                      background: "transparent",
                      textAlign: "center",
                      textShadow: "0 0 10px #f33, 0 0 30px #f00, 0 0 60px #f00",
                      marginTop: "2vh",
                      marginBottom: "1vh"
                    }}>
                      {Math.max(0, timer - 30)}
                    </div>
                    <div style={{ fontSize: "1.5rem", color: "#ccc" }}>
                      Seconds to choose your bet (0–25 points)
                    </div>
                  </div>
                )}


{introDone &&
  ((q.type || "").toUpperCase() !== "BETANSWER" || timer <= 30) && (

  (String(q.type || q.CAT || "").toUpperCase() === "OUTOFORDER")
    ? (
      // OUTOFORDER: show setup order during the question, animate to correct after timer==0
      <OutOfOrderRevealHost q={q} timer={timer} />

    )
    : (normalizeType(q) === "BREADCRUMBS")
    ? (
      // BREADCRUMBS: clue cards with playing-card image (portrait, 3x size), stacked/dropped, centred; answer above
      (() => {
        const BREADCRUMBS_CARD_IMAGE = (typeof process !== "undefined" && process.env && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : "") + "/media/playingcard.png";
        const factsFromQ = Array.isArray(q.facts) && q.facts.length > 0 ? q.facts : [];
        const facts = factsFromQ.length > 0 ? factsFromQ : [q.A2, q.A3, q.A4, q.A5, q.A6, q.A7, q.A8, q.A9, q.A10, q.A11].filter((a) => a != null && String(a).trim() !== "");
        const totalTime = Number(q.time ?? q.TIME ?? 0) || 60;
        const elapsed = Math.max(0, totalTime - Number(timer));
        const numClues = facts.length;
        // First clue as the answers open, then one every (clock ÷ clues) seconds, so the last arrives
        // with a full slice of the clock still to run. Matches scoring, which counts a guess against
        // the clue showing when it was sent (Quizmaster scoreBreadcrumbsQuestion).
        const timePerCard = numClues > 0 ? totalTime / numClues : 0;
        let revealedCount = 0;
        if (numClues === 0) {
          revealedCount = 0;
        } else if (totalTime <= 0 || Number(timer) <= 0) {
          revealedCount = numClues;
        } else {
          revealedCount = Math.min(numClues, 1 + Math.floor(elapsed / timePerCard));
        }
        breadcrumbsCluesRef.current = { facts, revealedCount, questionKey: bedQuestionKey };
        const getCardRotation = (i) => (i % 2 === 0 ? 1 : -1) * (10 + (i * 7 + 11) % 21);
        const stackOffsetPx = 6;
        // Card size from design: 557×763; scale so fanned stack fits in viewport (rotation adds ~card width each side)
        const designCardW = 557, designCardH = 763;
        let cardWidth = designCardW, cardMinHeight = designCardH;
        if (typeof window !== "undefined") {
          const marginH = 160;
          const marginV = 260;
          // Fanned/rotated stack needs ~2× card width; extra margin so full card edges and borders stay visible
          const maxCardW = (window.innerWidth - marginH) / 2;
          const maxCardH = window.innerHeight - marginV;
          const scale = Math.min(maxCardW / designCardW, maxCardH / designCardH, 1);
          cardWidth = Math.round(designCardW * scale);
          cardMinHeight = Math.round(designCardH * scale);
        }
        const pileHeight = revealedCount > 0 ? (revealedCount - 1) * stackOffsetPx + cardMinHeight : 0;
        const overflowPadding = 80;
        const pileWidth = Math.max(cardWidth * 2 + 120, cardWidth + 240);
        return (
          <div
            className="breadcrumbs-clue-area"
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              pointerEvents: "none",
              overflow: "hidden",
              padding: `${overflowPadding}px 100px ${overflowPadding}px 100px`,
              boxSizing: "border-box",
              minHeight: 0,
            }}
          >
            {/* Answer at top, above the cards */}
            {timer === 0 && q.answers && q.answers[0] && (
              <div
                style={{
                  position: "relative",
                  zIndex: 1000,
                  pointerEvents: "auto",
                  marginBottom: "20px",
                  padding: "20px 36px",
                  background: "rgba(34,197,94,0.35)",
                  borderRadius: "14px",
                  border: "3px solid #22c55e",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: "clamp(1.75rem, 5vw, 2.75rem)",
                  textAlign: "center",
                  boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
                  letterSpacing: "0.02em",
                }}
              >
                Answer: {String(q.answers[0]).replace(/^\s*\[(.*?)\]\s*$/, "$1")}
              </div>
            )}
            {/* Card pile: full height so nothing is clipped when scrolling */}
            <div
              style={{
                position: "relative",
                width: pileWidth,
                height: pileHeight + overflowPadding * 2,
                minHeight: pileHeight + overflowPadding * 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                overflow: "visible",
                flexShrink: 0,
              }}
            >
              {facts.slice(0, revealedCount).map((fact, i) => {
                const rotation = getCardRotation(i);
                const topOffset = i * stackOffsetPx;
                const leftOffsetPx = (i % 3) * 12 - 12;
                return (
                  <div
                    key={i}
                    className="breadcrumbs-clue-card"
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: overflowPadding + topOffset,
                      width: cardWidth,
                      minHeight: cardMinHeight,
                      backgroundImage: `url(${BREADCRUMBS_CARD_IMAGE})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                      borderRadius: "14px",
                      boxShadow: "0 8px 28px rgba(0,0,0,0.4)",
                      transform: `translate(calc(-50% + ${leftOffsetPx}px), 0) rotate(${rotation}deg)`,
                      zIndex: i,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: `${Math.round(50 * cardMinHeight / designCardH)}px ${Math.round(76 * cardWidth / designCardW)}px`,
                      boxSizing: "border-box",
                      overflow: "visible",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "clamp(1rem, 3.35vw, 2rem)",
                        color: "#1a1a1a",
                        textAlign: "center",
                        lineHeight: 1.35,
                        fontWeight: 600,
                        textShadow: "0 1px 2px rgba(255,255,255,0.9)",
                        maxWidth: "100%",
                        margin: "0 6px",
                      }}
                    >
                      {String(fact ?? "").trim() || "\u00a0"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()
    )
    : (
      // === YOUR EXISTING glossy list for all other types (unchanged) ===
// === UPDATED glossy list for MULTICHOICE, VANISHINGCHOICE, etc. ===
      (!(
        (normalizeType(q) === "TYPE" || normalizeType(q) === "BREADCRUMBS") && Number(timer) > 0
      )) && (
        <ol
          key={`ans-${animKey}`}
          className={`answer-list host-answer-list-layout ${ // <-- CLASS ADDED
            answersStagger ? "answers-stagger-in" : ""
          }`}
          type="A"
          style={{ 
            /* STYLES REMOVED: maxHeight and gap are now in .host-answer-list-layout */
          }}
        >
{getClampedAnswersOrder(q).map((answerIdx, displayIdx) => {
                          // --- Existing logic to calculate classes, visibility, text ---
                          const qType = String(q?.type || q?.CAT || q?.cat || q?.TYPE || q?.questionType || "").trim().toUpperCase();
                          const isVanishingType = qType === "VANISHINGCHOICE";
                          // For VANISHINGCHOICE: if vanishingIndexes is empty, show all (initial state); otherwise check inclusion
                          const isVisible = !isVanishingType || (vanishingIndexes.length === 0 || vanishingIndexes.includes(answerIdx));
                          const isPoofing = isVanishingType && justVanished && justVanished.includes(answerIdx);
                          const rawAnswer = q.answers[answerIdx];
                          const isMultiLocal = (q.type || "").toUpperCase() === "MULTIANSWER";
                          const isCorrectMulti = isMultiLocal && /^\s*\[.*?\]\s*$/.test(rawAnswer);
                          const isCorrectSingle = !isMultiLocal && answerIdx === 0;
                          const shouldShowCorrect = timer === 0 && (isCorrectSingle || isCorrectMulti);
                          const isGhosted = isVanishingType && !isVisible && !shouldShowCorrect && !isPoofing;
                          let classes = `answer-option option-${String.fromCharCode(65 + displayIdx).toLowerCase()} host-answer-pill`; // <-- CLASS ADDED
                          if (shouldShowCorrect) { classes += " correct pulse-correct"; }
                          if (isGhosted) classes += " ghosted";
                          if (isPoofing) classes += " poof-away";
                          const answerText = isMultiLocal ? String(rawAnswer).replace(/^\s*\[(.*?)\]\s*$/, "$1") : rawAnswer;
                          // --- End existing logic ---

                            return (
                              <li
                                  key={displayIdx}
                                  className="host-answer-item" // <-- CLASS ADDED
                                  style={{
                                      /* ALL STYLES REMOVED: width, maxWidth, display, justifyContent, etc. */
                                      /* are now in .host-answer-item */
                                  }}
                              >
                                  {/* --- The Pill Div --- */}
                              <div
                                      className={classes}
                                      onAnimationStart={displayIdx === 0 ? handleFirstAnswerAnimStart : undefined}
                                      style={{ // Pill Style (Dynamic styles remain)
                                        
                                        /* STYLES REMOVED: width, boxSizing, whiteSpace, overflow, */
                                        /* padding, margin, minHeight, fonts, textShadow, flex, etc. */
                                        /* are now in .host-answer-pill */

                                        // --- DYNAMIC STYLES (STAY) ---
                                        fontSize: computeAnswerFontSize(answerText),

                                        // TRUE/FALSE colouring stays intact
                                        ...(normalizeType(q) === "TRUE/FALSE" &&
                                          String(rawAnswer).toUpperCase().includes("TRUE") && {
                                            background: "linear-gradient(180deg, #22c55e, #15803d)"
                                          }),
                                        ...(normalizeType(q) === "TRUE/FALSE" &&
                                          String(rawAnswer).toUpperCase().includes("FALSE") && {
                                            background: "linear-gradient(180deg, #dc2626, #7f1d1d)"
                                          }),
                                        // --- END DYNAMIC ---
                                      }}
                                  >
                                    {answerText}
                                  </div>
                                  {/* --- End Pill Div --- */}
                              </li>
                          );
                        })}



        </ol>
      )
    )
)}

              </div>

{p1Url && (
      // Trigger fade-out immediately when timer reaches 0
      <div className={`image-container ${timer === 0 ? "fade-out" : ""}`}> {/* Wrap GlassImageCard */}
        <GlassImageCard
          src={p1Url}
          alt="Quiz Visual"
          style={{ // <<<--- MODIFIED STYLE OBJECT
            width: "clamp(280px, 45vw, 800px)", // Smaller clamp width (was 60vw, 1000px)
            maxWidth: "50vw",                  // Smaller max width (was 70vw)
            maxHeight: "65vh",                 // Smaller max height (was 72vh)
            marginTop: "2vh", // Slightly less top margin
            marginRight: "2vw",
            flexShrink: 1 // Keep allowing shrinking
          }}
          // JIGSAW column decides (off by default); a jigsaw picture holds still under its tiles.
          jigsawProps={
            isJigsawOn(q)
              ? {
                  src: p1Url,
                  timer: timer,
                  totalTime: q.time || 60,
                  rows: 6,
                  cols: 8
                }
              : null
          }
          kenBurns={!isJigsawOn(q)}
        />
      </div>
    )}



            </div>
          )}





          

{((q.type || q.TYPE || "").toUpperCase() !== "ENDROUND") && videoUrl && (
  <video
    controls
    width="320"
    src={videoUrl}
    style={{ marginTop: 20, borderRadius: "12px", boxShadow: "0 2px 12px rgba(0,0,0,0.5)" }}
  />
)}

        </>
      )}

      {/* Audio playback - MP3 files use audio element, Spotify uses preview URL */}
      {(() => {
        const soundUrl = q.SOUND || q.SOUND_URL || q.sound;
        const spotifyUrl = q.SPOTIFY_URL;
        const isSpotify = spotifyUrl && spotifyUrl.includes('open.spotify.com/embed');
        const hasAudio = soundUrl || spotifyUrl;
        
        audioLog('log', '[Audio Playback] Checking audio sources:', {
          current,
          hasAudio,
          soundUrl: soundUrl ? soundUrl.substring(0, 50) + '...' : null,
          spotifyUrl: spotifyUrl ? spotifyUrl.substring(0, 50) + '...' : null,
          isSpotify,
          spotifyPreviewUrl: spotifyPreviewUrl ? spotifyPreviewUrl.substring(0, 50) + '...' : null,
          q_SOUND: q.SOUND,
          q_SOUND_URL: q.SOUND_URL,
          q_SPOTIFY_URL: q.SPOTIFY_URL,
          q_sound: q.sound
        });
        
        if (!hasAudio) {
          audioLog('log', '[Audio Playback] No audio sources found, returning null');
          return null;
        }
        
        // If we have Spotify preview URL and no MP3, use audio element for preview
        if (spotifyPreviewUrl && !soundUrl) {
          audioLog('log', '[Audio Playback] Using Spotify preview URL in audio element:', spotifyPreviewUrl);
          return (
            <div style={{
              position: "fixed", left: "11vw", bottom: "2.8vh", transform: "translateX(-50%)", zIndex: 2000,
              background: "rgba(24,28,40,0.68)", padding: "0.7em 2.3em", borderRadius: "2.8em",
              boxShadow: "0 2px 18px #0008", display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <audio
                ref={audioRef}
                src={spotifyPreviewUrl}
                controls
                autoPlay
                playsInline
                style={{ display: "none" }}
                onLoadStart={() => {
                  audioLog('log', '[Audio Playback] Spotify preview load started');
                }}
                onCanPlay={() => {
                  audioLog('log', '[Audio Playback] Spotify preview can play');
                }}
                onPlay={() => {
                  audioLog('log', '[Audio Playback] Spotify preview playing');
                }}
                onError={(e) => {
                  audioLog('error', '[Audio Playback] Spotify preview error:', e);
                }}
              />
            </div>
          );
        }
        
        // Fallback: If we have Spotify URL but no preview, show iframe
        if (isSpotify && !soundUrl && !spotifyPreviewUrl) {
          audioLog('log', '[Audio Playback] No preview available, showing iframe (requires user interaction)');
          return (
            <div style={{
              position: "fixed", left: "11vw", bottom: "2.8vh", transform: "translateX(-50%)", zIndex: 2000,
              background: "rgba(24,28,40,0.68)", padding: "0.7em 2.3em", borderRadius: "2.8em",
              boxShadow: "0 2px 18px #0008", display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <iframe
                src={spotifyUrl}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                style={{
                  borderRadius: 12,
                  width: "100%",
                  minWidth: "300px",
                  maxWidth: "600px",
                  display: "block"
                }}
                onLoad={() => {
                  audioLog('log', '[Audio Playback] Spotify iframe loaded (user must click to play)');
                }}
              />
            </div>
          );
        }
        
        // Otherwise use audio element for MP3 (MP3 takes priority)
        if (soundUrl) {
          audioLog('log', '[Audio Playback] Using MP3 audio element:', soundUrl);
          return (
            <div style={{
              position: "fixed", left: "11vw", bottom: "2.8vh", transform: "translateX(-50%)", zIndex: 2000,
              background: "rgba(24,28,40,0.68)", padding: "0.7em 2.3em", borderRadius: "2.8em",
              boxShadow: "0 2px 18px #0008", display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <audio
                ref={audioRef}
                src={soundUrl}
                controls
                playsInline
                //style={{ width: "290px", maxWidth: "74vw", background: "none" }}
                style={{ display: "none" }}
                onLoadStart={() => {
                  audioLog('log', '[Audio Playback] MP3 audio load started');
                }}
                onCanPlay={() => {
                  audioLog('log', '[Audio Playback] MP3 audio can play');
                }}
                onPlay={() => {
                  audioLog('log', '[Audio Playback] MP3 audio playing');
                }}
                onError={(e) => {
                  audioLog('error', '[Audio Playback] MP3 audio error:', e);
                }}
              />
            </div>
          );
        }
        
        audioLog('log', '[Audio Playback] No matching audio source, returning null');
        return null;
      })()}


<audio ref={endPingRef} src="/EndQuestionPing.wav" preload="auto" style={{ display: "none" }} />

<audio
  ref={wheelWhooshRef}
  src="/sfx/sucking-whoosh.wav"
  preload="auto"
  style={{ display: "none" }}
/>

<audio
  ref={vanishSfxRef}
  src="/sfx/sucking-whoosh.wav"
  preload="auto"
  style={{ display: "none" }}
/>


<audio
  ref={bgBedRef}
  src="/sfx/396595_valo_0-120.mp3"
  preload="auto"
  loop
  playsInline
  data-bed="1"            // <-- mark as bed
  style={{ display: "none" }}
/>


<BackgroundBed
  src="/sfx/396595_valo_0-120.mp3"
  current={current}
  timer={timer}
  volume={bedVolume}
/>


{/* 10s cue overlay (video) */}
{showTenOverlay && (
  <TenSecondCueOverlay
    src={tenOverlaySrc}
    visible={showTenOverlay}
    onHidden={handleTenOverlayHidden}
  />
)}


{/* Quiz Introduction overlay (video via Ollie, blocking) */}
{showIntroOverlay && (
  <TenSecondCueOverlay
    src={introOverlaySrc}
    visible={showIntroOverlay}
    onHidden={handleQuizIntroHidden}
    fallbackLine={null}
  />
)}


{/* Multiple Choice intro overlay (video via Ollie) */}
{showMcqOverlay && (
  <TenSecondCueOverlay
    src={mcqOverlaySrc}
    visible={showMcqOverlay}
    onHidden={handleMcqOverlayHidden}
    fallbackLine={null}
  />
)}

{/* Other Ollie clips (end of round, …) — see playOllieVideo */}
{ollieOverlaySrc && (
  <TenSecondCueOverlay
    src={ollieOverlaySrc}
    visible={!!ollieOverlaySrc}
    onHidden={handleOllieOverlayHidden}
    fallbackLine={null}
  />
)}

{wheelTransition.active && !showIntroOverlay && !showMcqOverlay && !showTenOverlay && (
  <QuestionTypeWheelTransition
    visible={wheelTransition.active}
    targetType={wheelTransition.type}
    spinMs={wheelTransitionMs}
  />
)}

{/* Bottom-right auto-start countdown */}
<AutoStartCountdown gameCode={gameCode} current={current} />



{q.time > 0 && !isDisplay && timerActive && (
  <>
    <ThermoTimerBar q={q} timer={timer} questionNumber={displayQuestionNumber} />
  </>
)}

{/* Visible Explanation Marquee */}
{!isDisplay && (
  <VisibleExplanationMarquee visibleExplanation={q?.visibleExplanation} timer={timer} />
)}



{showQuizOptions && (
  <OptionsPanel onClose={() => setShowQuizOptions(false)} gameCode={gameCode} joinCode={joinCode} />


  


)}


{gameCode && (
            <TeamRoundChoiceGate
              gameCode={gameCode}
              current={current}
              questions={questions}
              advance={advance}
              venue={
                typeof localStorage !== "undefined"
                  ? (localStorage.getItem("opt_venue") || "").trim() || "Unknown Venue"
                  : "Unknown Venue"
              }
            />
          )}

{/* ===>>> ADD ROUND SCORES POPUP RENDERING <<<=== */}
          {showRoundScoresPopup && (
            <RoundScores
              gameCode={gameCode}
              roundQuestionStartIdx={roundQuestionStartIdx}
              questionIdx={current}
              onClose={async () => {
                // The scores stay up until Ollie has finished talking about them.
                const endIdx = current;
                await waitForOllieSpeech(30000, () => window.__ollieCurrent !== endIdx);
                if (window.__ollieCurrent !== endIdx) return;
                setShowRoundScoresPopup(false);

                const settings = getTeamRoundChoiceSettingsFromLocalStorage();
                if (gameCode && settings.enabled) {
                  try {
                    const [tSnap, aSnap] = await Promise.all([
                      getDocs(collection(db, "games", gameCode, "teams")),
                      getDocs(collection(db, "games", gameCode, "answers")),
                    ]);
                    const teamNames = [];
                    tSnap.forEach((d) => teamNames.push(d.id));
                    const answersList = [];
                    aSnap.forEach((d) => answersList.push(d.data()));
                    await scheduleTeamRoundAfterEndRound({
                      gameCode,
                      questions,
                      endRoundIdx: current,
                      answersList,
                      teamNames,
                      settings,
                    });
                  } catch (e) {
                    console.warn("scheduleTeamRoundAfterEndRound", e);
                  }
                }

                let advanced = false;
                const checkAndAdvance = () => {
                  if (advanced) return;
                  if (window.__mediaBusy || ollieSpeechBusy()) {
                    setTimeout(checkAndAdvance, 100);
                  } else {
                    try {
                      if (typeof advance === "function") {
                        const nextStep = (() => {
                          const total = Array.isArray(questions) ? questions.length : 0;
                          let step = 1;
                          for (let idx = current + 1; idx < total; idx += 1) {
                            const next = questions[idx] || {};
                            const nextType = String(next.type || next.CAT || "").toUpperCase();
                            if (nextType === "STARTROUND") {
                              step = idx - current;
                              break;
                            }
                            const isDisplayLike = ["DISPLAY","SLIDE","DEFAULT","ENDROUND","SHOWROUNDS","JOIN QUIZ","INTRODUCTION"].includes(nextType);
                            if (!isDisplayLike) {
                              step = idx - current;
                              break;
                            }
                          }
                          return Math.max(1, step);
                        })();

                        advance(nextStep, false);
                        advanced = true;
                      } else {
                        console.error("QuizScreen checkAndAdvance: 'advance' prop is not a function!");
                      }
                    } catch (e) {
                      console.error("QuizScreen checkAndAdvance: Error calling advance function!", e);
                    }
                  }
                };

                setTimeout(checkAndAdvance, 50);
              }}
            />
          )}
          {/* ===>>> END ROUND SCORES POPUP <<<=== */}



{/* Rules card overlays question view; shows between 3s and 7s after a new question (non-display) */}
{!isDisplay && (
<RulesSlideInCard
  q={q}
  lines={getRulesForQuestion(q)}
  visible={rulesCardVisible}
  questionNumber={displayQuestionNumber}   
/>
)}



      {countdownOverlay && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 5000, pointerEvents: "none",
        }}>
          <div style={{
            fontSize: "min(24vw, 36vh)", fontWeight: 900, color: "#fff",
            background: "transparent", textAlign: "center",
            fontFamily: "'Arial Black', Impact, Arial, sans-serif",
            animation: "scaleFade 1.4s ease-out forwards",
            textShadow: "0 0 10px #f33, 0 0 30px #f00, 0 0 60px #f00"
          }}>
            {countdownOverlay}
          </div>
        </div>
      )}

{/* Auto-advance footer */}
{(() => {
  // Base badge style
const base = {
    // position: "fixed", // <-- REMOVE
    // right: "1.6vw",    // <-- REMOVE
    // bottom: "1.6vh",   // <-- REMOVE
    zIndex: 10060, // Keep zIndex if needed relative to siblings, though likely redundant now
    fontFamily: "'Arial Black', Impact, Arial, sans-serif",
    fontWeight: 900,
    fontSize: "clamp(12px, 1.1vw, 16px)",
    color: "#e9eeff",
    background: "rgba(12,16,28,0.78)",
    border: "1px solid rgba(120,150,255,.28)",
    borderRadius: 14,
    padding: "8px 12px",
    textShadow: "0 1px 6px #000a",
    boxShadow: "0 10px 24px rgba(0,0,0,.45)",
    whiteSpace: 'nowrap' // Prevent wrapping within a badge
  };

// New container style for the footer badges
  const footerContainerStyle = {
    position: "fixed",
    right: "1.6vw",
    bottom: "1.6vh",
    zIndex: 10060,
    display: "flex",
    alignItems: "center",
    gap: "12px", // Space between badges
  };


  // Simple pause detection without new hooks:
  // - localStorage opt_autoplay flipped to "No" by pressing 'P'
  // - also respect in-scope flags if they exist
const paused =
    (() => {
      try {
        return (localStorage.getItem("opt_autoplay") || "Yes").toUpperCase() !== "YES";
      } catch {
        return false;
      }
    })() ||
    (typeof autoPaused !== "undefined" && !!autoPaused) ||
    (typeof autoPlayOn !== "undefined" && !autoPlayOn);

  // Get team count from the array we already have
  const teamCount = teamsArray ? teamsArray.length : 0;
  const showTeamCount = current === 0; // Only show in lobby

  // Build the status badge
  let statusBadge = null;
  if (paused) {
    statusBadge = (
      <div
        style={{
          ...base,
          background: "linear-gradient(180deg, rgba(120,30,30,.92), rgba(80,10,10,.96))",
          border: "1px solid rgba(255,120,120,.65)"
        }}
      >
        Auto-Advance Paused
      </div>
    );
  } else if (status) {
    if (status.mode === "wait") {
      const label =
        status.reason === "video" ? "after video" :
        status.reason === "audio" ? "after audio" :
        status.reason === "tts"   ? "after TTS"   : "…";
      statusBadge = <div style={base}>Waiting for {label}…</div>;
    } else if (status.mode === "countdown") {
      if (status.reason === "start") {
        statusBadge = <div style={base}>Start in {fmtHMS(status.seconds)}</div>;
      } else {
        statusBadge = <div style={base}>Auto-advance in {status.seconds}s</div>;
      }
    }
  }

  // If there's nothing to show, return null
  if (!showTeamCount && !statusBadge) {
    return null;
  }

  // Render the container with both badges
  return (
    <div style={footerContainerStyle}>
      {showTeamCount && (
        <div style={base}>
          Teams Joined: {teamCount}
        </div>
      )}
      {statusBadge}
    </div>
  );
})()}





    </div>
  );
}